import { useState, useEffect, useCallback, useMemo, useRef } from "react"
import { motion } from "framer-motion"
import { Plus, Minus, FileText, ChevronDown } from "lucide-react"
import axios from "axios"
import { ToastContainer, toast } from "react-toastify"
import "react-toastify/dist/ReactToastify.css"
import ValidationErrorSidebar from '../ui/ValidationErrorSidebar'
import { validationPatterns as centralizedPatterns } from '../../utils/validationPatterns';
import { usePageSave } from '../../contexts/PageSaveContext'
import { useServerPageStatus } from '../../contexts/ServerPageStatusContext';

// Add config if not already imported
// Assuming config is available at this path or adjust as needed
import config from "../../config"

const formatCurrency = (value) => {
  return new Intl.NumberFormat("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value)
}

// Helper to create default deduction data matching API structure
const createDefaultDeductionData = () => ({
  deductionId: null,
  totalDeduction: 0,
  deduction80Cs: [],
  deduction80CCFamilies: [],
  deduction80D: {
    deduction80DId: null,
    seniorCitizenFlagSelfFamily: "",
    preventiveHealthCheckUpSelfFamilyAmount: 0,
    medicalExpSelfFamilySeniorCitizen: 0,
    eligibleDeduction: 0,
    deductionSeniorCitizenSelfDetails: [],
    deductionParentsHealth: {
      deductionParentsHealthId: null,
      parentsSeniorCitizenFlag: "",
      healthInsuranceAmount: 0,
      preventiveHealthCheckUpSelfFamilyAmount: 0,
      medicalExpenditureSelfFamilySeniorCitizen: 0,
      eligibleDeduction: 0,
      deductionParentSeniorCitizenDetails: [],
    },
  },
  disablementDeductions: [],
  deduction80DDBs: [],
  deductionLoans: [],
  deductionDonations: [],
  deduction80GGs: [],
  deductionDepositInterests: [],
  deduction80GGCs: [],
  deductionUndChapVIAType: {
    deductionUndChapViaTypeId: null,
    section80C: 0,
    section80CCC: 0,
    section80CCDEmployeeOrSE: 0,
    section80CCD1B: 0,
    section80CCDEmployer: 0,
    section80D: 0,
    section80DD: 0,
    section80DDB: 0,
    section80E: 0,
    section80EE: 0,
    section80EEA: 0,
    section80EEB: 0,
    section80G: 0,
    section80GG: 0,
    section80GGA: 0,
    section80GGC: 0,
    section80U: 0,
    section80TTA: 0,
    section80TTB: 0,
    anyOthSec80CCH: 0,
    totalChapVIADeductions: 0,
  },
  deductionUsrUndChapVIAType: {
    deductionUserUndChapViaTypeId: null,
    section80C: 0,
    section80CCC: 0,
    section80CCDEmployeeOrSE: 0,
    section80CCD1B: 0,
    section80CCDEmployer: 0,
    pranNum: "",
    section80D: 0,
    section80DD: 0,
    section80DDBUsrType: 0,
    nameOfSpecDisease80DDB: "",
    section80DDB: 0,
    section80E: 0,
    section80EE: 0,
    section80EEA: 0,
    section80EEB: 0,
    section80G: 0,
    section80GG: 0,
    form10BAAckNum: 0,
    section80GGA: 0,
    section80GGC: 0,
    section80U: 0,
    section80TTA: 0,
    section80TTB: 0,
    anyOthSec80CCH: 0,
    totalChapVIADeductions: 0,
  },
})

const deductionTypes = [
  // Common Deductions
  {
    id: "80C",
    name: "80C",
    category: "Common Deductions",
    maxLimit: 150000,
    description:
      "Deduction in respect of life insurance premia, deferred annuity, contributions to provident fund etc.",
    apiField: "deduction80Cs",
    defaultEntry: { deduction80cId: null, amount: 0, identificationNo: "", eligibleDeduction: 0, nature: "" },
  },
  {
    id: "80CCC",
    name: "80CCC",
    category: "Common Deductions",
    maxLimit: 150000,
    description: "Payment in respect Pension Fund, etc.",
    apiField: "deduction80CCFamilies",
    sectionCode: "80CCC",
    defaultEntry: { deduction80CCFamilyId: null, section: "80CCC", pranNumber: "", amount: 0, eligibleDeduction: 0 },
  },
  {
    id: "80CCD1",
    name: "80CCD(1)",
    category: "Common Deductions",
    maxLimit: 150000,
    description: "Contribution to pension scheme of Central Government",
    apiField: "deduction80CCFamilies",
    sectionCode: "80CCD1",
    defaultEntry: { deduction80CCFamilyId: null, section: "80CCD1", pranNumber: "", amount: 0, eligibleDeduction: 0 },
  },
  {
    id: "80CCD1B",
    name: "80CCD(1B)",
    category: "Common Deductions",
    maxLimit: 50000,
    description: "Contribution to pension scheme of Central Government",
    apiField: "deduction80CCFamilies",
    sectionCode: "80CCD1B",
    defaultEntry: { deduction80CCFamilyId: null, section: "80CCD1B", pranNumber: "", amount: 0, eligibleDeduction: 0 },
  },
  {
    id: "80CCD2",
    name: "80CCD(2)",
    category: "Common Deductions",
    maxLimit: null,
    description: "Contribution to pension scheme of Central Government by employer",
    apiField: "deduction80CCFamilies",
    sectionCode: "80CCD2",
    defaultEntry: { deduction80CCFamilyId: null, section: "80CCD2", pranNumber: "", amount: 0, eligibleDeduction: 0 },
  },
  {
    id: "80TTA",
    name: "80TTA",
    category: "Common Deductions",
    maxLimit: 10000,
    description: "Interest on savings bank account",
    apiField: "deductionDepositInterests",
    sectionCode: "80TTA",
    defaultEntry: {
      deductionDepositInterestId: null,
      sectionOfInterest: "80TTA",
      interestPaid: 0,
      eligibleDeduction: 0,
    },
  },
  {
    id: "80TTB",
    name: "80TTB",
    category: "Common Deductions",
    maxLimit: 50000,
    description: "Interest on deposits in case of Resident senior citizens",
    apiField: "deductionDepositInterests",
    sectionCode: "80TTB",
    defaultEntry: {
      deductionDepositInterestId: null,
      sectionOfInterest: "80TTB",
      interestPaid: 0,
      eligibleDeduction: 0,
    },
  },
  {
    id: "80GG",
    name: "80GG",
    category: "Common Deductions",
    maxLimit: 60000,
    description: "Deduction in respect of Rent paid",
    apiField: "deduction80GGs",
    defaultEntry: { deduction80GGId: null, acknowledgement10BA: "", amountOfRentPaid: 0, eligibleDeduction: 0 },
  },
  // Medical Deduction
  {
    id: "80D",
    name: "80D",
    category: "Medical Deduction",
    maxLimit: 100000,
    description: "Deduction in respect of Health Insurance premia",
    apiField: "deduction80D",
  },
  {
    id: "80DDB",
    name: "80DDB",
    category: "Medical Deduction",
    maxLimit: 100000,
    description: "Medical treatment of specified disease",
    apiField: "deduction80DDBs",
    defaultEntry: {
      deduction80DDBId: null,
      userType: "",
      nameOfSpecifiedDisease: "",
      expenditureIncurred: 0,
      eligibleDeduction: 0,
    },
  },
  // Loan Deduction
  {
    id: "80E",
    name: "80E",
    category: "Loan Deduction",
    maxLimit: null,
    description: "Interest on loan taken for higher education",
    apiField: "deductionLoans",
    sectionCode: "80E",
    defaultEntry: {
      deductionLoanId: null,
      sectionOfLoan: "80E",
      stampValue: 0,
      vehicleRegistrationNo: "",
      loanTakenFrom: "",
      bankInstitutionName: "",
      accountNo: "",
      dateOfLoan: "",
      totalLoan: 0,
      loanOutStanding: 0,
      interestPayableForTheYear: 0,
      eligibleDeduction: 0,
    },
  },
  {
    id: "80EE",
    name: "80EE",
    category: "Loan Deduction",
    maxLimit: 50000,
    description: "Interest on loan taken for residential house property",
    apiField: "deductionLoans",
    sectionCode: "80EE",
    defaultEntry: {
      deductionLoanId: null,
      sectionOfLoan: "80EE",
      stampValue: 0,
      vehicleRegistrationNo: "",
      loanTakenFrom: "",
      bankInstitutionName: "",
      accountNo: "",
      dateOfLoan: "",
      totalLoan: 0,
      loanOutStanding: 0,
      interestPayableForTheYear: 0,
      eligibleDeduction: 0,
    },
  },
  {
    id: "80EEA",
    name: "80EEA",
    category: "Loan Deduction",
    maxLimit: 150000,
    description: "Deduction in respect of interest on loan taken for certain house property",
    apiField: "deductionLoans",
    sectionCode: "80EEA",
    defaultEntry: {
      deductionLoanId: null,
      sectionOfLoan: "80EEA",
      stampValue: 0,
      vehicleRegistrationNo: "",
      loanTakenFrom: "",
      bankInstitutionName: "",
      accountNo: "",
      dateOfLoan: "",
      totalLoan: 0,
      loanOutStanding: 0,
      interestPayableForTheYear: 0,
      eligibleDeduction: 0,
    },
  },
  {
    id: "80EEB",
    name: "80EEB",
    category: "Loan Deduction",
    maxLimit: 150000,
    description: "Deduction in respect of purchase of electric vehicle",
    apiField: "deductionLoans",
    sectionCode: "80EEB",
    defaultEntry: {
      deductionLoanId: null,
      sectionOfLoan: "80EEB",
      stampValue: 0,
      vehicleRegistrationNo: "",
      loanTakenFrom: "",
      bankInstitutionName: "",
      accountNo: "",
      dateOfLoan: "",
      totalLoan: 0,
      loanOutStanding: 0,
      interestPayableForTheYear: 0,
      eligibleDeduction: 0,
    },
  },
  // Disablement Deduction
  {
    id: "80DD",
    name: "80DD",
    category: "Disablement Deduction",
    maxLimit: 125000,
    description:
      "Details of deduction in respect of maintenance including medical treatment of a dependent who is a person with disability.",
    apiField: "disablementDeductions",
    sectionCode: "80DD",
    defaultEntry: {
      disablementDeductionId: null,
      section: "80DD",
      natureOfDisability: "",
      typeOfDisability: "",
      deductionAmount: 0,
      dependentType: "",
      dependentPan: "",
      dependentAadhaar: "",
      udidNo: "",
      form10AAckNum: ""
    },
  },
  {
    id: "80U",
    name: "80U",
    category: "Disablement Deduction",
    maxLimit: 125000,
    description: "Details of deduction in case of a person with disability.",
    apiField: "disablementDeductions",
    sectionCode: "80U",
    defaultEntry: {
      disablementDeductionId: null,
      section: "80U",
      natureOfDisability: "",
      typeOfDisability: "",
      deductionAmount: 0,
      dependentType: "",
      dependentPan: "",
      dependentAadhaar: "",
      udidNo: "",
      form10AAckNum: ""
    },
  },
  // Donation Deduction
  {
    id: "80G",
    name: "80G",
    category: "80G Donation",
    maxLimit: null,
    description: "Donations to certain funds, charitable institutions, etc",
    apiField: "deductionDonations",
    sectionCode: "80G",
    defaultEntry: {
      deductionDonationId: null,
      sectionOfDonation: "80G",
      donationType: "",
      panOfDonee: "",
      nameOfDonee: "",
      addressOfDonee: "",
      cityTownDistrict: "",
      stateCode: "",
      pinCode: "",
      donationInCash: 0,
      donationInOtherMode: 0,
      totalDonation: 0,
      eligibleDeduction: 0,
    },
  },
  {
    id: "80GGA",
    name: "80GGA",
    category: "Donation Deduction",
    maxLimit: null,
    description: "Certain donations for scientific research or rural development",
    apiField: "deductionDonations",
    sectionCode: "80GGA",
    defaultEntry: {
      deductionDonationId: null,
      sectionOfDonation: "80GGA",
      donationType: "",
      panOfDonee: "",
      nameOfDonee: "",
      addressOfDonee: "",
      cityTownDistrict: "",
      stateCode: "",
      pinCode: "",
      donationInCash: 0,
      donationInOtherMode: 0,
      totalDonation: 0,
      eligibleDeduction: 0,
    },
  },
  {
    id: "80GGC",
    name: "80GGC",
    category: "Donation Deduction",
    maxLimit: null,
    description: "Donation to Political party",
    apiField: "deduction80GGCs",
    defaultEntry: {
      Deduction80GGCId: null,
      DonationDate: "",
      DonationAmtCash: 0,
      DonationAmtOtherMode: 0,
      TransactionRefNum: "",
      IfscCode: "",
      DonationAmt: 0, // This will be calculated from cash + other mode
      EligibleDonationAmt: 0,
    },
  },
  // Others
  {
    id: "80QQB",
    name: "80QQB",
    category: "Others",
    maxLimit: null,
    description: "Royalty Income of author of certain books",
    apiField: "deductionDepositInterests",
    sectionCode: "80QQB",
    defaultEntry: {
      deductionDepositInterestId: null,
      sectionOfInterest: "80QQB",
      interestPaid: 0,
      eligibleDeduction: 0,
    },
  },
  {
    id: "80RRB",
    name: "80RRB",
    category: "Others",
    maxLimit: null,
    description: "Royalty on Patents",
    apiField: "deductionDepositInterests",
    sectionCode: "80RRB",
    defaultEntry: {
      deductionDepositInterestId: null,
      sectionOfInterest: "80RRB",
      interestPaid: 0,
      eligibleDeduction: 0,
    },
  },
  {
    id: "80CCH",
    name: "80CCH",
    category: "Others",
    maxLimit: null,
    description: "Contribution to Agnipath Scheme",
    apiField: "deductionDepositInterests",
    sectionCode: "80CCH",
    defaultEntry: {
      deductionDepositInterestId: null,
      sectionOfInterest: "80CCH",
      interestPaid: 0,
      eligibleDeduction: 0,
    },
  },
]

  const categories = [
    "Common Deductions",
    "Medical Deduction",
    "Loan Deduction",
    "Disablement Deduction",
    "Donation Deduction",
    "80G Donation",
  ]

const TaxDeductionForms = ({ onDeductionDataChange }) => {
  const { markPageAsSaved, markPageAsUnsaved } = usePageSave()
  const { refreshAllPageStatuses } = useServerPageStatus()
  const [activeTab, setActiveTab] = useState("80C")
  const [activeTabDescription, setActiveTabDescription] = useState(
    deductionTypes.find((d) => d.id === "80C").description,
  )
  const [activeCategory, setActiveCategory] = useState("Common Deductions")
  const [deductionData, setDeductionData] = useState(createDefaultDeductionData())
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isLoading, setIsLoading] = useState(true)
  const [isCalculating, setIsCalculating] = useState(false)
  const [calculationCompleted, setCalculationCompleted] = useState(false)
  const [currentCalculationTab, setCurrentCalculationTab] = useState(0)

  // 80D specific states
  const [seniorCitizenFlagSelfFamily, setSeniorCitizenFlagSelfFamily] = useState("")
  const [medicalExpSelfFamily, setMedicalExpSelfFamily] = useState(0)
  const [preventiveHealthCheckUpSelfFamily, setPreventiveHealthCheckUpSelfFamily] = useState(0)
  const [forSelfEntries, setForSelfEntries] = useState([
    { id: Date.now(), seniorCitizenSelfDetailId: null, company: "", policyNo: "", amount: 0 },
  ])
  const [parentsSeniorCitizenFlag, setParentsSeniorCitizenFlag] = useState("")
  const [medicalExpenditureParents, setMedicalExpenditureParents] = useState(0)
  const [preventiveHealthCheckUpParents, setPreventiveHealthCheckUpParents] = useState(0)
  const [forParentEntries, setForParentEntries] = useState([
    { id: Date.now(), parentSeniorCitizenDetailId: null, company: "", policyNo: "", amount: 0 },
  ])
  const [medicalExpenseError, setMedicalExpenseError] = useState('');
  const [parentsMedicalExpenseError, setParentsMedicalExpenseError] = useState('');

  const [total80CFamilyState, setTotal80CFamily] = useState(0);
  const [dob, setDob] = useState('1970-01-01'); // Format: 'YYYY-MM-DD'

  // Helper function to add local IDs to API data
  const addLocalIdsToArray = (apiArray) => {
    if (!Array.isArray(apiArray)) return []
    return apiArray.map((item, index) => ({
      ...item,
      id: item.id || `api_${Date.now()}_${index}`, // Ensure unique ID
    }))
  }

  const [specifiedDiseasesList, setSpecifiedDeiseasesList] = useState([])
  const [loanTakenList, setTokenList] = useState([])
  const [disabilityNatureList, setNatureOfDisabilityList] = useState([])
  const [typeOfDisabilityList, setTypeOfDisabilityList] = useState([])
  const [typeOfDependentList, setTypeOfDependent] = useState([])
  const [donationTypeList, setDonationTypeList] = useState([])
  const [statesList, setStatesList] = useState([])
  const [grossTotalIncome, setGrossTotalIncome] = useState(0);
  const [oldRegimeData, setOldRegimeData] = useState()
  const [validationErrors, setValidationErrors] = useState({})
  
  // Add render counter to detect infinite loops (after all hooks are declared)
  const renderCount = useRef(0)
  renderCount.current += 1
  
  // Safety check for infinite loops (log warning instead of early return)
  if (renderCount.current > 100) {
    console.error('Infinite loop detected in TaxDeductionForms! Render count:', renderCount.current)
    // Don't return early to avoid breaking hooks rules
  }

  console.log("oldRegimeData", oldRegimeData)

  // Helper function moved to after total80G is defined to fix hoisting issue



  const getAllCodeList = async (codeType, setDataFn) => {
    try {
      const response = await axios.get(`${config.apiUrl}/system/code/${codeType}`);
      const data = response.data;

      if (data && data.codes?.length > 0) {
        setDataFn(data.codes);
      } else {
        setDataFn([]); // empty array if no data
      }
    } catch (error) {
      console.log(`Error fetching ${codeType}:`, error);
      toast.error(`Failed to fetch ${codeType}.`);
    }
  };

  useEffect(() => {
    getAllCodeList("NameOfSpecDisease80DDB", setSpecifiedDeiseasesList);
    getAllCodeList("LoanTknFrom", setTokenList);
    getAllCodeList("NatureOfDisability", setNatureOfDisabilityList);
    getAllCodeList("TypeOfDisability", setTypeOfDisabilityList);
    getAllCodeList("DependentType", setTypeOfDependent);
    getAllCodeList("Deduction80GGAType", setDonationTypeList);
    getAllCodeList("StateCode", setStatesList);
  }, [])

  // Removed EightyGEligibleUpdater component as it was causing infinite loops
  // Optimize API calls by combining them and adding proper loading states
  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        const panNo = localStorage.getItem("PAN_NO")
        const year = localStorage.getItem("YEAR")
        if (panNo && year) {
          // Fetch tax computation data
          const oldRes = await axios.get(`${config.apiUrl}/user/tax-computation/${panNo}/${year}/OLD`)
          setOldRegimeData(oldRes.data?.taxComputation)
          setGrossTotalIncome(oldRes.data?.taxComputation?.totalTaxableIncome)
          
          const tc = oldRes.data?.taxComputation || {}
          const cap = Number(tc.incomeFromSalary || 0) + Number(tc.incomeFromHP || 0) + Number(tc.incomeFromOtherSources || 0)
          setDeductionData(prev => ({ ...prev, totalTaxableIncomeCap: cap }))
        }
      } catch (error) {
        console.error("Error fetching tax computation:", error)
        toast.error("Failed to load tax computation data")
      }
    }

    fetchInitialData()
  }, [])

  // Define calculateTotalEligibleDeduction function before its usage
  const calculateTotalEligibleDeduction = (data) => {
    let total = 0;

    // Calculate 80C family deductions
    const section80CFamily = [
      ...(data.deduction80Cs || []),
      ...(data.deduction80CCFamilies || []).filter(entry =>
        ['80CCC', '80CCD1', '80CCD1B'].includes(entry.section)
      )
    ];
    const total80CFamily = section80CFamily.reduce((sum, entry) => sum + (entry.amount || 0), 0);
    total += Math.min(total80CFamily, 150000); // 80C family limit

    // Calculate 80CCD(2) separately
    const total80CCD2 = (data.deduction80CCFamilies || [])
      .filter(entry => entry.section === '80CCD2')
      .reduce((sum, entry) => sum + (entry.amount || 0), 0);
    total += total80CCD2; // No limit for 80CCD(2)

    // Calculate 80D
    if (data.deduction80D) {
      const selfLimit = data.deduction80D.seniorCitizenFlagSelfFamily === 'Y' ? 50000 : 25000;
      const parentsLimit = data.deduction80D.deductionParentsHealth?.parentsSeniorCitizenFlag === 'Y' ? 50000 : 25000;

      const selfTotal = Math.min(
        (data.deduction80D.preventiveHealthCheckUpSelfFamilyAmount || 0),
        5000
      ) + (
          (data.deduction80D.medicalExpSelfFamilySeniorCitizen || 0) > 0
            ? data.deduction80D.medicalExpSelfFamilySeniorCitizen
            : (data.deduction80D.deductionSeniorCitizenSelfDetails || []).reduce((sum, entry) => sum + (entry.healthInsAmt || 0), 0)
        );

      const parentsTotal = Math.min(
        (data.deduction80D.deductionParentsHealth?.preventiveHealthCheckUpSelfFamilyAmount || 0),
        5000
      ) + (
          (data.deduction80D.deductionParentsHealth?.medicalExpenditureSelfFamilySeniorCitizen || 0) > 0
            ? data.deduction80D.deductionParentsHealth.medicalExpenditureSelfFamilySeniorCitizen
            : (data.deduction80D.deductionParentsHealth?.deductionParentSeniorCitizenDetails || []).reduce((sum, entry) => sum + (entry.healthInsAmt || 0), 0)
        );

      total += Math.min(selfTotal, selfLimit) + Math.min(parentsTotal, parentsLimit);
    }

    // Calculate other sections
    const calculateSectionTotal = (entries, maxLimit) => {
      const total = entries.reduce((sum, entry) => {
        // Handle different field names for different sections
        const amount = entry.amount || 
                      entry.expenditureIncurred || 
                      entry.interestPayableForTheYear || 
                      entry.deductionAmount || 
                      entry.amountOfRentPaid ||  // For 80GG
                      entry.interestPaid ||      // For 80TTA, 80TTB, 80CCH
                      entry.totalDonation ||     // For 80G, 80GGA
                      entry.DonationAmt ||       // For 80GGC
                      0;
        return sum + amount;
      }, 0);
      return maxLimit ? Math.min(total, maxLimit) : total;
    };

    total += calculateSectionTotal(data.deduction80DDBs || [], 100000);
    total += calculateSectionTotal(data.deductionLoans?.filter(loan => loan.sectionOfLoan === '80E') || [], null);
    total += calculateSectionTotal(data.deductionLoans?.filter(loan => loan.sectionOfLoan === '80EE') || [], 50000);
    total += calculateSectionTotal(data.deductionLoans?.filter(loan => loan.sectionOfLoan === '80EEA') || [], 150000);
    total += calculateSectionTotal(data.deductionLoans?.filter(loan => loan.sectionOfLoan === '80EEB') || [], 150000);
    total += calculateSectionTotal(data.disablementDeductions?.filter(d => d.section === '80DD') || [], 125000);
    total += calculateSectionTotal(data.disablementDeductions?.filter(d => d.section === '80U') || [], 125000);
    // Calculate 80G donations (using eligibleDeduction field from individual entries)
    const total80G = (data.deductionDonations || [])
      .filter(entry => entry.sectionOfDonation === "80G")
      .reduce((sum, entry) => sum + (entry.eligibleDeduction || 0), 0);
    total += total80G;

    // Calculate 80GGA donations (using eligibleDeduction field from individual entries)
    const total80GGA = (data.deductionDonations || [])
      .filter(entry => entry.sectionOfDonation === "80GGA")
      .reduce((sum, entry) => sum + (entry.eligibleDeduction || 0), 0);
    total += total80GGA;

    total += calculateSectionTotal(data.deduction80GGs || [], 60000);
    total += calculateSectionTotal(data.deductionDepositInterests?.filter(i => i.sectionOfInterest === '80TTA') || [], 10000);
    total += calculateSectionTotal(data.deductionDepositInterests?.filter(i => i.sectionOfInterest === '80TTB') || [], 50000);
    total += calculateSectionTotal(data.deduction80GGCs || [], null);
    // Include 80QQB, 80RRB, and 80CCH under anyOthSec80CCH
    total += calculateSectionTotal(data.deductionDepositInterests?.filter(i => ['80QQB', '80RRB', '80CCH'].includes(i.sectionOfInterest)) || [], null);
    
    return total;
  };

  // Helper function to calculate 80GGA eligible deduction
  const calculate80GGAEligibleDeduction = useCallback((entry) => {
    const donationInCash = Number.parseFloat(entry.donationInCash || 0);
    const donationInOtherMode = Number.parseFloat(entry.donationInOtherMode || 0);
    
    // If cash donation is more than 2000, cash is not eligible
    const eligibleCash = donationInCash > 2000 ? 0 : donationInCash;
    
    // Other mode is always fully eligible
    const eligibleOtherMode = donationInOtherMode;
    
    return eligibleCash + eligibleOtherMode;
  }, []);

  // Helper function to calculate 80GGC eligible deduction
  const calculate80GGCEligibleDeduction = useCallback((entry) => {
    const donationInCash = Number.parseFloat(entry.DonationAmtCash || 0);
    const donationInOtherMode = Number.parseFloat(entry.DonationAmtOtherMode || 0);
    
    // Cash donations are not eligible for deduction
    // Only other mode donations are fully eligible
    return donationInOtherMode;
  }, []);

  // Helper function to calculate Adjusted Gross Total Income (GTI - all Chapter VIA deductions)
  const calculateAdjustedGrossTotalIncome = useCallback(() => {
    // Get Gross Total Income from oldRegimeData
    const grossTotalIncome = oldRegimeData ? 
      Number(oldRegimeData.incomeFromSalary || 0) + 
      Number(oldRegimeData.incomeFromHP || 0) + 
      Number(oldRegimeData.incomeFromOtherSources || 0) : 0;
    
    // Calculate 80C series eligible deductions in real-time
    const section80CFamily = [
      ...(deductionData.deduction80Cs || []),
      ...(deductionData.deduction80CCFamilies || []).filter(entry =>
        ['80CCC', '80CCD1', '80CCD1B'].includes(entry.section)
      )
    ];
    const total80CFamilyAmount = section80CFamily.reduce((sum, entry) => sum + (entry.amount || 0), 0);
    const eligible80CFamily = Math.min(total80CFamilyAmount, 150000); // 80C family has combined limit of 150000
    
    // Calculate 80CCD(2) eligible deduction (no limit)
    const total80CCD2Eligible = (deductionData.deduction80CCFamilies || [])
      .filter(entry => entry.section === '80CCD2')
      .reduce((sum, entry) => sum + (entry.amount || 0), 0);
    
    // Calculate 80TTA eligible deduction (limit: ₹10,000)
    const total80TTAAmount = (deductionData.deductionDepositInterests || [])
      .filter(entry => entry.sectionOfInterest === '80TTA')
      .reduce((sum, entry) => sum + (entry.interestPaid || 0), 0);
    const eligible80TTA = Math.min(total80TTAAmount, 10000);
    
    // Calculate 80TTB eligible deduction (limit: ₹50,000)
    const total80TTBAmount = (deductionData.deductionDepositInterests || [])
      .filter(entry => entry.sectionOfInterest === '80TTB')
      .reduce((sum, entry) => sum + (entry.interestPaid || 0), 0);
    const eligible80TTB = Math.min(total80TTBAmount, 50000);
    
    // Calculate 80GG eligible deduction (limit: ₹60,000)
    const total80GGAmount = (deductionData.deduction80GGs || [])
      .reduce((sum, entry) => sum + (entry.amountOfRentPaid || 0), 0);
    const eligible80GG = Math.min(total80GGAmount, 60000);
    
    // Calculate 80D eligible deduction (complex calculation with limits)
    let eligible80D = 0;
    if (deductionData.deduction80D) {
      const selfLimit = seniorCitizenFlagSelfFamily === 'Y' ? 50000 : 25000;
      const parentsLimit = parentsSeniorCitizenFlag === 'Y' ? 50000 : 25000;

      const selfTotal = Math.min(preventiveHealthCheckUpSelfFamily || 0, 5000) + 
        (medicalExpSelfFamily > 0 ? medicalExpSelfFamily : 
         (forSelfEntries || []).reduce((sum, entry) => sum + (entry.amount || 0), 0));

      const parentsTotal = Math.min(preventiveHealthCheckUpParents || 0, 5000) + 
        (medicalExpenditureParents > 0 ? medicalExpenditureParents : 
         (forParentEntries || []).reduce((sum, entry) => sum + (entry.amount || 0), 0));

      eligible80D = Math.min(selfTotal, selfLimit) + Math.min(parentsTotal, parentsLimit);
    }
    
    // Calculate 80DDB eligible deduction (limit: ₹1,00,000)
    const total80DDBAmount = (deductionData.deduction80DDBs || [])
      .reduce((sum, entry) => sum + (entry.expenditureIncurred || 0), 0);
    const eligible80DDB = Math.min(total80DDBAmount, 100000);
    
    // Calculate loan deductions (80E, 80EE, 80EEA, 80EEB)
    const total80EAmount = (deductionData.deductionLoans || [])
      .filter(entry => entry.sectionOfLoan === '80E')
      .reduce((sum, entry) => sum + (entry.interestPayableForTheYear || 0), 0);
    const eligible80E = total80EAmount; // No limit for 80E
    
    const total80EEAmount = (deductionData.deductionLoans || [])
      .filter(entry => entry.sectionOfLoan === '80EE')
      .reduce((sum, entry) => sum + (entry.interestPayableForTheYear || 0), 0);
    const eligible80EE = Math.min(total80EEAmount, 50000); // ₹50,000 limit
    
    const total80EEAAmount = (deductionData.deductionLoans || [])
      .filter(entry => entry.sectionOfLoan === '80EEA')
      .reduce((sum, entry) => sum + (entry.interestPayableForTheYear || 0), 0);
    const eligible80EEA = Math.min(total80EEAAmount, 150000); // ₹1,50,000 limit
    
    const total80EEBAmount = (deductionData.deductionLoans || [])
      .filter(entry => entry.sectionOfLoan === '80EEB')
      .reduce((sum, entry) => sum + (entry.interestPayableForTheYear || 0), 0);
    const eligible80EEB = Math.min(total80EEBAmount, 150000); // ₹1,50,000 limit
    
    // Calculate disability deductions (80DD, 80U)
    const total80DDAmount = (deductionData.disablementDeductions || [])
      .filter(entry => entry.section === '80DD')
      .reduce((sum, entry) => sum + (entry.deductionAmount || 0), 0);
    const eligible80DD = Math.min(total80DDAmount, 125000); // ₹1,25,000 limit
    
    const total80UAmount = (deductionData.disablementDeductions || [])
      .filter(entry => entry.section === '80U')
      .reduce((sum, entry) => sum + (entry.deductionAmount || 0), 0);
    const eligible80U = Math.min(total80UAmount, 125000); // ₹1,25,000 limit
    
    // Calculate total 80GGA eligible deduction in real-time
    const total80GGAEligible = (deductionData.deductionDonations || [])
      .filter(entry => entry.sectionOfDonation === "80GGA")
      .reduce((sum, entry) => sum + (entry.eligibleDeduction || 0), 0);
    
    // Calculate total 80GGC eligible deduction in real-time
    const total80GGCEligible = (deductionData.deduction80GGCs || [])
      .reduce((sum, entry) => sum + calculate80GGCEligibleDeduction(entry), 0);
    
    // Adjusted GTI = GTI - all eligible deductions including disability deductions
    const adjustedGTI = Math.max(0, grossTotalIncome - eligible80CFamily - total80CCD2Eligible - eligible80TTA - eligible80TTB - eligible80GG - eligible80D - eligible80DDB - eligible80E - eligible80EE - eligible80EEA - eligible80EEB - eligible80DD - eligible80U - total80GGAEligible - total80GGCEligible);
    
    return adjustedGTI;
  }, [oldRegimeData, deductionData.deductionDonations, deductionData.deduction80GGCs, deductionData.deduction80Cs, deductionData.deduction80CCFamilies, deductionData.deductionDepositInterests, deductionData.deduction80GGs, deductionData.deduction80D, deductionData.deduction80DDBs, deductionData.deductionLoans, deductionData.disablementDeductions, seniorCitizenFlagSelfFamily, parentsSeniorCitizenFlag, preventiveHealthCheckUpSelfFamily, medicalExpSelfFamily, preventiveHealthCheckUpParents, medicalExpenditureParents, forSelfEntries, forParentEntries, calculate80GGCEligibleDeduction]);

  // Helper function to calculate 80G qualifying limit based on Adjusted Gross Total Income
  const calculate80GQualifyingLimit = useCallback((donationType) => {
    // Get Adjusted Gross Total Income (GTI - all Chapter VIA deductions)
    const adjustedGrossTotalIncome = calculateAdjustedGrossTotalIncome();
    
    // Apply qualifying limit percentage based on donation type
    switch (donationType) {
      case "Don100PercentApprReqd":
        // 100% deduction types have 10% qualifying limit of Adjusted GTI
        return adjustedGrossTotalIncome * 0.10;
      
      case "Don50PercentApprReqd":
        // 50% deduction types have 5% qualifying limit of Adjusted GTI
        return adjustedGrossTotalIncome * 0.05;
      
      default:
        // No qualifying limit for other types
        return null;
    }
  }, [calculateAdjustedGrossTotalIncome]);

  // Helper function to calculate 80G eligible deduction based on donation type
  const calculate80GEligibleDeduction = useCallback((entry) => {
    const cashDonation = Number.parseFloat(entry.donationInCash || 0);
    const otherModeDonation = Number.parseFloat(entry.donationInOtherMode || 0);
    
    // Cash donation limit: if more than ₹2,000, cash is not eligible
    const eligibleCashDonation = cashDonation > 2000 ? 0 : cashDonation;
    const totalEligibleDonation = eligibleCashDonation + otherModeDonation;
    
    // Calculate deduction before qualifying limit
    let calculatedDeduction = 0;
    
    switch (entry.donationType) {
      case "Don100Percent":
        // 100% deduction without qualifying limit
        calculatedDeduction = totalEligibleDonation;
        break;
        
      case "Don50PercentNoApprReqd":
        // 50% deduction without qualifying limit
        calculatedDeduction = totalEligibleDonation * 0.5;
        break;
        
      case "Don100PercentApprReqd":
        // 100% deduction subject to qualifying limit
        calculatedDeduction = totalEligibleDonation;
        break;
        
      case "Don50PercentApprReqd":
        // 50% deduction subject to qualifying limit
        calculatedDeduction = totalEligibleDonation * 0.5;
        break;
        
      default:
        // No deduction for unknown types
        calculatedDeduction = 0;
    }
    
    // Apply qualifying limit if applicable
    const qualifyingLimit = calculate80GQualifyingLimit(entry.donationType);
    if (qualifyingLimit !== null) {
      // For qualifying limit types, the deduction cannot exceed the qualifying limit
      return Math.min(calculatedDeduction, qualifyingLimit);
    }
    
    return calculatedDeduction;
  }, [calculate80GQualifyingLimit]);

  // Memoize heavy calculations to prevent unnecessary re-computations
  const totalEligibleDeduction = useMemo(() => {
    return calculateTotalEligibleDeduction(deductionData);
  }, [deductionData]);

  const total80G = useMemo(() => {
    return (deductionData.deductionDonations || [])
      .reduce((sum, entry) => sum + (entry.eligibleDeduction || 0), 0);
  }, [deductionData.deductionDonations]);



  // Removed problematic useEffect that was causing infinite loop
  // grossTotalIncome calculation is now handled in handleSubmit only

  // Monitor total deduction vs income cap in real-time (optimized)
  useEffect(() => {
    if (deductionData?.totalTaxableIncomeCap > 0) {
      const exceedsCap = totalEligibleDeduction > deductionData.totalTaxableIncomeCap;
      
      if (exceedsCap) {
        console.log('REAL-TIME WARNING: Total deduction exceeds income cap');
        console.log('Total Chapter VI-A:', totalEligibleDeduction);
        console.log('Income Cap:', deductionData.totalTaxableIncomeCap);
        console.log('Excess:', totalEligibleDeduction - deductionData.totalTaxableIncomeCap);
      }
    }
  }, [totalEligibleDeduction, deductionData?.totalTaxableIncomeCap])

  // Helper function to ensure at least one entry exists for a specific section
  const ensureMinimumEntriesForSection = (apiField, sectionCode, defaultEntry) => {
    setDeductionData((prev) => {
      const currentArray = prev[apiField] || []
      const filteredArray = sectionCode
        ? currentArray.filter(
          (item) =>
            item.section === sectionCode ||
            item.sectionOfLoan === sectionCode ||
            item.sectionOfDonation === sectionCode ||
            item.sectionOfInterest === sectionCode,
        )
        : currentArray

      if (filteredArray.length === 0) {
        // Add a new entry with the correct sectionCode if applicable
        const newEntry = { ...defaultEntry, id: Date.now() }
        if (sectionCode) {
          if (newEntry.section !== undefined) newEntry.section = sectionCode
          if (newEntry.sectionOfLoan !== undefined) newEntry.sectionOfLoan = sectionCode
          if (newEntry.sectionOfDonation !== undefined) newEntry.sectionOfDonation = sectionCode
          if (newEntry.sectionOfInterest !== undefined) newEntry.sectionOfInterest = sectionCode
        }
        // Only add if it's not already present in the full array (to avoid duplicates on re-render)
        const isNewEntryNeeded = !currentArray.some(
          (item) =>
            (item.sectionCode && item.sectionCode === sectionCode) ||
            (item.section && item.section === sectionCode) ||
            (item.sectionOfLoan && item.sectionOfLoan === sectionCode) ||
            (item.sectionOfDonation && item.sectionOfDonation === sectionCode) ||
            (item.sectionOfInterest && item.sectionOfInterest === sectionCode),
        )

        if (isNewEntryNeeded) {
          return {
            ...prev,
            [apiField]: [...currentArray, newEntry],
          }
        }
      }
      return prev
    })
  }


  useEffect(() => {
    const fetchDeductionData = async () => {
      try {
        setIsLoading(true)
        const panNo = localStorage.getItem("PAN_NO")
        const year = localStorage.getItem("YEAR")
        if (!panNo || !year) {
          toast.error("PAN or Year not found in localStorage")
          setIsLoading(false)
          return
        }
        const response = await axios.get(`${config.apiUrl}/deduction/${panNo}/${year}`)
        console.log('Full API Response:', response)
        console.log('API Response Status:', response.status)
        console.log('API Response Data:', response.data)
        
        const apiData = response.data?.deduction

        console.log('API Response for deduction data:', response.data)
        console.log('API Data (deduction):', apiData)
        console.log('80D data from API:', apiData?.deduction80D)

        if (apiData) {
          // Process all array fields and add local IDs
          const processedData = {
            ...apiData,
            deduction80Cs: addLocalIdsToArray(apiData.deduction80Cs),
            deduction80CCFamilies: addLocalIdsToArray(apiData.deduction80CCFamilies),
            disablementDeductions: addLocalIdsToArray(apiData.disablementDeductions),
            deduction80DDBs: addLocalIdsToArray(apiData.deduction80DDBs),
            deductionLoans: addLocalIdsToArray(apiData.deductionLoans),
            deductionDonations: addLocalIdsToArray(apiData.deductionDonations),
            deduction80GGs: addLocalIdsToArray(apiData.deduction80GGs),
            deductionDepositInterests: addLocalIdsToArray(apiData.deductionDepositInterests),
            deduction80GGCs: addLocalIdsToArray(apiData.deduction80GGCs),
          }
          
          setDeductionData(processedData)

          // Map 80D data to local states
          if (apiData.deduction80D) {
            setSeniorCitizenFlagSelfFamily(apiData.deduction80D.seniorCitizenFlagSelfFamily || "")
            setMedicalExpSelfFamily(apiData.deduction80D.medicalExpSelfFamilySeniorCitizen || 0)
            setPreventiveHealthCheckUpSelfFamily(apiData.deduction80D.preventiveHealthCheckUpSelfFamilyAmount || 0)
            // Process self entries
            const selfEntries = apiData.deduction80D.deductionSeniorCitizenSelfDetails || []
            setForSelfEntries(
              selfEntries.length > 0
                ? selfEntries.map((item, idx) => ({
                  id: `self_${idx}_${Date.now()}`,
                  seniorCitizenSelfDetailId: item.seniorCitizenSelfDetailId,
                  company: item.insurerName || "",
                  policyNo: item.policyNo || "",
                  amount: item.healthInsAmt || 0,
                }))
                : [{ id: Date.now(), seniorCitizenSelfDetailId: null, company: "", policyNo: "", amount: 0 }],
            )

            // Process parent entries
            if (apiData.deduction80D.deductionParentsHealth) {
              const parentHealth = apiData.deduction80D.deductionParentsHealth
              setParentsSeniorCitizenFlag(parentHealth.parentsSeniorCitizenFlag || "")
              setMedicalExpenditureParents(parentHealth.medicalExpenditureSelfFamilySeniorCitizen || 0)
              setPreventiveHealthCheckUpParents(parentHealth.preventiveHealthCheckUpSelfFamilyAmount || 0)
              const parentEntries = parentHealth.deductionParentSeniorCitizenDetails || []
              setForParentEntries(
                parentEntries.length > 0
                  ? parentEntries.map((item, idx) => ({
                    id: `parent_${idx}_${Date.now()}`,
                    parentSeniorCitizenDetailId: item.parentSeniorCitizenDetailId,
                    company: item.insurerName || "",
                    policyNo: item.policyNo || "",
                    amount: item.healthInsAmt || 0,
                  }))
                  : [{ id: Date.now(), parentSeniorCitizenDetailId: null, company: "", policyNo: "", amount: 0 }],
              )
            } else {
              // If deductionParentsHealth is null, initialize parent entries
              setForParentEntries([
                { id: Date.now(), parentSeniorCitizenDetailId: null, company: "", policyNo: "", amount: 0 },
              ])
            }
          } else {
            // If deduction80D is null, initialize self and parent entries
            setForSelfEntries([
              { id: Date.now(), seniorCitizenSelfDetailId: null, company: "", policyNo: "", amount: 0 },
            ])
            setForParentEntries([
              { id: Date.now(), parentSeniorCitizenDetailId: null, company: "", policyNo: "", amount: 0 },
            ])
          }

          // Find first populated tab or default to 80C
          const firstPopulatedTab = deductionTypes.find((type) => {
            if (type.apiField === "deduction80D") {
              return (
                apiData.deduction80D &&
                (apiData.deduction80D.seniorCitizenFlagSelfFamily ||
                  apiData.deduction80D.medicalExpSelfFamilySeniorCitizen > 0 ||
                  apiData.deduction80D.preventiveHealthCheckUpSelfFamilyAmount > 0 ||
                  (apiData.deduction80D.deductionSeniorCitizenSelfDetails &&
                    apiData.deduction80D.deductionSeniorCitizenSelfDetails.length > 0) ||
                  (apiData.deduction80D.deductionParentsHealth &&
                    (apiData.deduction80D.deductionParentsHealth.parentsSeniorCitizenFlag ||
                      apiData.deduction80D.deductionParentsHealth.medicalExpenditureSelfFamilySeniorCitizen > 0 ||
                      apiData.deduction80D.deductionParentsHealth.preventiveHealthCheckUpSelfFamilyAmount > 0 ||
                      (apiData.deduction80D.deductionParentsHealth.deductionParentSeniorCitizenDetails &&
                        apiData.deduction80D.deductionParentsHealth.deductionParentSeniorCitizenDetails.length >
                        0))))
              )
            }
            if (type.apiField && Array.isArray(apiData[type.apiField])) {
              if (type.sectionCode) {
                return apiData[type.apiField].some(
                  (item) =>
                    item.section === type.sectionCode ||
                    item.sectionOfLoan === type.sectionCode ||
                    item.sectionOfDonation === type.sectionCode ||
                    item.sectionOfInterest === type.sectionCode,
                )
              }
              return apiData[type.apiField].length > 0
            }
            return false
          })
          if (firstPopulatedTab) {
            switchTab(firstPopulatedTab.id, firstPopulatedTab.description)
          } else {
            // If no populated tab, ensure 80C has at least one entry
            const default80C = deductionTypes.find((d) => d.id === "80C")
            ensureMinimumEntriesForSection(default80C.apiField, default80C.sectionCode, default80C.defaultEntry)
          }
          toast.success("Deduction data loaded successfully!")
        } else {
          // If no API data, ensure 80C has at least one entry
          const default80C = deductionTypes.find((d) => d.id === "80C")
          ensureMinimumEntriesForSection(default80C.apiField, default80C.sectionCode, default80C.defaultEntry)
        }
      } catch (error) {
        console.error("Error fetching deduction data:", error)
        console.error("Error response:", error.response)
        console.error("Error status:", error.response?.status)
        console.error("Error data:", error.response?.data)
        if (error.response?.status === 404) {
          toast.info("No existing deduction data found. Starting fresh.")
          // If no API data, ensure 80C has at least one entry
          const default80C = deductionTypes.find((d) => d.id === "80C")
          ensureMinimumEntriesForSection(default80C.apiField, default80C.sectionCode, default80C.defaultEntry)
        } else {
          toast.error("Failed to fetch deduction data.")
        }
      } finally {
        setIsLoading(false)
      }
    }
    fetchDeductionData()
  }, [])

  // Removed visibility change event listener to prevent continuous refreshing

  // Removed focus event listener to prevent continuous refreshing

  // Function to sync 80D data with deductionData (called only when needed)
  const sync80DData = useCallback(() => {
    setDeductionData(prev => {
      // Calculate eligible deductions for each section
      const selfLimit = seniorCitizenFlagSelfFamily === 'Y' ? 50000 : 25000;
      const parentsLimit = parentsSeniorCitizenFlag === 'Y' ? 50000 : 25000;
      
      // Calculate self total (preventive + medical + insurance entries)
      const selfPreventiveAmount = Math.min(preventiveHealthCheckUpSelfFamily, 5000);
      const selfMedicalAmount = medicalExpSelfFamily > 0 ? medicalExpSelfFamily : forSelfEntries.reduce((sum, entry) => sum + (entry.amount || 0), 0);
      const selfTotal = selfPreventiveAmount + selfMedicalAmount;
      const selfEligible = Math.min(selfTotal, selfLimit);
      
      // Calculate parents total (preventive + medical + insurance entries)
      const parentsPreventiveAmount = Math.min(preventiveHealthCheckUpParents, 5000);
      const parentsMedicalAmount = medicalExpenditureParents > 0 ? medicalExpenditureParents : forParentEntries.reduce((sum, entry) => sum + (entry.amount || 0), 0);
      const parentsTotal = parentsPreventiveAmount + parentsMedicalAmount;
      const parentsEligible = Math.min(parentsTotal, parentsLimit);
      
      console.log('=== 80D ELIGIBLE CALCULATION DEBUG ===');
      console.log('Self - Preventive:', selfPreventiveAmount, 'Medical:', selfMedicalAmount, 'Total:', selfTotal, 'Limit:', selfLimit, 'Eligible:', selfEligible);
      console.log('Parents - Preventive:', parentsPreventiveAmount, 'Medical:', parentsMedicalAmount, 'Total:', parentsTotal, 'Limit:', parentsLimit, 'Eligible:', parentsEligible);
      
      return {
        ...prev,
        deduction80D: {
          ...prev.deduction80D,
          seniorCitizenFlagSelfFamily,
          medicalExpSelfFamilySeniorCitizen: medicalExpSelfFamily,
          preventiveHealthCheckUpSelfFamilyAmount: preventiveHealthCheckUpSelfFamily,
          eligibleDeduction: selfEligible + parentsEligible, // Total eligible deduction for entire 80D section
          deductionSeniorCitizenSelfDetails: forSelfEntries.map(entry => ({
            seniorCitizenSelfDetailId: entry.seniorCitizenSelfDetailId,
            insurerName: entry.company,
            policyNo: entry.policyNo,
            healthInsAmt: entry.amount,
            // No eligibleDeduction field for individual entries
          })),
          deductionParentsHealth: {
            ...prev.deduction80D?.deductionParentsHealth,
            parentsSeniorCitizenFlag,
            medicalExpenditureSelfFamilySeniorCitizen: medicalExpenditureParents,
            preventiveHealthCheckUpSelfFamilyAmount: preventiveHealthCheckUpParents,
            eligibleDeduction: parentsEligible, // Eligible deduction for parents health section only
            deductionParentSeniorCitizenDetails: forParentEntries.map(entry => ({
              parentSeniorCitizenDetailId: entry.parentSeniorCitizenDetailId,
              insurerName: entry.company,
              policyNo: entry.policyNo,
              healthInsAmt: entry.amount,
              // No eligibleDeduction field for individual entries
            }))
          }
        }
      };
    });
    }, [seniorCitizenFlagSelfFamily, medicalExpSelfFamily, preventiveHealthCheckUpSelfFamily, forSelfEntries, parentsSeniorCitizenFlag, medicalExpenditureParents, preventiveHealthCheckUpParents, forParentEntries])

  // Auto-calculation function that navigates through all tabs and recalculates
  const performAutoCalculation = useCallback(async () => {
    setIsCalculating(true);
    setCalculationCompleted(false);
    setCurrentCalculationTab(0);

    const tabsToCalculate = [
      // Common Deductions
      "80C", "80CCC", "80CCD1", "80CCD1B", "80CCD2", "80TTA", "80TTB", "80GG",
      // Medical Deduction
      "80D", "80DDB",
      // Loan Deduction
      "80E", "80EE", "80EEA", "80EEB",
      // Disablement Deduction
      "80DD", "80U",
      // Donation Deduction
      "80GGA", "80GGC",
      // 80G Donation (final tab)
      "80G"
    ];

    for (let i = 0; i < tabsToCalculate.length; i++) {
      const tabId = tabsToCalculate[i];
      setCurrentCalculationTab(i);
      
      // Navigate to the tab
      setActiveTab(tabId);
      
      // Find the deduction type for this tab
      const deductionType = deductionTypes.find(d => d.id === tabId);
      if (deductionType) {
        setActiveTabDescription(deductionType.description);
        setActiveCategory(deductionType.category);
      }

      // Recalculate deductions for this tab
      if (tabId === "80D") {
        // Sync 80D data and recalculate
        sync80DData();
      } else if (tabId === "80GGA" || tabId === "80GGC") {
        // Recalculate donation deductions
        setDeductionData(prev => ({ ...prev }));
      } else if (tabId === "80G") {
        // Recalculate 80G donations
        setDeductionData(prev => ({ ...prev }));
      }

      // Wait a bit for UI updates and calculations
      await new Promise(resolve => setTimeout(resolve, 100));
    }

    // Mark calculation as completed and enable save button
    setCalculationCompleted(true);
    setIsCalculating(false);
    
    // Show success message
    toast.success("All deductions calculated successfully! Save button is now active.");
  }, [deductionTypes, sync80DData]);

  // This useEffect ensures that when the activeTab changes, the current section has at least one entry.
  useEffect(() => {
    // Reset render counter when tab changes to prevent false infinite loop detection
    renderCount.current = 0
    
    const currentType = deductionTypes.find((type) => type.id === activeTab)
    if (currentType && currentType.apiField && currentType.defaultEntry) {
      ensureMinimumEntriesForSection(currentType.apiField, currentType.sectionCode, currentType.defaultEntry)
    }
  }, [activeTab]) // Depend on activeTab

  const addEntry = () => {
    const currentType = deductionTypes.find((type) => type.id === activeTab)
    if (!currentType || !currentType.apiField || !currentType.defaultEntry) return

    setDeductionData((prev) => {
      const newEntry = { ...currentType.defaultEntry, id: Date.now() }
      // Also set the appropriate API ID field for consistency
      if (currentType.apiField === "deductionDonations") {
        newEntry.deductionDonationId = null; // Will be set by API when saved
      } else if (currentType.apiField === "deductionLoans") {
        newEntry.deductionLoanId = null;
      } else if (currentType.apiField === "deduction80GGs") {
        newEntry.deduction80GGId = null;
      } else if (currentType.apiField === "deduction80GGCs") {
        newEntry.Deduction80GGCId = null;
      }
      // Ensure sectionCode is set for the new entry if applicable
      if (currentType.sectionCode) {
        if (newEntry.section !== undefined) newEntry.section = currentType.sectionCode
        if (newEntry.sectionOfLoan !== undefined) newEntry.sectionOfLoan = currentType.sectionCode
        if (newEntry.sectionOfDonation !== undefined) newEntry.sectionOfDonation = currentType.sectionCode
        if (newEntry.sectionOfInterest !== undefined) newEntry.sectionOfInterest = currentType.sectionCode
      }

      const updatedArray = [...(prev[currentType.apiField] || [])]
      
      
      // For 80GGA entries, ensure proper initial calculation
      if (activeTab === "80GGA") {
        const calculatedTotal = (newEntry.donationInCash || 0) + (newEntry.donationInOtherMode || 0);
        const calculatedEligible = calculate80GGAEligibleDeduction(newEntry);
        newEntry.totalDonation = calculatedTotal;
        newEntry.eligibleDeduction = calculatedEligible;
      }
      
      // For 80G entries, ensure proper initial calculation
      if (activeTab === "80G") {
        const calculatedEligible = calculate80GEligibleDeduction(newEntry);
        newEntry.eligibleDeduction = calculatedEligible;
      }
      
      // For 80GGC entries, ensure proper initial calculation
      if (activeTab === "80GGC") {
        const calculatedTotal = (newEntry.DonationAmtCash || 0) + (newEntry.DonationAmtOtherMode || 0);
        newEntry.DonationAmt = calculatedTotal;
      }
      
      updatedArray.push(newEntry)
      return {
        ...prev,
        [currentType.apiField]: updatedArray,
      }
    })
  }

  const removeEntry = async (idToRemove, deleteDataObject) => {
    console.log("Removing entry with ID:", deleteDataObject)
    const currentType = deductionTypes.find((type) => type.id === activeTab)
    if (!currentType || !currentType.apiField) return
    console.log(currentType.apiField)

    // handling deletion from api 80C
    if (currentType.apiField === "deduction80Cs" && deleteDataObject.deduction80cId) {
      await deleteEntry("80C", deleteDataObject.deduction80cId, "Deleted Successfully", "Failed to delete entry")
    }

    // // handling deletion from api deduction80CCFamilies
    if (currentType.apiField === "deduction80CCFamilies" && deleteDataObject.deduction80CCFamilyId) {
      await deleteEntry("80CC-family", deleteDataObject.deduction80CCFamilyId, "Deleted Successfully", "Failed to delete entry")
    }

    // handling deletion from api deductionLoans
    if (currentType.apiField === "deductionLoans" && deleteDataObject.deductionLoanId) {
      await deleteEntry("loan", deleteDataObject.deductionLoanId, "Deleted Successfully", "Failed to delete entry")
    }

    // handling deletion from api deductionDonations
    if (currentType.apiField === "deductionDonations" && deleteDataObject.deductionDonationId) {
      await deleteEntry("donation", deleteDataObject.deductionDonationId, "Deleted Successfully", "Failed to delete entry")
    }

    // handling deletion from api deduction80GGCs
    if (currentType.apiField === "deduction80GGCs" && deleteDataObject.Deduction80GGCId) {
      await deleteEntry("80GGC", deleteDataObject.Deduction80GGCId, "Deleted Successfully", "Failed to delete entry")
    }

    //  // handling deletion from api 80C
    // if (currentType.apiField === "deduction80GGs" && deleteDataObject.deduction80GGId) {
    //   await deleteEntry("80GG", deleteDataObject.deduction80GGId, "Deleted Successfully", "Failed to delete entry")
    // }

    setDeductionData((prev) => {
      const updatedArray = (prev[currentType.apiField] || []).filter((entry) => entry.id !== idToRemove)
      // Do not add a default entry here. The useEffect for activeTab will handle minimum entries.
      return {
        ...prev,
        [currentType.apiField]: updatedArray,
      }
    })
  }

  // Modify the updateEntry function to allow typing freely
  const updateEntry = (idToUpdate, field, value) => {
    markPageAsUnsaved('deduction-vi-a')
    const currentType = deductionTypes.find((type) => type.id === activeTab);
    if (!currentType || !currentType.apiField) return;

    // Always update the field value first (allow typing)
    setDeductionData((prev) => {
      const updatedArray = (prev[currentType.apiField] || []).map((entry) => {
        // Handle both local id and API id fields
        const entryId = entry.id || entry.deductionDonationId || entry.deductionLoanId || entry.deduction80GGId || entry.Deduction80GGCId;
        if (entryId === idToUpdate) {
          const updatedEntry = { ...entry, [field]: value };
          // Special handling for 80GGC total donation
          if (activeTab === "80GGC" && (field === "DonationAmtCash" || field === "DonationAmtOtherMode")) {
            updatedEntry.DonationAmt =
              Number.parseFloat(updatedEntry.DonationAmtCash || 0) +
              Number.parseFloat(updatedEntry.DonationAmtOtherMode || 0);
          }
          return updatedEntry;
        }
        return entry;
      });
      return {
        ...prev,
        [currentType.apiField]: updatedArray,
      };
    });

    // Validate PRAN field but don't block input
    const error = validateField(field, value);
    if (error) {
      setValidationErrors(prev => ({
        ...prev,
        [`${idToUpdate}_${field}`]: error
      }));
    } else {
      setValidationErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[`${idToUpdate}_${field}`];
        return newErrors;
      });
    }

    // Clear errors if field is empty
    if ((field === "pranNumber" || field === "pinCode") && (!value || !value.trim())) {
      setValidationErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[`${idToUpdate}_${field}`];
        return newErrors;
      });
    }

    // Real-time validation for Schedule 80E
    if (activeTab === "80E") {
      const entry = deductionData.deductionLoans?.find(loan => loan.id === idToUpdate && loan.sectionOfLoan === '80E');
      if (entry) {
        const validationStatus = get80EValidationStatus(entry);
        if (!validationStatus.isValid) {
          // Set validation errors for missing fields
          const missingFields = validationStatus.errorMessage.replace('Required fields: ', '').split(', ');
          missingFields.forEach(fieldName => {
            const fieldKey = getFieldKeyFromDisplayName(fieldName);
            if (fieldKey) {
              setValidationErrors(prev => ({
                ...prev,
                [`${idToUpdate}_${fieldKey}`]: `${fieldName} is required`
              }));
            }
          });
        } else {
          // Clear all validation errors for this entry
          const fieldsToClear = ['loanTakenFrom', 'bankInstitutionName', 'accountNo', 'dateOfLoan', 'totalLoan', 'loanOutStanding', 'interestPayableForTheYear'];
          fieldsToClear.forEach(fieldKey => {
            setValidationErrors(prev => {
              const newErrors = { ...prev };
              delete newErrors[`${idToUpdate}_${fieldKey}`];
              return newErrors;
            });
          });
        }
      }
    }

    // Real-time validation for Schedule 80EE
    if (activeTab === "80EE") {
      const entry = deductionData.deductionLoans?.find(loan => loan.id === idToUpdate && loan.sectionOfLoan === '80EE');
      if (entry) {
        const validationStatus = get80EEValidationStatus(entry);
        if (!validationStatus.isValid) {
          // Set validation errors for missing fields
          const missingFields = validationStatus.errorMessage.replace('Required fields: ', '').split(', ');
          missingFields.forEach(fieldName => {
            const fieldKey = getFieldKeyFromDisplayName(fieldName);
            if (fieldKey) {
              setValidationErrors(prev => ({
                ...prev,
                [`${idToUpdate}_${fieldKey}`]: `${fieldName} is required`
              }));
            }
          });
        } else {
          // Clear all validation errors for this entry
          const fieldsToClear = ['loanTakenFrom', 'bankInstitutionName', 'accountNo', 'dateOfLoan', 'totalLoan', 'loanOutStanding', 'interestPayableForTheYear'];
          fieldsToClear.forEach(fieldKey => {
            setValidationErrors(prev => {
              const newErrors = { ...prev };
              delete newErrors[`${idToUpdate}_${fieldKey}`];
              return newErrors;
            });
          });
        }
      }
    }

    // Real-time validation for Schedule 80EEA
    if (activeTab === "80EEA") {
      console.log("80EEA validation triggered for entry:", idToUpdate);
      const entry = deductionData.deductionLoans?.find(loan => loan.id === idToUpdate && loan.sectionOfLoan === '80EEA');
      console.log("Found entry:", entry);
      if (entry) {
        const validationStatus = get80EEAValidationStatus(entry);
        console.log("80EEA validation status:", validationStatus);
        if (!validationStatus.isValid) {
          // Set validation errors for missing fields
          const missingFields = validationStatus.errorMessage.replace('Required fields: ', '').replace('Required fields (Loan outstanding > 0): ', '').split(', ');
          console.log("Missing fields:", missingFields);
          missingFields.forEach(fieldName => {
            const fieldKey = getFieldKeyFromDisplayName(fieldName);
            console.log("Field name:", fieldName, "Field key:", fieldKey);
            if (fieldKey) {
              const errorKey = `${idToUpdate}_${fieldKey}`;
              console.log("Setting error for key:", errorKey);
              setValidationErrors(prev => {
                const newErrors = { ...prev, [errorKey]: `${fieldName} is required` };
                console.log("New validation errors:", newErrors);
                return newErrors;
              });
            }
          });
        } else {
          // Clear all validation errors for this entry
          const fieldsToClear = ['loanTakenFrom', 'bankInstitutionName', 'accountNo', 'dateOfLoan', 'totalLoan', 'loanOutStanding', 'interestPayableForTheYear'];
          fieldsToClear.forEach(fieldKey => {
            setValidationErrors(prev => {
              const newErrors = { ...prev };
              delete newErrors[`${idToUpdate}_${fieldKey}`];
              return newErrors;
            });
          });
        }
      }
    }

    // Real-time validation for exclusive schedules (80EE and 80EEA cannot be filled together)
    if (activeTab === "80EE" || activeTab === "80EEA") {
      const exclusiveScheduleValidation = validateExclusiveSchedules();
      if (!exclusiveScheduleValidation.isValid) {
        // Set a global error for exclusive schedule violation
        setValidationErrors(prev => ({
          ...prev,
          'exclusive_schedule_error': exclusiveScheduleValidation.errorMessage
        }));
      } else {
        // Clear the exclusive schedule error if validation passes
        setValidationErrors(prev => {
          const newErrors = { ...prev };
          delete newErrors['exclusive_schedule_error'];
          return newErrors;
        });
      }
    }

  };

  // Add a centralized validateField function
  const validateField = (field, value) => {
    let patternKey = null;
    if (["pranNumber", "pran"].includes(field)) patternKey = "PRAN";
    if (["pinCode", "pin"].includes(field)) patternKey = "PIN";
    if (["dependentPan", "pan"].includes(field)) patternKey = "PAN";
    if (["dependentAadhaar", "aadhaar", "aadhaarNo", "aadharNo"].includes(field)) patternKey = "AADHAR";
    if (!patternKey) return '';

    const pattern = centralizedPatterns[patternKey];
    const stringValue = (value || '').trim();
    let error = '';

    // Only show error if field is filled and length matches pattern
    if (stringValue) {
      let requiredLength = null;
      if (patternKey === 'PAN') requiredLength = 10;
      if (patternKey === 'PRAN') requiredLength = 12;
      if (patternKey === 'AADHAR') requiredLength = 12;
      if (patternKey === 'PIN') requiredLength = 6;
      if (requiredLength && stringValue.length === requiredLength && !pattern.test(stringValue)) {
        error = `Invalid ${patternKey} format.`;
      }
    }
    return error;
  };

  // Helper function to get validation error for a field
  const getFieldError = (entryId, fieldName) => {
    return validationErrors[`${entryId}_${fieldName}`] || null;
  };

  // Helper function to clear validation error for a field
  const clearValidationError = (entryId, fieldName) => {
    const errorKey = `${entryId}_${fieldName}`;
    setValidationErrors(prev => {
      const newErrors = { ...prev };
      delete newErrors[errorKey];
      return newErrors;
    });
  };

  // Helper function to map display names to field keys for validation
  const getFieldKeyFromDisplayName = (displayName) => {
    const fieldMapping = {
      'Loan Taken From': 'loanTakenFrom',
      'Name of Loan Provider': 'bankInstitutionName',
      'Account No.': 'accountNo',
      'Date of Loan sanctioned': 'dateOfLoan',
      'Loan Amount': 'totalLoan',
      'Loan outstanding': 'loanOutStanding',
      'Interest u/s 80E': 'interestPayableForTheYear',
      'Interest u/s 80EE': 'interestPayableForTheYear',
      'Interest u/s 80EEA': 'interestPayableForTheYear'
    };
    return fieldMapping[displayName] || null;
  };


  const getCurrentEntries = () => {
    const currentType = deductionTypes.find((type) => type.id === activeTab)
    if (!currentType || !currentType.apiField) return []

    const apiArray = deductionData[currentType.apiField]
    if (!Array.isArray(apiArray)) return []

    if (currentType.sectionCode) {
      // Filter entries that match the current sectionCode
      const filtered = apiArray.filter(
        (entry) =>
          entry.section === currentType.sectionCode ||
          entry.sectionOfLoan === currentType.sectionCode ||
          entry.sectionOfDonation === currentType.sectionCode ||
          entry.sectionOfInterest === currentType.sectionCode,
      )
      // Ensure at least one entry is shown for the current filtered view
      return filtered.length === 0 ? [{ ...currentType.defaultEntry, id: Date.now() }] : filtered
    }
    // For sections without a specific sectionCode filter (e.g., 80DDB, 80DD, 80U, 80C)
    return apiArray.length === 0 ? [{ ...currentType.defaultEntry, id: Date.now() }] : apiArray
  }

  // Calculate Health Insurance Premium variables before calculateTotal function
  const healthInsurancePremiumSelf = forSelfEntries.reduce((sum, entry) => sum + (entry.amount || 0), 0);
  const healthInsurancePremiumParents = forParentEntries.reduce((sum, entry) => sum + (entry.amount || 0), 0);

  // Memoize calculateTotal function to prevent unnecessary recalculations
  const calculateTotal = useCallback((e) => {
    const currentType = deductionTypes.find((type) => type.id === activeTab)
    if (!currentType) return 0
    if (activeTab === "80D") {
      if (e === "eligible") {
        return Math.min(
          Math.min(preventiveHealthCheckUpSelfFamily, 5000) +
          (medicalExpSelfFamily > 0 ? medicalExpSelfFamily : healthInsurancePremiumSelf),
          seniorCitizenFlagSelfFamily === 'Y' ? 50000 : 25000
        ) +
          Math.min(
            Math.min(preventiveHealthCheckUpParents, 5000) +
            (medicalExpenditureParents > 0 ? medicalExpenditureParents : healthInsurancePremiumParents),
            parentsSeniorCitizenFlag === 'Y' ? 50000 : 25000
          )
          ;
      }
      else {
        const totalSelf =
          Number.parseFloat(medicalExpSelfFamily || 0) +
          Number.parseFloat(preventiveHealthCheckUpSelfFamily || 0) +
          forSelfEntries.reduce((sum, entry) => sum + Number.parseFloat(entry.amount || 0), 0)
        const totalParents =
          Number.parseFloat(medicalExpenditureParents || 0) +
          Number.parseFloat(preventiveHealthCheckUpParents || 0) +
          forParentEntries.reduce((sum, entry) => sum + Number.parseFloat(entry.amount || 0), 0)

        return totalSelf + totalParents
      }
    } else {
      const currentEntries = getCurrentEntries()
      return currentEntries.reduce((total, entry) => {
        let amountField = "amount" // Default for 80C
        
        // Special handling for 80G - sum both cash and other mode donations
        if (activeTab === "80G") {
          const cashDonation = Number.parseFloat(entry.donationInCash || 0)
          const otherModeDonation = Number.parseFloat(entry.donationInOtherMode || 0)
          return total + cashDonation + otherModeDonation
        } else if (activeTab === "80GGC") {
          amountField = "DonationAmt"
        } else if (activeTab.startsWith("80EE") || activeTab === "80E") {
          amountField = "interestPayableForTheYear"
        } else if (activeTab === "80DDB") {
          amountField = "expenditureIncurred"
        } else if (activeTab === "80DD" || activeTab === "80U") {
          amountField = "deductionAmount"
        } else if (activeTab === "80GG") {
          amountField = "amountOfRentPaid"
        } else if (activeTab === "80GGA") {
          amountField = "totalDonation"
        } else if (activeTab.startsWith("80CCD") || activeTab === "80CCC") {
          amountField = "amount"
        } else if (
          activeTab.startsWith("80TT") ||
          activeTab.startsWith("80QQ") ||
          activeTab.startsWith("80RR") ||
          activeTab.startsWith("80CC") // This covers 80CCH
        ) {
          amountField = "interestPaid"
        }

        const amount = Number.parseFloat(entry[amountField] || 0)
        return total + amount
      }, 0)
    }
  }, [activeTab, preventiveHealthCheckUpSelfFamily, medicalExpSelfFamily, healthInsurancePremiumSelf, seniorCitizenFlagSelfFamily, preventiveHealthCheckUpParents, medicalExpenditureParents, healthInsurancePremiumParents, parentsSeniorCitizenFlag, forSelfEntries, forParentEntries, getCurrentEntries])

  const getEligibleDeduction = useCallback(() => {
    // Special handling for 80G (calculation based on donation type and cash limits)
    if (activeTab === "80G") {
      const currentEntries = getCurrentEntries()
      return currentEntries.reduce((sum, entry) => {
        return sum + calculate80GEligibleDeduction(entry)
      }, 0)
    }
    // Special handling for 80GGA, and 80GGC tabs
    else if (activeTab === "80GGA") {
      const currentEntries = getCurrentEntries()
      return currentEntries.reduce((sum, entry) => sum + Number.parseFloat(entry.eligibleDeduction || 0), 0)
    } else if (activeTab === "80GGC") {
      const currentEntries = getCurrentEntries()
      return currentEntries.reduce((sum, entry) => sum + calculate80GGCEligibleDeduction(entry), 0)
    }
    
    // Default calculation for other tabs
    const total = calculateTotal("eligible")
    const currentType = deductionTypes.find((type) => type.id === activeTab)
    if (currentType?.maxLimit) {
      return Math.min(total, currentType.maxLimit)
    }
    return total
  }, [activeTab, getCurrentEntries, calculateTotal, calculate80GGCEligibleDeduction, calculate80GEligibleDeduction])

  const switchTab = (tabId, description) => {
    setActiveTab(tabId)
    setActiveTabDescription(description)
    const newCategory = deductionTypes.find((type) => type.id === tabId)?.category
    if (newCategory) {
      setActiveCategory(newCategory)
    }
    // The useEffect for activeTab will now handle ensuring minimum entries.
  }

  const switchCategory = (category) => {
    setActiveCategory(category)
    const firstTabInCategory = deductionTypes.find((type) => type.category === category)
    if (firstTabInCategory) {
      switchTab(firstTabInCategory.id, firstTabInCategory.description)
    }
  }

  // 80D specific handlers (already seem fine, just ensure value props are numbers)
  const handleAddEntry = () => {
    setForSelfEntries((prev) => [
      ...prev,
      { id: Date.now(), seniorCitizenSelfDetailId: null, company: "", policyNo: "", amount: 0 },
    ])
  }
  const handleRemoveEntry = async (id, deleteDataObject) => {
    console.log("Removing parent entry with ID:", deleteDataObject)
    // handling deletion from api 80D
    if (deleteDataObject.seniorCitizenSelfDetailId) {
      await deleteEntry("senior-self-health", deleteDataObject.seniorCitizenSelfDetailId, "Deleted Successfully", "Failed to delete entry")
    }
    setForSelfEntries((prev) => {
      const filtered = prev.filter((entry) => entry.id !== id)
      return filtered.length === 0
        ? [{ id: Date.now(), seniorCitizenSelfDetailId: null, company: "", policyNo: "", amount: 0 }]
        : filtered
    })
  }
  const handleChange = (id, field, value) => {
    setForSelfEntries((prev) => prev.map((entry) => (entry.id === id ? { ...entry, [field]: value } : entry)))
  }
  const handleAddEntryForParents = () => {
    setForParentEntries((prev) => [
      ...prev,
      { id: Date.now(), parentSeniorCitizenDetailId: null, company: "", policyNo: "", amount: 0 },
    ])
  }
  const handleRemoveEntryForParents = async (id, deleteDataObject) => {
    console.log("Removing parent entry with ID:", deleteDataObject)
    // handling deletion from api 80D
    if (deleteDataObject.parentSeniorCitizenDetailId) {
      await deleteEntry("parent-senior-health", deleteDataObject.parentSeniorCitizenDetailId, "Deleted Successfully", "Failed to delete entry")
    }
    setForParentEntries((prev) => {
      const filtered = prev.filter((entry) => entry.id !== id)
      return filtered.length === 0
        ? [{ id: Date.now(), parentSeniorCitizenDetailId: null, company: "", policyNo: "", amount: 0 }]
        : filtered
    })
  }
  const handleChangeForParents = (id, field, value) => {
    setForParentEntries((prev) => prev.map((entry) => (entry.id === id ? { ...entry, [field]: value } : entry)))
  }

  const handleSubmit = async () => {
    try {
      setIsSubmitting(true);
      const panNo = localStorage.getItem("PAN_NO");
      const year = localStorage.getItem("YEAR");
      if (!panNo || !year) {
        toast.error("PAN or Year not found");
        return;
      }

      // Guard: Total Chapter VI-A deduction cannot exceed Total income (before deduction)
      // First, try to get the cap from deductionData, if not available, fetch it fresh
      let totalIncomeBeforeDeduction = Number(deductionData?.totalTaxableIncomeCap || 0);
      
      // Use existing oldRegimeData instead of making another API call
      let apiResponseData = null;
      if (!totalIncomeBeforeDeduction && oldRegimeData) {
        apiResponseData = { taxComputation: oldRegimeData };
        totalIncomeBeforeDeduction = Number(oldRegimeData.incomeFromSalary || 0) + Number(oldRegimeData.incomeFromHP || 0) + Number(oldRegimeData.incomeFromOtherSources || 0);
      }
      
      const totalChapterVIA = Number(calculateTotalEligibleDeduction(deductionData) || 0);
      
      // Debug logging
      console.log('=== DEDUCTION VALIDATION DEBUG ===');
      console.log('totalTaxableIncomeCap from state:', deductionData?.totalTaxableIncomeCap);
      console.log('totalIncomeBeforeDeduction (calculated):', totalIncomeBeforeDeduction);
      console.log('totalChapterVIA:', totalChapterVIA);
      console.log('Validation condition (FIXED):', totalChapterVIA > totalIncomeBeforeDeduction);
      console.log('Full deductionData:', deductionData);
      console.log('API Response data:', apiResponseData);
      
      // Fixed validation: Check if deductions exceed income (including when income is zero)
      if (totalChapterVIA > totalIncomeBeforeDeduction) {
        let errorMsg;
        if (totalIncomeBeforeDeduction === 0) {
          errorMsg = `Total Deduction (₹${formatCurrency(totalChapterVIA)}) cannot be claimed when Total Income is ₹0.00. Please enter income details first.`;
        } else {
          errorMsg = `Total Deduction (₹${formatCurrency(totalChapterVIA)}) cannot exceed Total Income before deduction (₹${formatCurrency(totalIncomeBeforeDeduction)}).`;
        }
        console.log('BLOCKING SAVE - Error:', errorMsg);
        console.error('VALIDATION FAILED:', errorMsg);
        toast.error(errorMsg);
        setIsSubmitting(false);
        return;
      }
      
      console.log('Validation passed - proceeding with save');

      // Validate 80CCD(1B) entries before submission
      const eightyCCD1BEntries = deductionData.deduction80CCFamilies?.filter(entry => entry.section === '80CCD1B') || [];
      const invalidEntries = eightyCCD1BEntries.filter(entry => {
        const hasAmount = Number.parseFloat(entry.amount || 0) > 0;
        const hasPranNumber = entry.pranNumber && entry.pranNumber.trim();
        
        // Only validate if user is actively filling the form (at least one field has data)
        if (!hasAmount && !hasPranNumber) {
          return false; // No validation needed if both fields are empty
        }
        
        // Entry is invalid if it has amount but no PRAN, or has PRAN but no amount
        return (hasAmount && !hasPranNumber) || (!hasAmount && hasPranNumber);
      });

      if (invalidEntries.length > 0) {
        toast.error("For 80CCD(1B), both amount and PRAN number are required together. Please fix the validation errors.");
        setIsSubmitting(false);
        return;
      }


      // Validate 80GGA entries before submission
      const eightyGGAEntries = deductionData.deductionDonations?.filter(entry => entry.sectionOfDonation === '80GGA') || [];
      const invalid80GGAEntries = eightyGGAEntries.filter(entry => {
        const validationStatus = get80GGAValidationStatus(entry);
        return !validationStatus.isValid;
      });

      if (invalid80GGAEntries.length > 0) {
        toast.error("Please fix the validation errors in 80GGA schedule before submitting.");
        setIsSubmitting(false);
        return;
      }

      // Validate 80GGC entries before submission
      const eightyGGCEntries = deductionData.deduction80GGCs || [];
      const invalid80GGCEntries = eightyGGCEntries.filter(entry => {
        const validationStatus = get80GGCValidationStatus(entry);
        return !validationStatus.isValid;
      });

      if (invalid80GGCEntries.length > 0) {
        toast.error("Please fix the validation errors in 80GGC schedule before submitting.");
        setIsSubmitting(false);
        return;
      }

      // Validate 80G entries before submission
      const eightyGEntries = deductionData["80G"] || [];
      console.log('=== 80G VALIDATION DEBUG ===');
      console.log('80G entries:', eightyGEntries);
      console.log('Active tab:', activeTab);
      console.log('Full deductionData:', deductionData);
      
      const invalid80GEntries = eightyGEntries.filter(entry => {
        const validationStatus = get80GValidationStatus(entry);
        console.log('Entry validation:', entry, 'Status:', validationStatus);
        return !validationStatus.isValid;
      });

      console.log('Invalid 80G entries:', invalid80GEntries);

      if (invalid80GEntries.length > 0) {
        console.log('BLOCKING SAVE - 80G validation failed');
        toast.error("Please fix the validation errors in 80G schedule before submitting.");
        setIsSubmitting(false);
        return;
      }

      console.log('80G validation passed - proceeding with save');

      // Validate 80DD entries before submission
      const eightyDDEntries = deductionData.disablementDeductions?.filter(entry => entry.section === '80DD') || [];
      const invalid80DDEntries = eightyDDEntries.filter(entry => {
        const validationStatus = get80DDValidationStatus(entry);
        return !validationStatus.isValid;
      });

      if (invalid80DDEntries.length > 0) {
        toast.error("Please fix the validation errors in 80DD schedule before submitting.");
        setIsSubmitting(false);
        return;
      }

      // Validate 80U entries before submission
      const eightyUEntries = deductionData.disablementDeductions?.filter(entry => entry.section === '80U') || [];
      const invalid80UEntries = eightyUEntries.filter(entry => {
        const validationStatus = get80UValidationStatus(entry);
        return !validationStatus.isValid;
      });

      if (invalid80UEntries.length > 0) {
        toast.error("Please fix the validation errors in 80U schedule before submitting.");
        setIsSubmitting(false);
        return;
      }

      // Validate 80DDB entries before submission
      const eightyDDBEntries = deductionData.deduction80DDBs || [];
      const invalid80DDBEntries = eightyDDBEntries.filter(entry => {
        const validationStatus = get80DDBValidationStatus(entry);
        return !validationStatus.isValid;
      });

      if (invalid80DDBEntries.length > 0) {
        toast.error("Please fix the validation errors in 80DDB schedule before submitting.");
        setIsSubmitting(false);
        return;
      }

      // Validate 80D section before submission
      const eightyDValidationStatus = get80DValidationStatus();
      if (!eightyDValidationStatus.isValid) {
        toast.error("Please fix the validation errors in 80D schedule before submitting.");
        setIsSubmitting(false);
        return;
      }

      // Validate 80C entries before submission
      const eightyCEntries = deductionData.deduction80Cs || [];
      const invalid80CEntries = eightyCEntries.filter(entry => {
        const validationStatus = get80CValidationStatus(entry);
        return !validationStatus.isValid;
      });

      if (invalid80CEntries.length > 0) {
        toast.error("Please fix the validation errors in 80C schedule before submitting.");
        setIsSubmitting(false);
        return;
      }

      // Validate 80GG entries before submission
      const eightyGGEntries = deductionData.deduction80GGs || [];
      const invalid80GGEntries = eightyGGEntries.filter(entry => {
        const validationStatus = get80GGValidationStatus(entry);
        return !validationStatus.isValid;
      });

      if (invalid80GGEntries.length > 0) {
        toast.error("Please fix the validation errors in 80GG schedule before submitting.");
        setIsSubmitting(false);
        return;
      }

      // Validate 80E entries before submission
      const eightyEEntries = deductionData.deductionLoans?.filter(entry => entry.sectionOfLoan === '80E') || [];
      const invalid80EEntries = eightyEEntries.filter(entry => {
        const validationStatus = get80EValidationStatus(entry);
        return !validationStatus.isValid;
      });

      if (invalid80EEntries.length > 0) {
        toast.error("Please fix the validation errors in 80E schedule before submitting.");
        setIsSubmitting(false);
        return;
      }

      // Validate exclusive schedules (80EE and 80EEA cannot be filled together)
      const exclusiveScheduleValidation = validateExclusiveSchedules();
      if (!exclusiveScheduleValidation.isValid) {
        toast.error(exclusiveScheduleValidation.errorMessage);
        setIsSubmitting(false);
        return;
      }

      // Validate 80EE entries before submission
      const eightyEEEntries = deductionData.deductionLoans?.filter(entry => entry.sectionOfLoan === '80EE') || [];
      const invalid80EEEntries = eightyEEEntries.filter(entry => {
        const validationStatus = get80EEValidationStatus(entry);
        return !validationStatus.isValid;
      });

      if (invalid80EEEntries.length > 0) {
        toast.error("Please fix the validation errors in 80EE schedule before submitting.");
        setIsSubmitting(false);
        return;
      }

      // Validate 80EEA entries before submission
      const eightyEEAEntries = deductionData.deductionLoans?.filter(entry => entry.sectionOfLoan === '80EEA') || [];
      const invalid80EEAEntries = eightyEEAEntries.filter(entry => {
        const validationStatus = get80EEAValidationStatus(entry);
        return !validationStatus.isValid;
      });

      if (invalid80EEAEntries.length > 0) {
        toast.error("Please fix the validation errors in 80EEA schedule before submitting.");
        setIsSubmitting(false);
        return;
      }

      // Create a deep copy of deductionData to modify for payload
      const payloadDeduction = JSON.parse(JSON.stringify(deductionData));
      
      // Sync 80D data directly in payload (synchronous calculation)
      const selfLimit = seniorCitizenFlagSelfFamily === 'Y' ? 50000 : 25000;
      const parentsLimit = parentsSeniorCitizenFlag === 'Y' ? 50000 : 25000;
      
      // Calculate self total (preventive + medical + insurance entries)
      const selfPreventiveAmount = Math.min(preventiveHealthCheckUpSelfFamily, 5000);
      const selfMedicalAmount = medicalExpSelfFamily > 0 ? medicalExpSelfFamily : forSelfEntries.reduce((sum, entry) => sum + (entry.amount || 0), 0);
      const selfTotal = selfPreventiveAmount + selfMedicalAmount;
      const selfEligible = Math.min(selfTotal, selfLimit);
      
      // Calculate parents total (preventive + medical + insurance entries)
      const parentsPreventiveAmount = Math.min(preventiveHealthCheckUpParents, 5000);
      const parentsMedicalAmount = medicalExpenditureParents > 0 ? medicalExpenditureParents : forParentEntries.reduce((sum, entry) => sum + (entry.amount || 0), 0);
      const parentsTotal = parentsPreventiveAmount + parentsMedicalAmount;
      const parentsEligible = Math.min(parentsTotal, parentsLimit);
      
      // Debug logging for 80D calculation
      console.log('=== 80D PAYLOAD CALCULATION DEBUG ===');
      console.log('Self - Preventive:', selfPreventiveAmount, 'Medical:', selfMedicalAmount, 'Total:', selfTotal, 'Limit:', selfLimit, 'Eligible:', selfEligible);
      console.log('Parents - Preventive:', parentsPreventiveAmount, 'Medical:', parentsMedicalAmount, 'Total:', parentsTotal, 'Limit:', parentsLimit, 'Eligible:', parentsEligible);
      console.log('Total 80D Eligible:', selfEligible + parentsEligible);
      
      // Update payload with correct 80D data
      if (payloadDeduction.deduction80D) {
        payloadDeduction.deduction80D = {
          ...payloadDeduction.deduction80D,
          seniorCitizenFlagSelfFamily,
          medicalExpSelfFamilySeniorCitizen: medicalExpSelfFamily,
          preventiveHealthCheckUpSelfFamilyAmount: preventiveHealthCheckUpSelfFamily,
          eligibleDeduction: selfEligible + parentsEligible, // Total eligible deduction for entire 80D section
          deductionSeniorCitizenSelfDetails: forSelfEntries.map(entry => ({
            seniorCitizenSelfDetailId: entry.seniorCitizenSelfDetailId,
            insurerName: entry.company,
            policyNo: entry.policyNo,
            healthInsAmt: entry.amount
            // No eligibleDeduction field for individual entries
          })),
          deductionParentsHealth: {
            ...payloadDeduction.deduction80D?.deductionParentsHealth,
            parentsSeniorCitizenFlag,
            medicalExpenditureSelfFamilySeniorCitizen: medicalExpenditureParents,
            preventiveHealthCheckUpSelfFamilyAmount: preventiveHealthCheckUpParents,
            eligibleDeduction: parentsEligible, // Eligible deduction for parents health section only
            deductionParentSeniorCitizenDetails: forParentEntries.map(entry => ({
              parentSeniorCitizenDetailId: entry.parentSeniorCitizenDetailId,
              insurerName: entry.company,
              policyNo: entry.policyNo,
              healthInsAmt: entry.amount,
              // No eligibleDeduction field for individual entries
            }))
          }
        };
      }
      
      console.log('=== PAYLOAD DEBUG - 80D DATA ===')
      console.log('deductionData.deduction80D:', deductionData.deduction80D)
      console.log('payloadDeduction.deduction80D:', payloadDeduction.deduction80D)
      console.log('Total 80D eligible deduction:', payloadDeduction.deduction80D?.eligibleDeduction)
      console.log('Parents health eligible deduction:', payloadDeduction.deduction80D?.deductionParentsHealth?.eligibleDeduction)
      console.log('Self entries (no individual eligible):', payloadDeduction.deduction80D?.deductionSeniorCitizenSelfDetails)
      console.log('Parent entries (no individual eligible):', payloadDeduction.deduction80D?.deductionParentsHealth?.deductionParentSeniorCitizenDetails)

      // Calculate eligible deductions for each section
      const section80CFamily = [
        ...(payloadDeduction.deduction80Cs || []),
        ...(payloadDeduction.deduction80CCFamilies || []).filter(entry =>
          ['80CCC', '80CCD1', '80CCD1B'].includes(entry.section))
      ];
      const section80cs = [
        ...(payloadDeduction.deduction80Cs || []),
      ]
      const total80CFamily = section80CFamily.reduce((sum, entry) => sum + (entry.amount || 0), 0);
      const total80c = section80cs.reduce((sum, entry) => sum + (entry.amount || 0), 0);
      const eligible80CFamily = Math.min(total80CFamily, 150000);
      const eligible80C = Math.min(total80c, 150000);

      const total80CCD2 = (payloadDeduction.deduction80CCFamilies || [])
        .filter(entry => entry.section === '80CCD2')
        .reduce((sum, entry) => sum + (entry.amount || 0), 0);

      // Calculate 80D eligible
      let eligible80D = 0;
      if (payloadDeduction.deduction80D) {
        const selfLimit = payloadDeduction.deduction80D.seniorCitizenFlagSelfFamily === 'Y' ? 50000 : 25000;
        const parentsLimit = payloadDeduction.deduction80D.deductionParentsHealth?.parentsSeniorCitizenFlag === 'Y' ? 50000 : 25000;

        console.log('=== 80D ELIGIBLE CALCULATION DEBUG ===')
        console.log('selfLimit:', selfLimit)
        console.log('parentsLimit:', parentsLimit)
        console.log('preventiveHealthCheckUpSelfFamilyAmount:', payloadDeduction.deduction80D.preventiveHealthCheckUpSelfFamilyAmount)
        console.log('medicalExpSelfFamilySeniorCitizen:', payloadDeduction.deduction80D.medicalExpSelfFamilySeniorCitizen)
        console.log('deductionSeniorCitizenSelfDetails:', payloadDeduction.deduction80D.deductionSeniorCitizenSelfDetails)

        const preventiveAmount = Math.min(
          (payloadDeduction.deduction80D.preventiveHealthCheckUpSelfFamilyAmount || 0),
          5000
        );
        console.log('preventiveAmount (capped at 5000):', preventiveAmount)

        const medicalAmount = (payloadDeduction.deduction80D.medicalExpSelfFamilySeniorCitizen || 0) > 0
          ? payloadDeduction.deduction80D.medicalExpSelfFamilySeniorCitizen
          : (payloadDeduction.deduction80D.deductionSeniorCitizenSelfDetails || []).reduce((sum, entry) => sum + (entry.healthInsAmt || 0), 0);
        console.log('medicalAmount:', medicalAmount)

        const selfTotal = preventiveAmount + medicalAmount;
        console.log('selfTotal:', selfTotal)

        const parentsPreventiveAmount = Math.min(
          (payloadDeduction.deduction80D.deductionParentsHealth?.preventiveHealthCheckUpSelfFamilyAmount || 0),
          5000
        );
        console.log('parentsPreventiveAmount:', parentsPreventiveAmount)

        const parentsMedicalAmount = (payloadDeduction.deduction80D.deductionParentsHealth?.medicalExpenditureSelfFamilySeniorCitizen || 0) > 0
          ? payloadDeduction.deduction80D.deductionParentsHealth.medicalExpenditureSelfFamilySeniorCitizen
          : (payloadDeduction.deduction80D.deductionParentsHealth?.deductionParentSeniorCitizenDetails || []).reduce((sum, entry) => sum + (entry.healthInsAmt || 0), 0);
        console.log('parentsMedicalAmount:', parentsMedicalAmount)

        const parentsTotal = parentsPreventiveAmount + parentsMedicalAmount;
        console.log('parentsTotal:', parentsTotal)

        eligible80D = Math.min(selfTotal, selfLimit) + Math.min(parentsTotal, parentsLimit);
        console.log('final eligible80D:', eligible80D)
        
        // eligibleDeduction is already calculated in sync80DData function
      }

      // Calculate other sections
      const calculateSectionTotal = (entries, maxLimit) => {
        const total = entries.reduce((sum, entry) => {
          // Handle different field names for different sections
          const amount = entry.amount || 
                        entry.expenditureIncurred || 
                        entry.interestPayableForTheYear || 
                        entry.deductionAmount || 
                        entry.amountOfRentPaid ||  // For 80GG
                        entry.interestPaid ||      // For 80TTA, 80TTB, 80CCH
                        entry.totalDonation ||     // For 80G, 80GGA
                        entry.DonationAmt ||       // For 80GGC
                        0;
          return sum + amount;
        }, 0);
        return maxLimit ? Math.min(total, maxLimit) : total;
      };

      const eligible80DDB = calculateSectionTotal(payloadDeduction.deduction80DDBs || [], 100000);
      const eligible80E = calculateSectionTotal(payloadDeduction.deductionLoans?.filter(loan => loan.sectionOfLoan === '80E') || [], null);
      const eligible80EE = calculateSectionTotal(payloadDeduction.deductionLoans?.filter(loan => loan.sectionOfLoan === '80EE') || [], 50000);
      const eligible80EEA = calculateSectionTotal(payloadDeduction.deductionLoans?.filter(loan => loan.sectionOfLoan === '80EEA') || [], 150000);
      const eligible80EEB = calculateSectionTotal(payloadDeduction.deductionLoans?.filter(loan => loan.sectionOfLoan === '80EEB') || [], 150000);
      const eligible80DD = calculateSectionTotal(payloadDeduction.disablementDeductions?.filter(d => d.section === '80DD') || [], 125000);
      const eligible80U = calculateSectionTotal(payloadDeduction.disablementDeductions?.filter(d => d.section === '80U') || [], 125000);
      const eligible80G = (payloadDeduction.deductionDonations || [])
        .reduce((sum, entry) => sum + (entry.eligibleDeduction || 0), 0);
      const eligible80GG = calculateSectionTotal(payloadDeduction.deduction80GGs || [], 60000);
      const eligible80TTA = calculateSectionTotal(payloadDeduction.deductionDepositInterests?.filter(i => i.sectionOfInterest === '80TTA') || [], 10000);
      const eligible80TTB = calculateSectionTotal(payloadDeduction.deductionDepositInterests?.filter(i => i.sectionOfInterest === '80TTB') || [], 50000);
      const eligible80GGC = (payloadDeduction.deduction80GGCs || [])
        .reduce((sum, entry) => sum + calculate80GGCEligibleDeduction(entry), 0);
      // Group 80QQB, 80RRB, and 80CCH under anyOthSec80CCH
      const eligible80CCH = calculateSectionTotal(payloadDeduction.deductionDepositInterests?.filter(i => ['80QQB', '80RRB', '80CCH'].includes(i.sectionOfInterest)) || [], null);

      // Calculate total eligible deductions
      const totalEligibleDeductions = calculateTotalEligibleDeduction(payloadDeduction);

      // Prepare deductionUndChapVIAType payload (Eligible Deductions)
      payloadDeduction.deductionUndChapVIAType = {
        deductionUndChapViaTypeId: payloadDeduction.deductionUndChapVIAType?.deductionUndChapViaTypeId || null,
        section80C: eligible80C,
        section80CCC: payloadDeduction.deduction80CCFamilies
          ?.filter(entry => entry.section === '80CCC')
          .reduce((sum, entry) => sum + (entry.amount || 0), 0),
        section80CCDEmployeeOrSE: payloadDeduction.deduction80CCFamilies
          ?.filter(entry => entry.section === '80CCD1')
          .reduce((sum, entry) => sum + (entry.amount || 0), 0),
        section80CCD1B: payloadDeduction.deduction80CCFamilies
          ?.filter(entry => entry.section === '80CCD1B')
          .reduce((sum, entry) => sum + (entry.amount || 0), 0),
        section80CCDEmployer: total80CCD2,
        section80D: eligible80D,
        section80DD: eligible80DD,
        section80DDB: eligible80DDB,
        section80E: eligible80E,
        section80EE: eligible80EE,
        section80EEA: eligible80EEA,
        section80EEB: eligible80EEB,
        section80G: eligible80G,
        section80GG: eligible80GG,
        section80GGA: payloadDeduction.deduction80GGAs
          ?.reduce((sum, entry) => sum + (entry.eligibleDeduction || 0), 0) || 0,
        section80GGC: eligible80GGC,
        section80U: eligible80U,
        section80TTA: eligible80TTA,
        section80TTB: eligible80TTB,
        anyOthSec80CCH: eligible80CCH,
        totalChapVIADeductions: totalEligibleDeductions
      };
      
      // Debug logging for individual section mapping
      console.log('=== INDIVIDUAL SECTION MAPPING DEBUG ===')
      console.log('80C - Total:', total80c, 'Eligible:', eligible80C)
      console.log('80CCC - Eligible:', payloadDeduction.deduction80CCFamilies?.filter(entry => entry.section === '80CCC').reduce((sum, entry) => sum + (entry.amount || 0), 0))
      console.log('80CCD1 - Eligible:', payloadDeduction.deduction80CCFamilies?.filter(entry => entry.section === '80CCD1').reduce((sum, entry) => sum + (entry.amount || 0), 0))
      console.log('80CCD1B - Eligible:', payloadDeduction.deduction80CCFamilies?.filter(entry => entry.section === '80CCD1B').reduce((sum, entry) => sum + (entry.amount || 0), 0))
      console.log('80CCD2 - Eligible:', total80CCD2)
      console.log('80D - Eligible:', eligible80D)
      console.log('80DDB - Eligible:', eligible80DDB)
      console.log('80E - Eligible:', eligible80E)
      console.log('80EE - Eligible:', eligible80EE)
      console.log('80EEA - Eligible:', eligible80EEA)
      console.log('80EEB - Eligible:', eligible80EEB)
      console.log('80DD - Eligible:', eligible80DD)
      console.log('80U - Eligible:', eligible80U)
      console.log('80G - Eligible:', eligible80G)
      console.log('80GGA - Eligible:', payloadDeduction.deduction80GGAs?.reduce((sum, entry) => sum + (entry.eligibleDeduction || 0), 0) || 0)
      console.log('80GGC - Eligible:', eligible80GGC)
      console.log('80GG - Eligible:', eligible80GG, 'Raw Data:', payloadDeduction.deduction80GGs)
      console.log('80TTA - Eligible:', eligible80TTA, 'Raw Data:', payloadDeduction.deductionDepositInterests?.filter(i => i.sectionOfInterest === '80TTA'))
      console.log('80TTB - Eligible:', eligible80TTB, 'Raw Data:', payloadDeduction.deductionDepositInterests?.filter(i => i.sectionOfInterest === '80TTB'))
      console.log('80CCH - Eligible:', eligible80CCH, 'Raw Data:', payloadDeduction.deductionDepositInterests?.filter(i => ['80QQB', '80RRB', '80CCH'].includes(i.sectionOfInterest)))
      console.log('Total Eligible Deductions:', totalEligibleDeductions)
      console.log('deductionUndChapVIAType (Eligible):', payloadDeduction.deductionUndChapVIAType)
      // Prepare deductionUsrUndChapVIAType payload (for total amounts)
      payloadDeduction.deductionUsrUndChapVIAType = {
        deductionUserUndChapViaTypeId: payloadDeduction.deductionUsrUndChapVIAType?.deductionUserUndChapViaTypeId || null,
        // section80C: payloadDeduction.deduction80Cs?.reduce((sum, entry) => sum + (entry.amount || 0), 0) || 0,
        section80C: payloadDeduction.deduction80Cs
          ?.filter(entry => !entry.section || entry.section === '80C') // Only pure 80C entries
          .reduce((sum, entry) => sum + (entry.amount || 0), 0) || 0,
        section80CCC: payloadDeduction.deduction80CCFamilies
          ?.filter(entry => entry.section === '80CCC')
          .reduce((sum, entry) => sum + (entry.amount || 0), 0) || 0,
        section80CCDEmployeeOrSE: payloadDeduction.deduction80CCFamilies
          ?.filter(entry => entry.section === '80CCD1')
          .reduce((sum, entry) => sum + (entry.amount || 0), 0) || 0,
        section80CCD1B: payloadDeduction.deduction80CCFamilies
          ?.filter(entry => entry.section === '80CCD1B')
          .reduce((sum, entry) => sum + (entry.amount || 0), 0) || 0,
        section80CCDEmployer: total80CCD2,
        pranNum: payloadDeduction.deductionUsrUndChapVIAType?.pranNum || "",
        section80D: payloadDeduction.deduction80D ? (
          (payloadDeduction.deduction80D.preventiveHealthCheckUpSelfFamilyAmount || 0) +
          (payloadDeduction.deduction80D.medicalExpSelfFamilySeniorCitizen || 0) +
          (payloadDeduction.deduction80D.deductionSeniorCitizenSelfDetails?.reduce((sum, entry) => sum + (entry.healthInsAmt || 0), 0) || 0) +
          (payloadDeduction.deduction80D.deductionParentsHealth?.preventiveHealthCheckUpSelfFamilyAmount || 0) +
          (payloadDeduction.deduction80D.deductionParentsHealth?.medicalExpenditureSelfFamilySeniorCitizen || 0) +
          (payloadDeduction.deduction80D.deductionParentsHealth?.deductionParentSeniorCitizenDetails?.reduce((sum, entry) => sum + (entry.healthInsAmt || 0), 0) || 0)
        ) : 0,
        section80DD: payloadDeduction.disablementDeductions
          ?.filter(d => d.section === '80DD')
          .reduce((sum, entry) => sum + (entry.deductionAmount || 0), 0) || 0,
        section80DDBUsrType: payloadDeduction.deduction80DDBs?.[0]?.userType || "",
        nameOfSpecDisease80DDB: payloadDeduction.deduction80DDBs?.[0]?.nameOfSpecifiedDisease || "",
        section80DDB: payloadDeduction.deduction80DDBs
          ?.reduce((sum, entry) => sum + (entry.expenditureIncurred || 0), 0) || 0,
        section80E: payloadDeduction.deductionLoans
          ?.filter(loan => loan.sectionOfLoan === '80E')
          .reduce((sum, entry) => sum + (entry.interestPayableForTheYear || 0), 0) || 0,
        section80EE: payloadDeduction.deductionLoans
          ?.filter(loan => loan.sectionOfLoan === '80EE')
          .reduce((sum, entry) => sum + (entry.interestPayableForTheYear || 0), 0) || 0,
        section80EEA: payloadDeduction.deductionLoans
          ?.filter(loan => loan.sectionOfLoan === '80EEA')
          .reduce((sum, entry) => sum + (entry.interestPayableForTheYear || 0), 0) || 0,
        section80EEB: payloadDeduction.deductionLoans
          ?.filter(loan => loan.sectionOfLoan === '80EEB')
          .reduce((sum, entry) => sum + (entry.interestPayableForTheYear || 0), 0) || 0,
        section80G: payloadDeduction.deductionDonations
          ?.reduce((sum, entry) => sum + (entry.totalDonation || 0), 0) || 0,
        section80GG: payloadDeduction.deduction80GGs
          ?.reduce((sum, entry) => sum + (entry.amountOfRentPaid || 0), 0) || 0,
        form10BAAckNum: payloadDeduction.deduction80GGs?.[0]?.acknowledgement10BA || "",
        section80GGA: payloadDeduction.deduction80GGAs
          ?.reduce((sum, entry) => sum + (entry.totalDonation || 0), 0) || 0,
        section80GGC: payloadDeduction.deduction80GGCs
          ?.reduce((sum, entry) => sum + (entry.DonationAmt || 0), 0) || 0,
        section80U: payloadDeduction.disablementDeductions
          ?.filter(d => d.section === '80U')
          .reduce((sum, entry) => sum + (entry.deductionAmount || 0), 0) || 0,
        section80TTA: payloadDeduction.deductionDepositInterests
          ?.filter(i => i.sectionOfInterest === '80TTA')
          .reduce((sum, entry) => sum + (entry.interestPaid || 0), 0) || 0,
        section80TTB: payloadDeduction.deductionDepositInterests
          ?.filter(i => i.sectionOfInterest === '80TTB')
          .reduce((sum, entry) => sum + (entry.interestPaid || 0), 0) || 0,
        anyOthSec80CCH: payloadDeduction.deductionDepositInterests
          ?.filter(i => ['80QQB', '80RRB', '80CCH'].includes(i.sectionOfInterest))
          .reduce((sum, entry) => sum + (entry.interestPaid || 0), 0) || 0,
        totalChapVIADeductions: calculateTotalEligibleDeduction(payloadDeduction)
      };

      // Debug logging for total amounts (user entered)
      console.log('=== TOTAL AMOUNTS (USER ENTERED) DEBUG ===')
      console.log('80C - Total User Amount:', payloadDeduction.deductionUsrUndChapVIAType.section80C)
      console.log('80CCC - Total User Amount:', payloadDeduction.deductionUsrUndChapVIAType.section80CCC)
      console.log('80CCD1 - Total User Amount:', payloadDeduction.deductionUsrUndChapVIAType.section80CCDEmployeeOrSE)
      console.log('80CCD1B - Total User Amount:', payloadDeduction.deductionUsrUndChapVIAType.section80CCD1B)
      console.log('80CCD2 - Total User Amount:', payloadDeduction.deductionUsrUndChapVIAType.section80CCDEmployer)
      console.log('80D - Total User Amount:', payloadDeduction.deductionUsrUndChapVIAType.section80D)
      console.log('80DDB - Total User Amount:', payloadDeduction.deductionUsrUndChapVIAType.section80DDB)
      console.log('80E - Total User Amount:', payloadDeduction.deductionUsrUndChapVIAType.section80E)
      console.log('80EE - Total User Amount:', payloadDeduction.deductionUsrUndChapVIAType.section80EE)
      console.log('80EEA - Total User Amount:', payloadDeduction.deductionUsrUndChapVIAType.section80EEA)
      console.log('80EEB - Total User Amount:', payloadDeduction.deductionUsrUndChapVIAType.section80EEB)
      console.log('80DD - Total User Amount:', payloadDeduction.deductionUsrUndChapVIAType.section80DD)
      console.log('80U - Total User Amount:', payloadDeduction.deductionUsrUndChapVIAType.section80U)
      console.log('80G - Total User Amount:', payloadDeduction.deductionUsrUndChapVIAType.section80G)
      console.log('80GGA - Total User Amount:', payloadDeduction.deductionUsrUndChapVIAType.section80GGA)
      console.log('80GGC - Total User Amount:', payloadDeduction.deductionUsrUndChapVIAType.section80GGC)
      console.log('80GG - Total User Amount:', payloadDeduction.deductionUsrUndChapVIAType.section80GG)
      console.log('80TTA - Total User Amount:', payloadDeduction.deductionUsrUndChapVIAType.section80TTA)
      console.log('80TTB - Total User Amount:', payloadDeduction.deductionUsrUndChapVIAType.section80TTB)
      console.log('80CCH - Total User Amount:', payloadDeduction.deductionUsrUndChapVIAType.anyOthSec80CCH)
      console.log('Total User Deductions:', payloadDeduction.deductionUsrUndChapVIAType.totalChapVIADeductions)
      console.log('deductionUsrUndChapVIAType (Total):', payloadDeduction.deductionUsrUndChapVIAType)
      console.log('=== END INDIVIDUAL SECTION MAPPING ===')

      // Construct the final body to send
      const fullBody = {
        deduction: {
          ...payloadDeduction,
          totalDeduction: calculateTotalEligibleDeduction(payloadDeduction),
        },
      };

      console.log('🔄 API Call: Deduction - Saving deduction data...');
      const deductionResponse = await axios.patch(`${config.apiUrl}/deduction/${panNo}/${year}`, fullBody);
      console.log('✅ API Call: Deduction - Successfully saved');

      // Fetch updated deduction data after saving
      console.log('🔄 API Call: Deduction - Fetching updated data...');
      const deductionGetResponse = await axios.get(`${config.apiUrl}/deduction/${panNo}/${year}`);
      console.log('✅ API Call: Deduction - Successfully fetched updated data');

      // Update state with returned data
      if (deductionGetResponse.data && deductionGetResponse.data.deduction) {
        const updatedData = deductionGetResponse.data.deduction;
        const processedData = {
          ...updatedData,
          deduction80Cs: addLocalIdsToArray(updatedData.deduction80Cs),
          deduction80CCFamilies: addLocalIdsToArray(updatedData.deduction80CCFamilies),
          disablementDeductions: addLocalIdsToArray(updatedData.disablementDeductions),
          deduction80DDBs: addLocalIdsToArray(updatedData.deduction80DDBs),
          deductionLoans: addLocalIdsToArray(updatedData.deductionLoans),
          deductionDonations: addLocalIdsToArray(updatedData.deductionDonations),
          deduction80GGs: addLocalIdsToArray(updatedData.deduction80GGs),
          deductionDepositInterests: addLocalIdsToArray(updatedData.deductionDepositInterests),
          deduction80GGCs: addLocalIdsToArray(updatedData.deduction80GGCs),
        };
        setDeductionData(processedData);
        
        // Sync 80D data in state to keep UI in sync
        sync80DData();

        // Store total eligible deduction in localStorage
        localStorage.setItem("total_eligible_deduction_chapter_vi", totalEligibleDeductions.toString());
        
        // Call tax computation APIs only once
        await taxComputation(totalEligibleDeductions);
        
        
        const saved = markPageAsSaved('deduction-vi-a');
        if (saved) {
          // Reset calculation state after successful save
          setCalculationCompleted(false);
          toast.success("Deduction details updated successfully!");
        } else {
          toast.error("Please save your profile first before saving deduction details!");
        }
      }
    } catch (error) {
      console.error("Error saving deduction data:", error);
      toast.error(error.response?.data?.message || "Failed to save deduction data.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const taxComputation = async (totalAmount) => {
    try {
      const pan = localStorage.getItem("PAN_NO");
      const year = localStorage.getItem("YEAR");

      // Fetch both regimes in parallel
      console.log('🔄 API Call: Computation Old Regime - Fetching data...');
      console.log('🔄 API Call: Computation New Regime - Fetching data...');
      const [computationOldRegimeRes, computationNewRegimeRes] = await Promise.all([
        axios.get(`${config.apiUrl}/user/tax-computation/${pan}/${year}/OLD`),
        axios.get(`${config.apiUrl}/user/tax-computation/${pan}/${year}/NEW`)
      ]);
      console.log('✅ API Call: Computation Old Regime - Successfully fetched');
      console.log('✅ API Call: Computation New Regime - Successfully fetched');

      const prepareRegimeData = (data, regime, amount) => {
        return {
          ...data,
          taxComputationId: data?.taxComputationId || null,
          regime,
          totalDeductionVia: amount || 0,
        };
      };

      // Prepare payloads inside `taxComputation` key
      const computationOldRegimePayload = {
        taxComputation: prepareRegimeData(
          computationOldRegimeRes.data?.taxComputation,
          "OLD",
          totalAmount
        )
      };

      const computationNewRegimePayload = {
        taxComputation: prepareRegimeData(
          computationNewRegimeRes.data?.taxComputation,
          "NEW",
          totalAmount
        )
      };

      // Update both regimes in parallel
      console.log('🔄 API Call: Computation Old Regime - Updating data...');
      console.log('🔄 API Call: Computation New Regime - Updating data...');
      await Promise.all([
        axios.patch(`${config.apiUrl}/user/tax-computation/${pan}/${year}/OLD`, computationOldRegimePayload),
        axios.patch(`${config.apiUrl}/user/tax-computation/${pan}/${year}/NEW`, computationNewRegimePayload)
      ]);
      console.log('✅ API Call: Computation Old Regime - Successfully updated');
      console.log('✅ API Call: Computation New Regime - Successfully updated');

    } catch (error) {
      console.error("Error in taxComputation:", error);
    }
  };

  // Delete handling
  const deleteEntry = async (endpoint, id, successMessage) => {
    try {
      if (!id) {
        toast.error(`No ${endpoint} entry selected for deletion.`);
        return;
      }
      const res = await axios.delete(`${config.apiUrl}/deduction/${endpoint}/${id}`);
      toast.success(successMessage);
    } catch (error) {
      console.log(`Error in deleteEntry (${endpoint}): ${error}`);
      toast.error(`Failed to delete ${endpoint} data.`);
    }
  };

  // Update the calculateTotalEligibleDeduction function to include this logic
  const calculateTotal80CFamily = () => {
    const section80CFamily = [
      ...(deductionData.deduction80Cs || []),
      ...(deductionData.deduction80CCFamilies || []).filter(entry =>
        ['80CCC', '80CCD1'].includes(entry.section)
      )
    ];
    return section80CFamily.reduce((sum, entry) => sum + (entry.amount || 0), 0);
  };

  // Memoize 80C family calculation to prevent unnecessary re-computations
  const memoizedTotal80CFamily = useMemo(() => {
    return calculateTotal80CFamily();
  }, [deductionData.deduction80Cs, deductionData.deduction80CCFamilies]);

  // Memoize 80GGA entries calculation
  const eightyGGAEntries = useMemo(() => {
    if (activeTab === "80GGA" && deductionData.deductionDonations) {
      return deductionData.deductionDonations.filter(entry => entry.sectionOfDonation === "80GGA");
    }
    return [];
  }, [activeTab, deductionData.deductionDonations]);

  // Auto-calculate totals for 80GGA entries (optimized with batching)
  // Removed problematic useEffect that was causing infinite loop
  // 80GGA eligible deduction calculation is now handled in input handlers and handleSubmit

  // Memoize 80GGC entries calculation
  const eightyGGCEntries = useMemo(() => {
    if (activeTab === "80GGC" && deductionData.deduction80GGCs) {
      return deductionData.deduction80GGCs;
    }
    return [];
  }, [activeTab, deductionData.deduction80GGCs]);

  // Removed problematic useEffect that was causing infinite loop
  // 80GGC eligible deduction calculation is now handled in input handlers and handleSubmit

  // LoadingSkeleton component definition (moved here to fix hoisting issue)
  const LoadingSkeleton = () => (
    <div className="space-y-6 p-6">
      {/* Header skeleton */}
      <div className="h-8 bg-gray-200 rounded w-1/3 animate-pulse"></div>
      
      {/* Tabs skeleton */}
      <div className="flex space-x-4">
        {[1, 2, 3, 4, 5].map((i) => (
          <div key={i} className="h-10 bg-gray-200 rounded w-24 animate-pulse"></div>
        ))}
      </div>
      
      {/* Form skeleton */}
      <div className="space-y-4">
        <div className="h-12 bg-gray-200 rounded animate-pulse"></div>
        <div className="h-12 bg-gray-200 rounded animate-pulse"></div>
        <div className="h-12 bg-gray-200 rounded animate-pulse"></div>
      </div>
      
      {/* Buttons skeleton */}
      <div className="flex justify-end space-x-4">
        <div className="h-10 bg-gray-200 rounded w-20 animate-pulse"></div>
        <div className="h-10 bg-gray-200 rounded w-24 animate-pulse"></div>
      </div>
    </div>
  );







  // Early returns after all hooks are declared to follow Rules of Hooks
  if (isLoading) {
    return <LoadingSkeleton />;
  }

  // Check for infinite loop and display error
  if (renderCount.current > 100) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="max-w-md w-full bg-white shadow-lg rounded-lg p-6">
          <div className="flex items-center justify-center w-12 h-12 mx-auto bg-red-100 rounded-full mb-4">
            <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
          </div>
          <h3 className="text-lg font-medium text-gray-900 text-center mb-2">Component Error</h3>
          <p className="text-sm text-gray-500 text-center mb-4">
            The deduction component is stuck in an infinite loop. Please refresh the page to continue.
          </p>
          <div className="text-center">
            <button 
              onClick={() => window.location.reload()} 
              className="bg-red-600 text-white px-4 py-2 rounded-md hover:bg-red-700 transition-colors"
            >
              Refresh Page
            </button>
          </div>
        </div>
      </div>
    )
  }

  // Add this function to calculate age
  const calculateAge = (birthDate) => {
    // Fixed cutoff date: April 2, 2025 for senior citizen calculation
    const cutoffDate = new Date('2025-04-02');
    const birthDateObj = new Date(birthDate);
    let age = cutoffDate.getFullYear() - birthDateObj.getFullYear();
    const monthDiff = cutoffDate.getMonth() - birthDateObj.getMonth();

    if (monthDiff < 0 || (monthDiff === 0 && cutoffDate.getDate() < birthDateObj.getDate())) {
      age--;
    }

    return age;
  };

  // Helper function to check 80CCD(1B) validation status
  const get80CCD1BValidationStatus = (entry) => {
    if (activeTab !== "80CCD1B") return { isValid: true, errorMessage: "" };
    
    const hasAmount = Number.parseFloat(entry.amount || 0) > 0;
    const hasPranNumber = entry.pranNumber && entry.pranNumber.trim();
    
    // Only validate if user is actively filling the form (at least one field has data)
    if (!hasAmount && !hasPranNumber) {
      return { isValid: true, errorMessage: "" };
    }
    
    // If amount is entered but PRAN is missing
    if (hasAmount && !hasPranNumber) {
      return { isValid: false, errorMessage: "PRAN Number is required when amount is entered" };
    }
    
    // If PRAN is entered but amount is missing
    if (!hasAmount && hasPranNumber) {
      return { isValid: false, errorMessage: "Amount is required when PRAN Number is entered" };
    }
    
    return { isValid: true, errorMessage: "" };
  };

  // Helper function to check 80E validation status
  const get80EValidationStatus = (entry) => {
    if (activeTab !== "80E") return { isValid: true, errorMessage: "" };
    
    const hasLoanTakenFrom = entry.loanTakenFrom && entry.loanTakenFrom.trim();
    const hasBankInstitutionName = entry.bankInstitutionName && entry.bankInstitutionName.trim();
    const hasAccountNo = entry.accountNo && entry.accountNo.trim();
    const hasDateOfLoan = entry.dateOfLoan && entry.dateOfLoan.trim();
    const hasTotalLoan = Number.parseFloat(entry.totalLoan || 0) > 0;
    const hasLoanOutStanding = Number.parseFloat(entry.loanOutStanding || 0) >= 0; // Allow zero
    const hasInterestPayable = Number.parseFloat(entry.interestPayableForTheYear || 0) > 0;
    
    // Check if any field has data (user is actively filling the form)
    const hasAnyData = hasLoanTakenFrom || hasBankInstitutionName || hasAccountNo || 
                      hasDateOfLoan || hasTotalLoan || hasInterestPayable;
    
    // If no data entered, no validation needed
    if (!hasAnyData) {
      return { isValid: true, errorMessage: "" };
    }
    
    // If any field is filled, all fields are mandatory
    const missingFields = [];
    if (!hasLoanTakenFrom) missingFields.push("Loan Taken From");
    if (!hasBankInstitutionName) missingFields.push("Name of Loan Provider");
    if (!hasAccountNo) missingFields.push("Account No.");
    if (!hasDateOfLoan) missingFields.push("Date of Loan sanctioned");
    if (!hasTotalLoan) missingFields.push("Loan Amount");
    if (hasLoanOutStanding === false) missingFields.push("Loan outstanding"); // Check if it's undefined/null, not if it's zero
    if (!hasInterestPayable) missingFields.push("Interest u/s 80E");
    
    if (missingFields.length > 0) {
      return {
        isValid: false,
        errorMessage: `Required fields: ${missingFields.join(", ")}`
      };
    }
    
    return { isValid: true, errorMessage: "" };
  };

  // Helper function to check 80EE validation status
  const get80EEValidationStatus = (entry) => {
    if (activeTab !== "80EE") return { isValid: true, errorMessage: "" };
    
    const hasLoanTakenFrom = entry.loanTakenFrom && entry.loanTakenFrom.trim();
    const hasBankInstitutionName = entry.bankInstitutionName && entry.bankInstitutionName.trim();
    const hasAccountNo = entry.accountNo && entry.accountNo.trim();
    const hasDateOfLoan = entry.dateOfLoan && entry.dateOfLoan.trim();
    const hasTotalLoan = Number.parseFloat(entry.totalLoan || 0) > 0;
    const hasLoanOutStanding = Number.parseFloat(entry.loanOutStanding || 0) >= 0; // Allow zero
    const hasInterestPayable = Number.parseFloat(entry.interestPayableForTheYear || 0) > 0;
    
    // Check if any field has data (user is actively filling the form)
    const hasAnyData = hasLoanTakenFrom || hasBankInstitutionName || hasAccountNo || 
                      hasDateOfLoan || hasTotalLoan || hasInterestPayable;
    
    // If no data entered, no validation needed
    if (!hasAnyData) {
      return { isValid: true, errorMessage: "" };
    }
    
    // If any field is filled, all fields are mandatory
    const missingFields = [];
    if (!hasLoanTakenFrom) missingFields.push("Loan Taken From");
    if (!hasBankInstitutionName) missingFields.push("Name of Loan Provider");
    if (!hasAccountNo) missingFields.push("Account No.");
    if (!hasDateOfLoan) missingFields.push("Date of Loan sanctioned");
    if (!hasTotalLoan) missingFields.push("Loan Amount");
    if (hasLoanOutStanding === false) missingFields.push("Loan outstanding"); // Check if it's undefined/null, not if it's zero
    if (!hasInterestPayable) missingFields.push("Interest u/s 80EE");
    
    if (missingFields.length > 0) {
      return {
        isValid: false,
        errorMessage: `Required fields: ${missingFields.join(", ")}`
      };
    }
    
    return { isValid: true, errorMessage: "" };
  };

  // Helper function to check 80EEA validation status
  const get80EEAValidationStatus = (entry) => {
    console.log("get80EEAValidationStatus called with entry:", entry);
    console.log("activeTab:", activeTab);
    
    if (activeTab !== "80EEA") {
      console.log("Not 80EEA tab, returning valid");
      return { isValid: true, errorMessage: "" };
    }
    
    const hasLoanTakenFrom = entry.loanTakenFrom && entry.loanTakenFrom.trim();
    const hasBankInstitutionName = entry.bankInstitutionName && entry.bankInstitutionName.trim();
    const hasAccountNo = entry.accountNo && entry.accountNo.trim();
    const hasDateOfLoan = entry.dateOfLoan && entry.dateOfLoan.trim();
    const hasTotalLoan = Number.parseFloat(entry.totalLoan || 0) > 0;
    const hasLoanOutStanding = Number.parseFloat(entry.loanOutStanding || 0);
    const hasInterestPayable = Number.parseFloat(entry.interestPayableForTheYear || 0) > 0;
    
    console.log("Field validation results:", {
      hasLoanTakenFrom,
      hasBankInstitutionName,
      hasAccountNo,
      hasDateOfLoan,
      hasTotalLoan,
      hasLoanOutStanding,
      hasInterestPayable
    });
    
    // Check if any field has data (user is actively filling the form)
    const hasAnyData = hasLoanTakenFrom || hasBankInstitutionName || hasAccountNo || 
                      hasDateOfLoan || hasTotalLoan || hasInterestPayable;
    
    console.log("Has any data:", hasAnyData);
    
    // If no data entered, no validation needed
    if (!hasAnyData) {
      console.log("No data entered, returning valid");
      return { isValid: true, errorMessage: "" };
    }
    
    // If Loan outstanding is more than 0, all fields are mandatory
    if (hasLoanOutStanding > 0) {
      const missingFields = [];
      if (!hasLoanTakenFrom) missingFields.push("Loan Taken From");
      if (!hasBankInstitutionName) missingFields.push("Name of Loan Provider");
      if (!hasAccountNo) missingFields.push("Account No.");
      if (!hasDateOfLoan) missingFields.push("Date of Loan sanctioned");
      if (!hasTotalLoan) missingFields.push("Loan Amount");
      if (!hasInterestPayable) missingFields.push("Interest u/s 80EEA");
      
      console.log("Loan outstanding > 0, missing fields:", missingFields);
      
      if (missingFields.length > 0) {
        return {
          isValid: false,
          errorMessage: `Required fields (Loan outstanding > 0): ${missingFields.join(", ")}`
        };
      }
    } else {
      // If Loan outstanding is 0, still validate other fields if any are filled
      const missingFields = [];
      if (!hasLoanTakenFrom) missingFields.push("Loan Taken From");
      if (!hasBankInstitutionName) missingFields.push("Name of Loan Provider");
      if (!hasAccountNo) missingFields.push("Account No.");
      if (!hasDateOfLoan) missingFields.push("Date of Loan sanctioned");
      if (!hasTotalLoan) missingFields.push("Loan Amount");
      if (!hasInterestPayable) missingFields.push("Interest u/s 80EEA");
      
      console.log("Loan outstanding = 0, missing fields:", missingFields);
      
      if (missingFields.length > 0) {
        return {
          isValid: false,
          errorMessage: `Required fields: ${missingFields.join(", ")}`
        };
      }
    }
    
    console.log("All fields valid");
    return { isValid: true, errorMessage: "" };
  };


  // Helper function to check if user is filling both 80EE and 80EEA schedules
  const validateExclusiveSchedules = () => {
    const eightyEEEntries = deductionData.deductionLoans?.filter(entry => entry.sectionOfLoan === '80EE') || [];
    const eightyEEAEntries = deductionData.deductionLoans?.filter(entry => entry.sectionOfLoan === '80EEA') || [];
    
    // Check if any 80EE entry has data
    const has80EEData = eightyEEEntries.some(entry => {
      const hasLoanTakenFrom = entry.loanTakenFrom && entry.loanTakenFrom.trim();
      const hasBankInstitutionName = entry.bankInstitutionName && entry.bankInstitutionName.trim();
      const hasAccountNo = entry.accountNo && entry.accountNo.trim();
      const hasDateOfLoan = entry.dateOfLoan && entry.dateOfLoan.trim();
      const hasTotalLoan = Number.parseFloat(entry.totalLoan || 0) > 0;
      const hasLoanOutStanding = Number.parseFloat(entry.loanOutStanding || 0) >= 0;
      const hasInterestPayable = Number.parseFloat(entry.interestPayableForTheYear || 0) > 0;
      
      return hasLoanTakenFrom || hasBankInstitutionName || hasAccountNo || 
             hasDateOfLoan || hasTotalLoan || hasInterestPayable;
    });
    
    // Check if any 80EEA entry has data
    const has80EEAData = eightyEEAEntries.some(entry => {
      const hasLoanTakenFrom = entry.loanTakenFrom && entry.loanTakenFrom.trim();
      const hasBankInstitutionName = entry.bankInstitutionName && entry.bankInstitutionName.trim();
      const hasAccountNo = entry.accountNo && entry.accountNo.trim();
      const hasDateOfLoan = entry.dateOfLoan && entry.dateOfLoan.trim();
      const hasTotalLoan = Number.parseFloat(entry.totalLoan || 0) > 0;
      const hasLoanOutStanding = Number.parseFloat(entry.loanOutStanding || 0) >= 0;
      const hasInterestPayable = Number.parseFloat(entry.interestPayableForTheYear || 0) > 0;
      
      return hasLoanTakenFrom || hasBankInstitutionName || hasAccountNo || 
             hasDateOfLoan || hasTotalLoan || hasInterestPayable;
    });
    
    // If both schedules have data, return error
    if (has80EEData && has80EEAData) {
      return {
        isValid: false,
        errorMessage: "You can only fill one schedule: either 80EE OR 80EEA, not both"
      };
    }
    
    return { isValid: true, errorMessage: "" };
  };


  // Helper function to update 80GGA entry totals
  const update80GGATotals = (entryId, field, value) => {
    updateEntry(entryId, field, value);
    
    // Update total donation and eligible deduction
    setTimeout(() => {
      const entries = deductionData.deductionDonations || [];
      const updatedEntry = entries.find(e => e.id === entryId);
      if (updatedEntry) {
        const newEntry = { ...updatedEntry, [field]: value };
        const totalDonation = (Number.parseFloat(newEntry.donationInCash || 0)) + (Number.parseFloat(newEntry.donationInOtherMode || 0));
        const eligibleDeduction = calculate80GGAEligibleDeduction(newEntry);
        
        updateEntry(entryId, "totalDonation", totalDonation);
        updateEntry(entryId, "eligibleDeduction", eligibleDeduction);
      }
    }, 0);
  };

  // Helper function to update 80GGC entry totals
  const update80GGCTotals = (entryId, field, value) => {
    updateEntry(entryId, field, value);
    
    // Update total donation and eligible deduction
    setTimeout(() => {
      const entries = deductionData.deduction80GGCs || [];
      const updatedEntry = entries.find(e => e.id === entryId);
      if (updatedEntry) {
        const newEntry = { ...updatedEntry, [field]: value };
        const totalDonation = (Number.parseFloat(newEntry.DonationAmtCash || 0)) + (Number.parseFloat(newEntry.DonationAmtOtherMode || 0));
        const eligibleDeduction = calculate80GGCEligibleDeduction(newEntry);
        
        updateEntry(entryId, "DonationAmt", totalDonation);
        updateEntry(entryId, "eligibleDeduction", eligibleDeduction);
      }
    }, 0);
  };


  // Helper function to check 80GGC validation status
  const get80GGCValidationStatus = (entry) => {
    if (activeTab !== "80GGC") return { isValid: true, errorMessage: "" };
    
    const hasDonationInCash = Number.parseFloat(entry.DonationAmtCash || 0) > 0;
    const hasDonationInOtherMode = Number.parseFloat(entry.DonationAmtOtherMode || 0) > 0;
    const hasDonationDate = entry.DonationDate && entry.DonationDate.trim();
    const hasTransactionRefNum = entry.TransactionRefNum && entry.TransactionRefNum.trim();
    const hasIfscCode = entry.IfscCode && entry.IfscCode.trim();
    
    // Check if any field has data (user is actively filling the form)
    const hasAnyData = hasDonationInCash || hasDonationInOtherMode || hasDonationDate || 
                      hasTransactionRefNum || hasIfscCode;
    
    // If no data entered, no validation needed
    if (!hasAnyData) {
      return { isValid: true, errorMessage: "" };
    }
    
    // Both cash and other mode cannot be filled together
    if (hasDonationInCash && hasDonationInOtherMode) {
      return {
        isValid: false,
        errorMessage: "Only one donation mode (Cash OR Other Mode) can be filled, not both"
      };
    }
    
    // At least one donation mode should be filled
    if (!hasDonationInCash && !hasDonationInOtherMode) {
      return {
        isValid: false,
        errorMessage: "Either Cash donation or Other Mode donation must be filled"
      };
    }
    
    // If cash donation is filled, only date is mandatory
    if (hasDonationInCash) {
      if (!hasDonationDate) {
        return {
          isValid: false,
          errorMessage: "Date of donation is mandatory for cash donations"
        };
      }
    }
    
    // If other mode donation is filled, date, transaction ref and IFSC are mandatory
    if (hasDonationInOtherMode) {
      const missingFields = [];
      if (!hasDonationDate) missingFields.push("Date of donation");
      if (!hasTransactionRefNum) missingFields.push("Transaction Reference No.");
      if (!hasIfscCode) missingFields.push("IFSC Code");
      
      if (missingFields.length > 0) {
        return {
          isValid: false,
          errorMessage: `Required for other mode donations: ${missingFields.join(", ")}`
        };
      }
    }
    
    return { isValid: true, errorMessage: "" };
  };

  // Helper function to check if disability type is severe (for limit calculation)
  const isSevereDisability = (typeOfDisability) => {
    // Check if the disability type indicates severe disability
    // You may need to adjust these values based on your actual dropdown options
    const severeDisabilityTypes = [
      "AUTISM", "CEREBAL PALSY", "MULTIPLE DISABILITIES", "SEVERE MENTAL RETARDATION",
      "BLINDNESS", "LOW VISION", "LEPROSY CURED", "HEARING IMPAIRMENT", "LOCOMOTOR DISABILITY",
      "MENTAL ILLNESS", "CHRONIC NEUROLOGICAL CONDITIONS", "SPECIFIC LEARNING DISABILITIES",
      "MULTIPLE SCLEROSIS", "PARKINSON'S DISEASE", "HAEMOPHILIA", "THALASSEMIA", "SICKLE CELL DISEASE"
    ];
    
    return severeDisabilityTypes.some(severe => 
      typeOfDisability && typeOfDisability.toUpperCase().includes(severe)
    );
  };

  // Helper function to check if Form 10IA is required for specific disabilities
  const isForm10IARequired = (typeOfDisability, typeOfDisabilityList) => {
    if (!typeOfDisability || !typeOfDisabilityList) return false;
    
    // Find the selected disability option from the list
    const selectedDisability = typeOfDisabilityList.find(option => option.codeValue === typeOfDisability);
    if (!selectedDisability) return false;
    
    // Check the description for the specific conditions
    const description = selectedDisability.description.toUpperCase();
    return description.includes("AUTISM") || 
           description.includes("CEREBAL PALSY") || 
           description.includes("CEREBRAL PALSY") || 
           description.includes("MULTIPLE DISABILIT");
  };

  // Helper function to get disability amount limit
  const getDisabilityAmountLimit = (typeOfDisability) => {
    return isSevereDisability(typeOfDisability) ? 125000 : 75000;
  };

  // Helper function to get 80DDB amount limit based on user type
  const get80DDBAmountLimit = (userType) => {
    // userType: "1" = Self or Dependent, "2" = Self or Dependent - Senior Citizen
    return userType === "2" ? 100000 : 40000;
  };

  // Helper function to check 80DDB validation status
  const get80DDBValidationStatus = (entry) => {
    if (activeTab !== "80DDB") return { isValid: true, errorMessage: "" };
    
    const hasAmount = Number.parseFloat(entry.expenditureIncurred || 0) > 0;
    const hasUserType = entry.userType && entry.userType.trim();
    const hasDiseaseName = entry.nameOfSpecifiedDisease && entry.nameOfSpecifiedDisease.trim();
    
    // Check if any field has data (user is actively filling the form)
    const hasAnyData = hasAmount || hasUserType || hasDiseaseName;
    
    // If no data entered, no validation needed
    if (!hasAnyData) {
      return { isValid: true, errorMessage: "" };
    }
    
    // If any field is filled, all required fields must be filled
    if (hasAmount || hasUserType || hasDiseaseName) {
      const missingFields = [];
      if (!hasUserType) missingFields.push("Type of User");
      if (!hasDiseaseName) missingFields.push("Name of Specified Disease");
      if (!hasAmount) missingFields.push("Amount");
      
      if (missingFields.length > 0) {
        return {
          isValid: false,
          errorMessage: `Required fields: ${missingFields.join(", ")}`
        };
      }
    }
    
    // Check amount limit based on user type
    if (hasAmount && hasUserType) {
      const limit = get80DDBAmountLimit(entry.userType);
      if (entry.expenditureIncurred > limit) {
        const userTypeText = entry.userType === "2" ? "Senior Citizen" : "Self/Dependent";
        return {
          isValid: false,
          errorMessage: `Amount cannot exceed ₹${limit.toLocaleString()} for ${userTypeText}`
        };
      }
    }
    
    return { isValid: true, errorMessage: "" };
  };

  // Helper function to check 80D validation status
  const get80DValidationStatus = () => {
    if (activeTab !== "80D") return { isValid: true, errorMessage: "" };
    
    // Check Self/Family section
    const hasSelfSeniorFlag = seniorCitizenFlagSelfFamily && seniorCitizenFlagSelfFamily !== "";
    const hasSelfMedicalExp = Number.parseFloat(medicalExpSelfFamily || 0) > 0;
    const hasSelfPreventiveCheckup = Number.parseFloat(preventiveHealthCheckUpSelfFamily || 0) > 0;
    const hasSelfInsuranceDetails = forSelfEntries.some(entry => 
      (entry.company && entry.company.trim()) || 
      (entry.policyNo && entry.policyNo.trim()) || 
      Number.parseFloat(entry.amount || 0) > 0
    );
    const hasSelfInsuranceAmount = forSelfEntries.some(entry => Number.parseFloat(entry.amount || 0) > 0);
    
    // Check Parents section
    const hasParentsSeniorFlag = parentsSeniorCitizenFlag && parentsSeniorCitizenFlag !== "";
    const hasParentsMedicalExp = Number.parseFloat(medicalExpenditureParents || 0) > 0;
    const hasParentsPreventiveCheckup = Number.parseFloat(preventiveHealthCheckUpParents || 0) > 0;
    const hasParentsInsuranceDetails = forParentEntries.some(entry => 
      (entry.company && entry.company.trim()) || 
      (entry.policyNo && entry.policyNo.trim()) || 
      Number.parseFloat(entry.amount || 0) > 0
    );
    const hasParentsInsuranceAmount = forParentEntries.some(entry => Number.parseFloat(entry.amount || 0) > 0);
    
    // Check if user is actively filling any section
    const hasAnySelfData = hasSelfSeniorFlag || hasSelfMedicalExp || hasSelfPreventiveCheckup || hasSelfInsuranceDetails;
    const hasAnyParentsData = hasParentsSeniorFlag || hasParentsMedicalExp || hasParentsPreventiveCheckup || hasParentsInsuranceDetails;
    
    // If no data entered at all, no validation needed
    if (!hasAnySelfData && !hasAnyParentsData) {
      return { isValid: true, errorMessage: "" };
    }
    
    const errors = [];
    
    // Validate Self/Family section
    if (hasAnySelfData) {
      // If senior citizen flag is selected, at least one amount field is required
      if (hasSelfSeniorFlag && seniorCitizenFlagSelfFamily !== "S") {
        const hasAnyAmount = hasSelfMedicalExp || hasSelfPreventiveCheckup || hasSelfInsuranceAmount;
        if (!hasAnyAmount) {
          errors.push("Self/Family: Amount is required when senior citizen status is selected");
        }
      }
      
      // If insurance details are filled, amount is required
      if (hasSelfInsuranceDetails && !hasSelfInsuranceAmount) {
        const hasInsuranceInfo = forSelfEntries.some(entry => 
          (entry.company && entry.company.trim()) || (entry.policyNo && entry.policyNo.trim())
        );
        if (hasInsuranceInfo) {
          errors.push("Self/Family: Insurance amount is required when insurance details are provided");
        }
      }
      
      // If insurance amount is filled, insurance details are required
      if (hasSelfInsuranceAmount) {
        const missingInsuranceDetails = forSelfEntries.filter(entry => 
          Number.parseFloat(entry.amount || 0) > 0 && 
          (!entry.company || !entry.company.trim() || !entry.policyNo || !entry.policyNo.trim())
        );
        if (missingInsuranceDetails.length > 0) {
          errors.push("Self/Family: Insurance company name and policy number are required when amount is provided");
        }
      }
    }
    
    // Validate Parents section
    if (hasAnyParentsData) {
      // If senior citizen flag is selected, at least one amount field is required
      if (hasParentsSeniorFlag && parentsSeniorCitizenFlag !== "S") {
        const hasAnyAmount = hasParentsMedicalExp || hasParentsPreventiveCheckup || hasParentsInsuranceAmount;
        if (!hasAnyAmount) {
          errors.push("Parents: Amount is required when senior citizen status is selected");
        }
      }
      
      // If insurance details are filled, amount is required
      if (hasParentsInsuranceDetails && !hasParentsInsuranceAmount) {
        const hasInsuranceInfo = forParentEntries.some(entry => 
          (entry.company && entry.company.trim()) || (entry.policyNo && entry.policyNo.trim())
        );
        if (hasInsuranceInfo) {
          errors.push("Parents: Insurance amount is required when insurance details are provided");
        }
      }
      
      // If insurance amount is filled, insurance details are required
      if (hasParentsInsuranceAmount) {
        const missingInsuranceDetails = forParentEntries.filter(entry => 
          Number.parseFloat(entry.amount || 0) > 0 && 
          (!entry.company || !entry.company.trim() || !entry.policyNo || !entry.policyNo.trim())
        );
        if (missingInsuranceDetails.length > 0) {
          errors.push("Parents: Insurance company name and policy number are required when amount is provided");
        }
      }
    }
    
    if (errors.length > 0) {
      return {
        isValid: false,
        errorMessage: errors.join("; ")
      };
    }
    
    return { isValid: true, errorMessage: "" };
  };
  // Helper function to check 80C validation status
  const get80CValidationStatus = (entry) => {
    if (activeTab !== "80C") return { isValid: true, errorMessage: "" };
    
    const hasAmount = Number.parseFloat(entry.amount || 0) > 0;
    const hasIdentification = entry.identificationNo && entry.identificationNo.trim();
    const hasNature = entry.nature && entry.nature.trim();
    
    // Check if any field has data (user is actively filling the form)
    const hasAnyData = hasAmount || hasIdentification || hasNature;
    
    // If no data entered, no validation needed
    if (!hasAnyData) {
      return { isValid: true, errorMessage: "" };
    }
    
    // If amount is entered, identification is required
    if (hasAmount && !hasIdentification) {
      return {
        isValid: false,
        errorMessage: "Identification No. is required when amount is entered"
      };
    }
    
    // If identification is entered, amount should be entered
    if (hasIdentification && !hasAmount) {
      return {
        isValid: false,
        errorMessage: "Amount is required when identification is provided"
      };
    }
    
    return { isValid: true, errorMessage: "" };
  };

  // Helper function to check 80GG validation status
  const get80GGValidationStatus = (entry) => {
    if (activeTab !== "80GG") return { isValid: true, errorMessage: "" };
    
    const hasAmount = Number.parseFloat(entry.amountOfRentPaid || 0) > 0;
    const hasForm10BA = entry.acknowledgement10BA && entry.acknowledgement10BA.trim();
    
    // Check if any field has data (user is actively filling the form)
    const hasAnyData = hasAmount || hasForm10BA;
    
    // If no data entered, no validation needed
    if (!hasAnyData) {
      return { isValid: true, errorMessage: "" };
    }
    
    // If amount is entered, Form 10BA acknowledgement number is required
    if (hasAmount && !hasForm10BA) {
      return {
        isValid: false,
        errorMessage: "Form 10BA Acknowledgement Number is required when amount is entered"
      };
    }
    
    // If Form 10BA is entered, amount should be entered
    if (hasForm10BA && !hasAmount) {
      return {
        isValid: false,
        errorMessage: "Annual Rent Amount is required when Form 10BA acknowledgement number is provided"
      };
    }
    
    return { isValid: true, errorMessage: "" };
  };

  // Helper function to check 80DD validation status
  const get80DDValidationStatus = (entry) => {
    if (activeTab !== "80DD") return { isValid: true, errorMessage: "" };
    
    const hasAmount = Number.parseFloat(entry.deductionAmount || 0) > 0;
    const hasNatureOfDisability = entry.natureOfDisability && entry.natureOfDisability.trim();
    const hasTypeOfDisability = entry.typeOfDisability && entry.typeOfDisability.trim();
    const hasDependentType = entry.dependentType && entry.dependentType.trim();
    const hasForm10IA = entry.form10AAckNum && entry.form10AAckNum.trim();
    
    // Check if any field has data (user is actively filling the form)
    const hasAnyData = hasAmount || hasNatureOfDisability || hasTypeOfDisability || 
                      hasDependentType || hasForm10IA;
    
    // If no data entered, no validation needed
    if (!hasAnyData) {
      return { isValid: true, errorMessage: "" };
    }
    
    // If any amount is entered, all basic fields are required
    if (hasAmount || hasNatureOfDisability || hasTypeOfDisability || hasDependentType) {
      const missingFields = [];
      if (!hasNatureOfDisability) missingFields.push("Nature of Disability");
      if (!hasTypeOfDisability) missingFields.push("Type of Disability");
      if (!hasDependentType) missingFields.push("Type of Dependent");
      if (!hasAmount) missingFields.push("Amount");
      
      if (missingFields.length > 0) {
        return {
          isValid: false,
          errorMessage: `Required fields: ${missingFields.join(", ")}`
        };
      }
    }
    
    // Check amount limit based on disability type
    if (hasAmount && hasTypeOfDisability) {
      const limit = getDisabilityAmountLimit(entry.typeOfDisability);
      if (entry.deductionAmount > limit) {
        return {
          isValid: false,
          errorMessage: `Amount cannot exceed ₹${limit.toLocaleString()} for this disability type`
        };
      }
    }
    
    // Check if Form 10IA is required for autism/cerebral palsy/multiple disability
    if (hasTypeOfDisability && isForm10IARequired(entry.typeOfDisability, typeOfDisabilityList) && !hasForm10IA) {
      return {
        isValid: false,
        errorMessage: "Form 10IA acknowledgement number is required for Autism/Cerebral Palsy/Multiple Disability"
      };
    }
    
    return { isValid: true, errorMessage: "" };
  };

  // Helper function to check 80U validation status
  const get80UValidationStatus = (entry) => {
    if (activeTab !== "80U") return { isValid: true, errorMessage: "" };
    
    const hasAmount = Number.parseFloat(entry.deductionAmount || 0) > 0;
    const hasNatureOfDisability = entry.natureOfDisability && entry.natureOfDisability.trim();
    const hasTypeOfDisability = entry.typeOfDisability && entry.typeOfDisability.trim();
    const hasForm10IA = entry.form10AAckNum && entry.form10AAckNum.trim();
    
    // Check if any field has data (user is actively filling the form)
    const hasAnyData = hasAmount || hasNatureOfDisability || hasTypeOfDisability || hasForm10IA;
    
    // If no data entered, no validation needed
    if (!hasAnyData) {
      return { isValid: true, errorMessage: "" };
    }
    
    // If any amount is entered, all basic fields are required
    if (hasAmount || hasNatureOfDisability || hasTypeOfDisability) {
      const missingFields = [];
      if (!hasNatureOfDisability) missingFields.push("Nature of Disability");
      if (!hasTypeOfDisability) missingFields.push("Type of Disability");
      if (!hasAmount) missingFields.push("Amount");
      
      if (missingFields.length > 0) {
        return {
          isValid: false,
          errorMessage: `Required fields: ${missingFields.join(", ")}`
        };
      }
    }
    
    // Check amount limit based on disability type
    if (hasAmount && hasTypeOfDisability) {
      const limit = getDisabilityAmountLimit(entry.typeOfDisability);
      if (entry.deductionAmount > limit) {
        return {
          isValid: false,
          errorMessage: `Amount cannot exceed ₹${limit.toLocaleString()} for this disability type`
        };
      }
    }
    
    // Check if Form 10IA is required for autism/cerebral palsy/multiple disability
    if (hasTypeOfDisability && isForm10IARequired(entry.typeOfDisability, typeOfDisabilityList) && !hasForm10IA) {
      return {
        isValid: false,
        errorMessage: "Form 10IA acknowledgement number is required for Autism/Cerebral Palsy/Multiple Disability"
      };
    }
    
    return { isValid: true, errorMessage: "" };
  };



  // Helper function to check 80GGA validation status
  const get80GGAValidationStatus = (entry) => {
    if (activeTab !== "80GGA") return { isValid: true, errorMessage: "" };
    
    const hasDonationInCash = Number.parseFloat(entry.donationInCash || 0) > 0;
    const hasDonationInOtherMode = Number.parseFloat(entry.donationInOtherMode || 0) > 0;
    const hasDonationType = entry.donationType && entry.donationType.trim();
    const hasPanOfDonee = entry.panOfDonee && entry.panOfDonee.trim();
    const hasNameOfDonee = entry.nameOfDonee && entry.nameOfDonee.trim();
    const hasAddressOfDonee = entry.addressOfDonee && entry.addressOfDonee.trim();
    const hasCityTownDistrict = entry.cityTownDistrict && entry.cityTownDistrict.trim();
    const hasStateCode = entry.stateCode && entry.stateCode.trim();
    const hasPinCode = entry.pinCode && entry.pinCode.trim();
    
    // Check if any field has data (user is actively filling the form)
    const hasAnyData = hasDonationInCash || hasDonationInOtherMode || hasDonationType || 
                      hasPanOfDonee || hasNameOfDonee || hasAddressOfDonee || 
                      hasCityTownDistrict || hasStateCode || hasPinCode;
    
    // If no data entered, no validation needed
    if (!hasAnyData) {
      return { isValid: true, errorMessage: "" };
    }
    
    // If user started filling, validate that at least one donation mode has value
    if (!hasDonationInCash && !hasDonationInOtherMode) {
      return {
        isValid: false,
        errorMessage: "At least one donation mode (Cash or Other Mode) must have a value greater than 0"
      };
    }
    
    // Check for required fields when any donation amount is entered
    if (hasDonationInCash || hasDonationInOtherMode) {
      const missingFields = [];
      if (!hasDonationType) missingFields.push("Donation Type");
      if (!hasPanOfDonee) missingFields.push("PAN of Donee");
      if (!hasNameOfDonee) missingFields.push("Name of Donee");
      if (!hasAddressOfDonee) missingFields.push("Address");
      if (!hasCityTownDistrict) missingFields.push("City/Town/District");
      if (!hasPinCode) missingFields.push("Pin Code");
      
      if (missingFields.length > 0) {
        return {
          isValid: false,
          errorMessage: `Required fields missing: ${missingFields.join(", ")}`
        };
      }
      
      // PAN of donee should be different from the PAN of assessee
      if (hasPanOfDonee) {
        const assesseePan = localStorage.getItem('PAN_NO');
        if (assesseePan && hasPanOfDonee.toUpperCase() === assesseePan.toUpperCase()) {
          return {
            isValid: false,
            errorMessage: "PAN of donee should be different from the PAN of assessee"
          };
        }
      }
    }
    
    
    return { isValid: true, errorMessage: "" };
  };

  // Helper function to check 80G validation status
  const get80GValidationStatus = (entry) => {
    // Always validate 80G entries regardless of active tab during save
    // if (activeTab !== "80G") return { isValid: true, errorMessage: "" };
    
    console.log('get80GValidationStatus called with entry:', entry);
    
    const hasDonationInCash = Number.parseFloat(entry.donationInCash || 0) > 0;
    const hasDonationInOtherMode = Number.parseFloat(entry.donationInOtherMode || 0) > 0;
    const hasDonationType = entry.donationType && entry.donationType.trim();
    const hasPanOfDonee = entry.panOfDonee && entry.panOfDonee.trim();
    const hasNameOfDonee = entry.nameOfDonee && entry.nameOfDonee.trim();
    const hasAddressOfDonee = entry.addressOfDonee && entry.addressOfDonee.trim();
    const hasCityTownDistrict = entry.cityTownDistrict && entry.cityTownDistrict.trim();
    const hasStateCode = entry.stateCode && entry.stateCode.trim();
    const hasPinCode = entry.pinCode && entry.pinCode.trim();
    
    // Check if any field has data (user is actively filling the form)
    const hasAnyData = hasDonationInCash || hasDonationInOtherMode || hasDonationType || 
                      hasPanOfDonee || hasNameOfDonee || hasAddressOfDonee || 
                      hasCityTownDistrict || hasStateCode || hasPinCode;
    
    // No validation if form is empty
    if (!hasAnyData) {
      return { isValid: true, errorMessage: "" };
    }
    
    // Either cash or other mode donation required
    if (!hasDonationInCash && !hasDonationInOtherMode) {
      console.log('80G validation result: INVALID - No donation amount');
      return {
        isValid: false,
        errorMessage: "Either donation in cash or donation in other mode is required"
      };
    }
    
    // All other fields become mandatory when schedule is filled
    if (!hasDonationType) {
      console.log('80G validation result: INVALID - Missing donation type');
      return {
        isValid: false,
        errorMessage: "Donation type is required"
      };
    }
    
    if (!hasPanOfDonee) {
      return {
        isValid: false,
        errorMessage: "PAN of donee is required"
      };
    }
    
    // PAN of donee should be different from the PAN of assessee
    if (hasPanOfDonee) {
      const assesseePan = localStorage.getItem('PAN_NO');
      if (assesseePan && hasPanOfDonee.toUpperCase() === assesseePan.toUpperCase()) {
        return {
          isValid: false,
          errorMessage: "PAN of donee should be different from the PAN of assessee"
        };
      }
    }
    
    if (!hasNameOfDonee) {
      return {
        isValid: false,
        errorMessage: "Name of donee is required"
      };
    }
    
    if (!hasAddressOfDonee) {
      return {
        isValid: false,
        errorMessage: "Address of donee is required"
      };
    }
    
    if (!hasCityTownDistrict) {
      return {
        isValid: false,
        errorMessage: "City/Town/District is required"
      };
    }
    
    if (!hasStateCode) {
      return {
        isValid: false,
        errorMessage: "State is required"
      };
    }
    
    if (!hasPinCode) {
      return {
        isValid: false,
        errorMessage: "PIN code is required"
      };
    }
    
    console.log('80G validation result: VALID');
    return { isValid: true, errorMessage: "" };
  };

  // Helper function to check if there are any 80G validation errors
  const has80GValidationErrors = () => {
    // Always check 80G validation regardless of active tab
    // if (activeTab !== "80G") return false;
    
    const currentEntries = deductionData["80G"] || [];
    return currentEntries.some(entry => {
      const validationStatus = get80GValidationStatus(entry);
      return !validationStatus.isValid;
    });
  };

  // Helper function to check if there are any validation errors across all deduction tabs
  const hasAnyValidationErrors = () => {
    // Check 80G validation errors
    if (has80GValidationErrors()) {
      return true;
    }

    // Check 80GGA validation errors
    const eightyGGAEntries = deductionData.deductionDonations?.filter(entry => entry.sectionOfDonation === '80GGA') || [];
    const has80GGAErrors = eightyGGAEntries.some(entry => {
      const validationStatus = get80GGAValidationStatus(entry);
      return !validationStatus.isValid;
    });
    if (has80GGAErrors) {
      return true;
    }

    // Check 80GGC validation errors
    const eightyGGCEntries = deductionData.deduction80GGCs || [];
    const has80GGCErrors = eightyGGCEntries.some(entry => {
      const validationStatus = get80GGCValidationStatus(entry);
      return !validationStatus.isValid;
    });
    if (has80GGCErrors) {
      return true;
    }

    // Check 80DD validation errors
    const eightyDDEntries = deductionData.disablementDeductions?.filter(entry => entry.section === '80DD') || [];
    const has80DDErrors = eightyDDEntries.some(entry => {
      const validationStatus = get80DDValidationStatus(entry);
      return !validationStatus.isValid;
    });
    if (has80DDErrors) {
      return true;
    }

    // Check 80U validation errors
    const eightyUEntries = deductionData.disablementDeductions?.filter(entry => entry.section === '80U') || [];
    const has80UErrors = eightyUEntries.some(entry => {
      const validationStatus = get80UValidationStatus(entry);
      return !validationStatus.isValid;
    });
    if (has80UErrors) {
      return true;
    }

    // Check 80GG validation errors
    const eightyGGEntries = deductionData.deduction80GGs || [];
    const has80GGErrors = eightyGGEntries.some(entry => {
      const validationStatus = get80GGValidationStatus(entry);
      return !validationStatus.isValid;
    });
    if (has80GGErrors) {
      return true;
    }

    return false;
  };

  const isSeniorCitizen = calculateAge(dob) >= 60;
  // Show loading state
  // Loading skeleton component for better perceived performance
  // LoadingSkeleton component moved to before early returns to fix hoisting issue

  // Early returns moved to after all hooks are declared to follow Rules of Hooks

  // ..................................................
  const validateParentsMedicalExpenses = () => {
    const healthInsuranceTotal = forParentEntries.reduce((sum, entry) => sum + Number(entry.amount || 0), 0);
    const totalExpenses = medicalExpenditureParents + preventiveHealthCheckUpParents + healthInsuranceTotal;

    const limit = parentsSeniorCitizenFlag === 'Y' ? 50000 : 25000;

    if (totalExpenses > limit) {
      setParentsMedicalExpenseError(`Total medical expenses for parents cannot exceed ₹${limit.toLocaleString('en-IN')}`);
      toast.error(`Total medical expenses for parents cannot exceed ₹${limit.toLocaleString('en-IN')}`);
      return false;
    }

    setParentsMedicalExpenseError('');
    return true;
  };



  const validateMedicalExpenses = () => {
    const healthInsuranceTotal = forSelfEntries.reduce((sum, entry) => sum + Number(entry.amount || 0), 0);
    const totalExpenses = medicalExpSelfFamily + preventiveHealthCheckUpSelfFamily + healthInsuranceTotal;

    const limit = seniorCitizenFlagSelfFamily === 'Y' ? 50000 : 25000;

    if (totalExpenses > limit) {
      setMedicalExpenseError(`Total medical expenses cannot exceed ₹${limit.toLocaleString('en-IN')}`);
      toast.error(`Total medical expenses cannot exceed ₹${limit.toLocaleString('en-IN')}`);
      return false;
    }

    setMedicalExpenseError('');
    return true;
  };







  const renderForm = () => {
    const currentEntries = getCurrentEntries()
    switch (activeTab) {
      case "80D":
        const eightyDValidationStatus = get80DValidationStatus();
        return (
          <div className="space-y-8">
            {/* For Self, Spouse and Dependent */}
            <div className={`border rounded-lg p-6 ${
              activeTab === "80D" && !eightyDValidationStatus.isValid 
                ? 'border-red-300 bg-red-50' 
                : 'border-gray-200'
            }`}>
              <h3 className="text-lg font-semibold text-gray-800 mb-4">For Self, Spouse and Dependent</h3>

              {/* Senior Citizen Selection */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Whether you or any of your family member (excluding parents) is a senior citizen?
                  </label>
                  <div className="relative">
                    <select
                      className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-cyan-500 appearance-none ${seniorCitizenFlagSelfFamily === 'NA' ? 'bg-gray-100 border-gray-300 text-gray-500' : 'bg-white border-gray-300'
                        }`}
                      value={seniorCitizenFlagSelfFamily}
                      onChange={(e) => {
                        setSeniorCitizenFlagSelfFamily(e.target.value);
                        if (e.target.value === 'NA') {
                          setMedicalExpSelfFamily(0);
                          setPreventiveHealthCheckUpSelfFamily(0);
                          setForSelfEntries([{ id: Date.now(), company: '', policyNo: '', amount: 0 }]);
                        }
                        else {
                          setMedicalExpSelfFamily(0);
                          setForSelfEntries([{ id: Date.now(), company: '', policyNo: '', amount: 0 }]);
                        }
                      }}
                    >
                      <option value="">Select</option>
                      <option value="Y">Yes</option>
                      <option value="N">No</option>
                      <option value="S">Not Claiming</option>
                    </select>
                    <ChevronDown className="absolute right-3 top-3 h-4 w-4 text-gray-400" />
                  </div>
                </div>
              </div>

              {/* Medical, Preventive and Insurance Fields */}
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6">
                {/* Medical Expenditure */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Medical Expenditure
                    {seniorCitizenFlagSelfFamily !== 'Y' && (
                      <span className="block text-xs text-gray-500 mt-1">Available only for senior citizens</span>
                    )}
                    {healthInsurancePremiumSelf > 0 && (
                      <span className="block text-xs text-gray-500 mt-1">Disabled when insurance premium is entered</span>
                    )}
                  </label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">₹</span>
                    <input
                      type="text"
                      placeholder="0.00"
                      value={medicalExpSelfFamily}
                      onChange={(e) => {
                        // Only allow numbers (0-9) and decimal point
                        const value = e.target.value.replace(/[^0-9.]/g, '');
                        // Prevent multiple decimal points
                        const parts = value.split('.');
                        const filteredValue = parts.length > 2 ? parts[0] + '.' + parts.slice(1).join('') : value;
                        const numericValue = Math.max(0, Number.parseFloat(filteredValue || 0));                         setMedicalExpSelfFamily(numericValue);
                        if (numericValue > 0) {
                          setForSelfEntries([{ id: Date.now(), company: '', policyNo: '', amount: 0 }]);
                        }
                        // Removed validateMedicalExpenses() to allow any amount entry
                      }}
                      className={`w-full pl-8 pr-3 py-2 text-right border rounded-md focus:outline-none focus:ring-2 focus:ring-cyan-500 ${seniorCitizenFlagSelfFamily !== 'Y' || healthInsurancePremiumSelf > 0 || seniorCitizenFlagSelfFamily === 'NA'
                        ? 'bg-gray-100 text-gray-500'
                        : 'bg-white border-gray-300'
                        }`}
                      disabled={seniorCitizenFlagSelfFamily !== 'Y' || healthInsurancePremiumSelf > 0 || seniorCitizenFlagSelfFamily === 'NA'}
                    />
                  </div>
                </div>

                {/* Preventive Health Check Up */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Preventive Health Check Up
                    <span className="block text-xs text-gray-500 mt-1">(Max deduction: ₹5,000)</span>
                  </label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">₹</span>
                    <input
                      type="text"
                      placeholder="0.00"
                      value={preventiveHealthCheckUpSelfFamily}
                      onChange={(e) => {
                        // Only allow numbers (0-9) and decimal point
                        const value = e.target.value.replace(/[^0-9.]/g, '');
                        // Prevent multiple decimal points
                        const parts = value.split('.');
                        const filteredValue = parts.length > 2 ? parts[0] + '.' + parts.slice(1).join('') : value;
                        let numericValue = Math.max(0, Number.parseFloat(filteredValue || 0));                         // Removed 5000 limit restriction to allow any amount entry
                        setPreventiveHealthCheckUpSelfFamily(numericValue);
                        // Removed validateMedicalExpenses() to allow any amount entry
                      }}
                      className={`w-full pl-8 pr-3 py-2 text-right border rounded-md focus:outline-none focus:ring-2 focus:ring-cyan-500 ${seniorCitizenFlagSelfFamily === 'NA' ? 'bg-gray-100 text-gray-500' : 'bg-white border-gray-300'
                        }`}
                      disabled={seniorCitizenFlagSelfFamily === 'NA'}
                    />
                  </div>
                </div>

                {/* Health Insurance Premium (calculated) */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Health Insurance Premium
                    {medicalExpSelfFamily > 0 && (
                      <span className="block text-xs text-gray-500 mt-1">Disabled when medical expenditure is entered</span>
                    )}
                  </label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">₹</span>
                    <input
                      type="text"
                      placeholder="0.00"
                      value={forSelfEntries.reduce((sum, entry) => sum + Number.parseFloat(entry.amount || 0), 0)}
                      readOnly
                      className="w-full pl-8 pr-3 py-2 text-right border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-cyan-500 bg-gray-100"
                    />
                  </div>
                </div>
              </div>

              {/* Insurance Policy Details */}
              {forSelfEntries.map((entry, index) => (
                <div key={entry.id} className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-4">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Name of Insurance Company</label>
                    <input
                      type="text"
                      placeholder="Enter Company Name"
                      value={entry.company || ""}
                      onChange={(e) => handleChange(entry.id, "company", e.target.value)}
                      className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-cyan-500 ${medicalExpSelfFamily > 0 || seniorCitizenFlagSelfFamily === 'NA'
                        ? 'bg-gray-100 text-gray-500'
                        : 'bg-white border-gray-300'
                        }`}
                      disabled={medicalExpSelfFamily > 0 || seniorCitizenFlagSelfFamily === 'NA'}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Policy No.</label>
                    <input
                      type="text"
                      placeholder="Enter Policy Number"
                      value={entry.policyNo || ""}
                      onChange={(e) => handleChange(entry.id, "policyNo", e.target.value)}
                      className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-cyan-500 ${medicalExpSelfFamily > 0 || seniorCitizenFlagSelfFamily === 'NA'
                        ? 'bg-gray-100 text-gray-500'
                        : 'bg-white border-gray-300'
                        }`}
                      disabled={medicalExpSelfFamily > 0 || seniorCitizenFlagSelfFamily === 'NA'}
                    />
                  </div>
                  <div className="flex items-end">
                    <div className="flex-1">
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Amount</label>
                      <div className="relative">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">₹</span>
                        <input
                          type="text"
                          placeholder="0.00"
                          value={entry.amount}
                          onChange={(e) => {
                            // Only allow numbers (0-9) and decimal point
                            const value = e.target.value.replace(/[^0-9.]/g, '');
                            // Prevent multiple decimal points
                            const parts = value.split('.');
                            const filteredValue = parts.length > 2 ? parts[0] + '.' + parts.slice(1).join('') : value;
                            const numericValue = Math.max(0, Number.parseFloat(filteredValue || 0));                             handleChange(entry.id, "amount", numericValue);
                            if (numericValue > 0) {
                              setMedicalExpSelfFamily(0);
                            }
                            // Removed validateMedicalExpenses() to allow any amount entry
                          }}
                          className={`w-full pl-8 pr-3 py-2 text-right border rounded-md focus:outline-none focus:ring-2 focus:ring-cyan-500 ${medicalExpSelfFamily > 0 || seniorCitizenFlagSelfFamily === 'NA'
                            ? 'bg-gray-100 text-gray-500'
                            : 'bg-white border-gray-300'
                            }`}
                          disabled={medicalExpSelfFamily > 0 || seniorCitizenFlagSelfFamily === 'NA'}
                        />
                      </div>
                    </div>
                    <div className="ml-2 flex space-x-1">
                      <button
                        type="button"
                        onClick={() => {
                          if (medicalExpSelfFamily === 0 && seniorCitizenFlagSelfFamily !== 'NA') {
                            handleAddEntry();
                          }
                        }}
                        className={`w-8 h-8 rounded flex items-center justify-center transition-colors ${medicalExpSelfFamily > 0 || seniorCitizenFlagSelfFamily === 'NA'
                          ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                          : 'bg-green-500 text-white hover:bg-green-600'
                          }`}
                        disabled={medicalExpSelfFamily > 0 || seniorCitizenFlagSelfFamily === 'NA'}
                      >
                        <Plus className="h-4 w-4" />
                      </button>
                      {forSelfEntries.length > 1 && (
                        <button
                          type="button"
                          onClick={() => {
                            if (medicalExpSelfFamily === 0 && seniorCitizenFlagSelfFamily !== 'NA') {
                              handleRemoveEntry(entry.id, entry);
                            }
                          }}
                          className={`w-8 h-8 rounded flex items-center justify-center transition-colors ${medicalExpSelfFamily > 0 || seniorCitizenFlagSelfFamily === 'NA'
                            ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                            : 'bg-red-500 text-white hover:bg-red-600'
                            }`}
                          disabled={medicalExpSelfFamily > 0 || seniorCitizenFlagSelfFamily === 'NA'}
                        >
                          <Minus className="h-4 w-4" />
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
            {/* For Parents */}
            <div className={`border rounded-lg p-6 ${
              activeTab === "80D" && !eightyDValidationStatus.isValid 
                ? 'border-red-300 bg-red-50' 
                : 'border-gray-200'
            }`}>
              <h3 className="text-lg font-semibold text-gray-800 mb-4">For Parents</h3>

              {/* Senior Citizen Selection */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Whether your parents are senior citizens?
                  </label>
                  <div className="relative">
                    <select
                      className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-cyan-500 appearance-none ${parentsSeniorCitizenFlag === 'NA'
                        ? 'bg-gray-100 border-gray-300 text-gray-500'
                        : 'bg-white border-gray-300'
                        }`}
                      value={parentsSeniorCitizenFlag}
                      onChange={(e) => {
                        setParentsSeniorCitizenFlag(e.target.value);
                        if (e.target.value === 'NA') {
                          setMedicalExpenditureParents(0);
                          setPreventiveHealthCheckUpParents(0);
                          setForParentEntries([
                            { id: Date.now(), company: '', policyNo: '', amount: 0 },
                          ]);
                        }
                        else {
                          setMedicalExpenditureParents(0);
                          setPreventiveHealthCheckUpParents(0);
                        }
                      }}
                    >
                      <option value="">Select</option>
                      <option value="Y">Yes</option>
                      <option value="N">No</option>
                      <option value="S">Not Claiming</option>
                    </select>
                    <ChevronDown className="absolute right-3 top-3 h-4 w-4 text-gray-400" />
                  </div>
                </div>
              </div>

              {/* Medical, Preventive and Insurance Fields */}
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6">
                {/* Medical Expenditure */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Medical Expenditure
                    {parentsSeniorCitizenFlag !== 'Y' && (
                      <span className="block text-xs text-gray-500 mt-1">
                        Available only for senior citizens
                      </span>
                    )}
                    {healthInsurancePremiumParents > 0 && (
                      <span className="block text-xs text-gray-500 mt-1">
                        Disabled when insurance premium is entered
                      </span>
                    )}
                  </label>
                  <input
                    type="text"
                    placeholder="0.00"
                    value={medicalExpenditureParents}
                                          onChange={(e) => {
                        // Only allow numbers (0-9) and decimal point
                        const value = e.target.value.replace(/[^0-9.]/g, '');
                        // Prevent multiple decimal points
                        const parts = value.split('.');
                        const filteredValue = parts.length > 2 ? parts[0] + '.' + parts.slice(1).join('') : value;
                        const numericValue = Math.max(0, Number.parseFloat(filteredValue || 0));                         setMedicalExpenditureParents(numericValue);
                        if (numericValue > 0) {
                          setForParentEntries([
                            { id: Date.now(), company: '', policyNo: '', amount: 0 },
                          ]);
                        }
                        // Removed validateParentsMedicalExpenses() to allow any amount entry
                      }}
                    className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-cyan-500 ${parentsSeniorCitizenFlag !== 'Y' ||
                      healthInsurancePremiumParents > 0 ||
                      parentsSeniorCitizenFlag === 'NA'
                      ? 'bg-gray-100 text-gray-500'
                      : 'bg-white border-gray-300'
                      }`}
                    disabled={
                      parentsSeniorCitizenFlag !== 'Y' ||
                      healthInsurancePremiumParents > 0 ||
                      parentsSeniorCitizenFlag === 'NA'
                    }
                  />
                </div>

                {/* Preventive Health Check Up */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Preventive Health Check Up
                    <span className="block text-xs text-gray-500 mt-1">
                      (Max deduction: ₹5,000)
                    </span>
                  </label>
                  <input
                    type="text"
                    placeholder="0.00"
                    value={preventiveHealthCheckUpParents}
                                          onChange={(e) => {
                        // Only allow numbers (0-9) and decimal point
                        const value = e.target.value.replace(/[^0-9.]/g, '');
                        // Prevent multiple decimal points
                        const parts = value.split('.');
                        const filteredValue = parts.length > 2 ? parts[0] + '.' + parts.slice(1).join('') : value;
                        let numericValue = Math.max(0, Number.parseFloat(filteredValue || 0));                         // Removed 5000 limit restriction to allow any amount entry
                        setPreventiveHealthCheckUpParents(numericValue);
                        // Removed validateParentsMedicalExpenses() to allow any amount entry
                      }}
                    className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-cyan-500 ${parentsSeniorCitizenFlag === 'NA'
                      ? 'bg-gray-100 text-gray-500'
                      : 'bg-white border-gray-300'
                      }`}
                    disabled={parentsSeniorCitizenFlag === 'NA'}
                  />
                </div>

                {/* Health Insurance Premium (calculated) */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Health Insurance Premium
                    {medicalExpenditureParents > 0 && (
                      <span className="block text-xs text-gray-500 mt-1">
                        Disabled when medical expenditure is entered
                      </span>
                    )}
                  </label>
                  <input
                    type="text"
                    placeholder="0.00"
                    value={forParentEntries.reduce(
                      (sum, entry) => sum + Number.parseFloat(entry.amount || 0),
                      0
                    )}
                    readOnly
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-cyan-500 bg-gray-100"
                  />
                </div>
              </div>

              {/* Insurance Policy Details */}
              {forParentEntries.map((entry) => (
                <div key={entry.id} className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-4">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Name of Insurance Company
                    </label>
                    <input
                      type="text"
                      placeholder="Enter Company Name"
                      value={entry.company || ''}
                      onChange={(e) =>
                        handleChangeForParents(entry.id, 'company', e.target.value)
                      }
                      className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-cyan-500 ${medicalExpenditureParents > 0 ||
                        parentsSeniorCitizenFlag === 'NA'
                        ? 'bg-gray-100 text-gray-500'
                        : 'bg-white border-gray-300'
                        }`}
                      disabled={
                        medicalExpenditureParents > 0 || parentsSeniorCitizenFlag === 'NA'
                      }
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Policy No.
                    </label>
                    <input
                      type="text"
                      placeholder="Enter Policy Number"
                      value={entry.policyNo || ''}
                      onChange={(e) =>
                        handleChangeForParents(entry.id, 'policyNo', e.target.value)
                      }
                      className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-cyan-500 ${medicalExpenditureParents > 0 ||
                        parentsSeniorCitizenFlag === 'NA'
                        ? 'bg-gray-100 text-gray-500'
                        : 'bg-white border-gray-300'
                        }`}
                      disabled={
                        medicalExpenditureParents > 0 || parentsSeniorCitizenFlag === 'NA'
                      }
                    />
                  </div>
                  <div className="flex items-end">
                    <div className="flex-1">
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Amount
                      </label>
                      <input
                        type="text"
                        placeholder="0.00"
                        value={entry.amount}
                                                  onChange={(e) => {
                            // Only allow numbers (0-9) and decimal point
                            const value = e.target.value.replace(/[^0-9.]/g, '');
                            // Prevent multiple decimal points
                            const parts = value.split('.');
                            const filteredValue = parts.length > 2 ? parts[0] + '.' + parts.slice(1).join('') : value;
                            const numericValue = Math.max(0, Number.parseFloat(filteredValue || 0));                             handleChangeForParents(entry.id, 'amount', numericValue);
                            if (numericValue > 0) setMedicalExpenditureParents(0);
                            // Removed validateParentsMedicalExpenses() to allow any amount entry
                          }}
                        className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-cyan-500 ${medicalExpenditureParents > 0 ||
                          parentsSeniorCitizenFlag === 'NA'
                          ? 'bg-gray-100 text-gray-500'
                          : 'bg-white border-gray-300'
                          }`}
                        disabled={
                          medicalExpenditureParents > 0 || parentsSeniorCitizenFlag === 'NA'
                        }
                      />
                    </div>
                    <div className="ml-2 flex space-x-1">
                      <button
                        type="button"
                        onClick={() => {
                          if (
                            medicalExpenditureParents === 0 &&
                            parentsSeniorCitizenFlag !== 'NA'
                          ) {
                            handleAddEntryForParents();
                          }
                        }}
                        className={`w-8 h-8 rounded flex items-center justify-center transition-colors ${medicalExpenditureParents > 0 ||
                          parentsSeniorCitizenFlag === 'NA'
                          ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                          : 'bg-green-500 text-white hover:bg-green-600'
                          }`}
                        disabled={
                          medicalExpenditureParents > 0 || parentsSeniorCitizenFlag === 'NA'
                        }
                      >
                        <Plus className="h-4 w-4" />
                      </button>
                      {forParentEntries.length > 1 && (
                        <button
                          type="button"
                          onClick={() => {
                            if (
                              medicalExpenditureParents === 0 &&
                              parentsSeniorCitizenFlag !== 'NA'
                            ) {
                              handleRemoveEntryForParents(entry.id, entry);
                            }
                          }}
                          className={`w-8 h-8 rounded flex items-center justify-center transition-colors ${medicalExpenditureParents > 0 ||
                            parentsSeniorCitizenFlag === 'NA'
                            ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                            : 'bg-red-500 text-white hover:bg-red-600'
                            }`}
                          disabled={
                            medicalExpenditureParents > 0 ||
                            parentsSeniorCitizenFlag === 'NA'
                          }
                        >
                          <Minus className="h-4 w-4" />
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )
      case "80G":
        return (
          <div className="space-y-6">
            <div className="bg-blue-50 p-4 rounded-lg">
              <h3 className="text-lg font-semibold text-blue-800 mb-2">80G - Donations to Certain Funds, Charitable Institutions, etc.</h3>
              <p className="text-sm text-blue-600">Enter your donation details below.</p>
            </div>

            {currentEntries.map((entry, index) => {
              const validationStatus = get80GValidationStatus(entry);
              return (
                <motion.div
                  key={entry.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className={`border rounded-lg p-4 space-y-4 relative ${
                    activeTab === "80G" && !validationStatus.isValid 
                      ? 'border-red-300 bg-red-50' 
                      : 'border-gray-200'
                  }`}
                >
                  <h3 className="absolute top-2 left-4 text-sm font-semibold text-gray-600">Donation {index + 1}</h3>
                  {currentEntries.length > 1 && (
                    <button
                      onClick={() => removeEntry(entry.id, entry)}
                      className="absolute top-2 right-2 w-8 h-8 bg-red-500 text-white rounded-full flex items-center justify-center hover:bg-red-600 transition-colors"
                    >
                      <Minus className="h-4 w-4" />
                    </button>
                  )}
                  
                  {/* Validation Error Message */}
                  {activeTab === "80G" && !validationStatus.isValid && (
                    <div className="bg-red-100 border border-red-300 text-red-700 px-3 py-2 rounded-md text-sm">
                      {validationStatus.errorMessage}
                    </div>
                  )}

                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Donation Type</label>
                        <select
                        className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 ${
                          activeTab === "80G" && !validationStatus.isValid 
                            ? 'border-red-500 focus:ring-red-500' 
                            : 'border-gray-300 focus:ring-cyan-500'
                        }`}
                          value={entry.donationType || ""}
                        onChange={(e) => {
                          updateEntry(entry.id, "donationType", e.target.value);
                          // Trigger recalculation by updating eligible deduction
                          const updatedEntry = { ...entry, donationType: e.target.value };
                          const calculatedEligible = calculate80GEligibleDeduction(updatedEntry);
                          updateEntry(entry.id, "eligibleDeduction", calculatedEligible);
                        }}
                        >
                          <option value="">Select Donation Type</option>
                        <option value="Don100Percent">100% deduction without qualifying limit</option>
                        <option value="Don50PercentNoApprReqd">50% deduction without qualifying limit</option>
                        <option value="Don100PercentApprReqd">100% deduction subject to qualifying limit</option>
                        <option value="Don50PercentApprReqd">50% deduction subject to qualifying limit</option>
                        </select>
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">PAN of Donee</label>
                      <input
                        type="text"
                        value={entry.panOfDonee || ""}
                        onChange={(e) => updateEntry(entry.id, "panOfDonee", e.target.value)}
                        className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 ${
                          activeTab === "80G" && !validationStatus.isValid 
                            ? 'border-red-500 focus:ring-red-500' 
                            : 'border-gray-300 focus:ring-cyan-500'
                        }`}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Name of Donee</label>
                      <input
                        type="text"
                        value={entry.nameOfDonee || ""}
                        onChange={(e) => updateEntry(entry.id, "nameOfDonee", e.target.value)}
                        className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 ${
                          activeTab === "80G" && !validationStatus.isValid 
                            ? 'border-red-500 focus:ring-red-500' 
                            : 'border-gray-300 focus:ring-cyan-500'
                        }`}
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Address</label>
                      <input
                        type="text"
                        value={entry.addressOfDonee || ""}
                        onChange={(e) => updateEntry(entry.id, "addressOfDonee", e.target.value)}
                        className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 ${
                          activeTab === "80G" && !validationStatus.isValid 
                            ? 'border-red-500 focus:ring-red-500' 
                            : 'border-gray-300 focus:ring-cyan-500'
                        }`}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">City/Town/District</label>
                      <input
                        type="text"
                        value={entry.cityTownDistrict || ""}
                        onChange={(e) => updateEntry(entry.id, "cityTownDistrict", e.target.value)}
                        className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 ${
                          activeTab === "80G" && !validationStatus.isValid 
                            ? 'border-red-500 focus:ring-red-500' 
                            : 'border-gray-300 focus:ring-cyan-500'
                        }`}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">State</label>
                      <select
                        value={entry.stateCode || ""}
                        onChange={(e) => updateEntry(entry.id, "stateCode", e.target.value)}
                        className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 ${
                          activeTab === "80G" && !validationStatus.isValid 
                            ? 'border-red-500 focus:ring-red-500' 
                            : 'border-gray-300 focus:ring-cyan-500'
                        }`}
                      >
                        <option value="">Select State</option>
                        {statesList?.map((state) => (
                          <option key={state.codeValue} value={state.codeValue}>
                            {state.description}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Pin Code</label>
                      <input
                        type="text"
                        value={entry.pinCode || ""}
                        onChange={(e) => updateEntry(entry.id, "pinCode", e.target.value)}
                        className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 ${
                          activeTab === "80G" && !validationStatus.isValid 
                            ? 'border-red-500 focus:ring-red-500' 
                            : 'border-gray-300 focus:ring-cyan-500'
                        }`}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Donation in Cash (₹)</label>
                        <input
                          type="number"
                          placeholder="0.00"
                        value={entry.donationInCash || ""}
                                                onChange={(e) => {
                          updateEntry(entry.id, "donationInCash", e.target.value);
                          // Trigger recalculation by updating eligible deduction
                          const updatedEntry = { ...entry, donationInCash: e.target.value };
                            const calculatedEligible = calculate80GEligibleDeduction(updatedEntry);
                            updateEntry(entry.id, "eligibleDeduction", calculatedEligible);
                        }}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-cyan-500"
                        />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Donation in Other Mode (₹)</label>
                        <input
                          type="number"
                          placeholder="0.00"
                        value={entry.donationInOtherMode || ""}
                          onChange={(e) => {
                          updateEntry(entry.id, "donationInOtherMode", e.target.value);
                          // Trigger recalculation by updating eligible deduction
                          const updatedEntry = { ...entry, donationInOtherMode: e.target.value };
                            const calculatedEligible = calculate80GEligibleDeduction(updatedEntry);
                            updateEntry(entry.id, "eligibleDeduction", calculatedEligible);
                        }}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-cyan-500"
                        />
                      </div>
                    </div>

                  {/* Total Amount and Eligible Deduction Summary for 80G */}
                  <div className="mt-4 bg-green-50 border border-green-200 rounded-md p-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
                      <div className="text-center">
                        <span className="text-sm font-medium text-gray-700">Total Donation</span>
                        <div className="flex items-center justify-center mt-1">
                          <span className="text-gray-500 mr-1">₹</span>
                          <span className="text-lg font-semibold text-gray-900">
                            {formatCurrency((Number.parseFloat(entry.donationInCash || 0) + Number.parseFloat(entry.donationInOtherMode || 0)))}
                          </span>
                        </div>
                      </div>
                      <div className="text-center">
                        <span className="text-sm font-medium text-gray-700">Eligible Cash</span>
                        <div className="flex items-center justify-center mt-1">
                          <span className="text-gray-500 mr-1">₹</span>
                          <span className={`text-lg font-semibold ${(Number.parseFloat(entry.donationInCash || 0) > 2000) ? 'text-red-600' : 'text-blue-600'}`}>
                            {formatCurrency((Number.parseFloat(entry.donationInCash || 0) > 2000) ? 0 : Number.parseFloat(entry.donationInCash || 0))}
                          </span>
                        </div>
                        {(Number.parseFloat(entry.donationInCash || 0) > 2000) && (
                          <p className="text-xs text-red-600 mt-1">Cash &gt; ₹2,000 not eligible</p>
                        )}
                      </div>
                      <div className="text-center">
                        <span className="text-sm font-medium text-gray-700">Other Mode</span>
                        <div className="flex items-center justify-center mt-1">
                          <span className="text-gray-500 mr-1">₹</span>
                          <span className="text-lg font-semibold text-green-600">
                            {formatCurrency(Number.parseFloat(entry.donationInOtherMode || 0))}
                          </span>
                        </div>
                      </div>
                      {/* Qualifying Limit Information */}
                      {(entry.donationType?.includes("ApprReqd")) && (
                        <div className="text-center">
                          <span className="text-sm font-medium text-gray-700">Qualifying Limit</span>
                          <div className="flex items-center justify-center mt-1">
                            <span className="text-gray-500 mr-1">₹</span>
                            <span className="text-lg font-semibold text-orange-600">
                              {formatCurrency(calculate80GQualifyingLimit(entry.donationType) || 0)}
                            </span>
                          </div>
                          <p className="text-xs text-gray-600 mt-1">
                            {entry.donationType === "Don50PercentApprReqd" ? "5%" : "10%"} of Adjusted GTI
                          </p>
                        </div>
                      )}
                      <div className="text-center">
                        <span className="text-sm font-medium text-gray-700">Eligible Deduction</span>
                        <div className="flex items-center justify-center mt-1">
                          <span className="text-gray-500 mr-1">₹</span>
                          <span className="text-lg font-semibold text-purple-600">
                            {formatCurrency(calculate80GEligibleDeduction(entry))}
                          </span>
                        </div>
                        {entry.donationType && (
                          <p className="text-xs text-gray-600 mt-1">
                            {entry.donationType.includes('100') ? '100%' : entry.donationType.includes('50') ? '50%' : '0%'} deduction
                            {entry.donationType?.includes("ApprReqd") && " (capped)"}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Adjusted GTI Breakdown for Qualifying Limit Types - HIDDEN */}
                  {false && entry.donationType?.includes("ApprReqd") && (
                    (() => {
                      const grossTotalIncome = oldRegimeData ? 
                        Number(oldRegimeData.incomeFromSalary || 0) + 
                        Number(oldRegimeData.incomeFromHP || 0) + 
                        Number(oldRegimeData.incomeFromOtherSources || 0) : 0;
                      
                      // Calculate all deduction amounts
                      const section80CFamily = [
                        ...(deductionData.deduction80Cs || []),
                        ...(deductionData.deduction80CCFamilies || []).filter(familyEntry =>
                          ['80CCC', '80CCD1', '80CCD1B'].includes(familyEntry.section)
                        )
                      ];
                      const total80CFamilyAmount = section80CFamily.reduce((sum, familyEntry) => sum + (familyEntry.amount || 0), 0);
                      const eligible80CFamily = Math.min(total80CFamilyAmount, 150000);
                      
                      const total80CCD2Eligible = (deductionData.deduction80CCFamilies || [])
                        .filter(familyEntry => familyEntry.section === '80CCD2')
                        .reduce((sum, familyEntry) => sum + (familyEntry.amount || 0), 0);
                      
                      const total80TTAAmount = (deductionData.deductionDepositInterests || [])
                        .filter(interestEntry => interestEntry.sectionOfInterest === '80TTA')
                        .reduce((sum, interestEntry) => sum + (interestEntry.interestPaid || 0), 0);
                      const eligible80TTA = Math.min(total80TTAAmount, 10000);
                      
                      const total80TTBAmount = (deductionData.deductionDepositInterests || [])
                        .filter(interestEntry => interestEntry.sectionOfInterest === '80TTB')
                        .reduce((sum, interestEntry) => sum + (interestEntry.interestPaid || 0), 0);
                      const eligible80TTB = Math.min(total80TTBAmount, 50000);
                      
                      const total80GGAmount = (deductionData.deduction80GGs || [])
                        .reduce((sum, ggEntry) => sum + (ggEntry.amountOfRentPaid || 0), 0);
                      const eligible80GG = Math.min(total80GGAmount, 60000);
                      
                      // Calculate 80D eligible deduction (same logic as in main function)
                      let eligible80D = 0;
                      if (deductionData.deduction80D) {
                        const selfLimit = seniorCitizenFlagSelfFamily === 'Y' ? 50000 : 25000;
                        const parentsLimit = parentsSeniorCitizenFlag === 'Y' ? 50000 : 25000;

                        const selfTotal = Math.min(preventiveHealthCheckUpSelfFamily || 0, 5000) + 
                          (medicalExpSelfFamily > 0 ? medicalExpSelfFamily : 
                           (forSelfEntries || []).reduce((sum, entry) => sum + (entry.amount || 0), 0));

                        const parentsTotal = Math.min(preventiveHealthCheckUpParents || 0, 5000) + 
                          (medicalExpenditureParents > 0 ? medicalExpenditureParents : 
                           (forParentEntries || []).reduce((sum, entry) => sum + (entry.amount || 0), 0));

                        eligible80D = Math.min(selfTotal, selfLimit) + Math.min(parentsTotal, parentsLimit);
                      }
                      
                      // Calculate 80DDB eligible deduction
                      const total80DDBAmount = (deductionData.deduction80DDBs || [])
                        .reduce((sum, ddbEntry) => sum + (ddbEntry.expenditureIncurred || 0), 0);
                      const eligible80DDB = Math.min(total80DDBAmount, 100000);
                      
                      // Calculate loan deductions
                      const total80EAmount = (deductionData.deductionLoans || [])
                        .filter(loanEntry => loanEntry.sectionOfLoan === '80E')
                        .reduce((sum, loanEntry) => sum + (loanEntry.interestPayableForTheYear || 0), 0);
                      const eligible80E = total80EAmount; // No limit for 80E
                      
                      const total80EEAmount = (deductionData.deductionLoans || [])
                        .filter(loanEntry => loanEntry.sectionOfLoan === '80EE')
                        .reduce((sum, loanEntry) => sum + (loanEntry.interestPayableForTheYear || 0), 0);
                      const eligible80EE = Math.min(total80EEAmount, 50000); // ₹50,000 limit
                      
                      const total80EEAAmount = (deductionData.deductionLoans || [])
                        .filter(loanEntry => loanEntry.sectionOfLoan === '80EEA')
                        .reduce((sum, loanEntry) => sum + (loanEntry.interestPayableForTheYear || 0), 0);
                      const eligible80EEA = Math.min(total80EEAAmount, 150000); // ₹1,50,000 limit
                      
                      const total80EEBAmount = (deductionData.deductionLoans || [])
                        .filter(loanEntry => loanEntry.sectionOfLoan === '80EEB')
                        .reduce((sum, loanEntry) => sum + (loanEntry.interestPayableForTheYear || 0), 0);
                      const eligible80EEB = Math.min(total80EEBAmount, 150000); // ₹1,50,000 limit
                      
                      // Calculate disability deductions
                      const total80DDAmount = (deductionData.disablementDeductions || [])
                        .filter(disabilityEntry => disabilityEntry.section === '80DD')
                        .reduce((sum, disabilityEntry) => sum + (disabilityEntry.deductionAmount || 0), 0);
                      const eligible80DD = Math.min(total80DDAmount, 125000); // ₹1,25,000 limit
                      
                      const total80UAmount = (deductionData.disablementDeductions || [])
                        .filter(disabilityEntry => disabilityEntry.section === '80U')
                        .reduce((sum, disabilityEntry) => sum + (disabilityEntry.deductionAmount || 0), 0);
                      const eligible80U = Math.min(total80UAmount, 125000); // ₹1,25,000 limit
                      
                      const total80GGAEligible = (deductionData.deductionDonations || [])
                        .filter(donationEntry => donationEntry.sectionOfDonation === "80GGA")
                        .reduce((sum, donationEntry) => sum + (donationEntry.eligibleDeduction || 0), 0);
                      
                      const total80GGCEligible = (deductionData.deduction80GGCs || [])
                        .reduce((sum, gccEntry) => sum + calculate80GGCEligibleDeduction(gccEntry), 0);
                      
                      const totalAllDeductions = eligible80CFamily + total80CCD2Eligible + eligible80TTA + eligible80TTB + eligible80GG + eligible80D + eligible80DDB + eligible80E + eligible80EE + eligible80EEA + eligible80EEB + eligible80DD + eligible80U + total80GGAEligible + total80GGCEligible;
                      
                      return (
                        <div className="mt-4 bg-gray-50 border border-gray-200 rounded-md p-4">
                          <h4 className="text-sm font-semibold text-gray-700 mb-3">Adjusted Gross Total Income Calculation</h4>
                          
                          {/* Starting Amount */}
                          <div className="text-center mb-4">
                            <span className="text-gray-600 text-lg">Gross Total Income</span>
                            <div className="font-bold text-gray-800 text-xl">
                              ₹{formatCurrency(grossTotalIncome)}
                            </div>
                          </div>
                          
                          {/* Deductions Grid - Organized by Category */}
                          <div className="space-y-4 mb-4">
                            {/* Investment & Savings Deductions */}
                            <div>
                              <h5 className="text-xs font-semibold text-gray-700 mb-2">Investment & Savings</h5>
                              <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
                                <div className="text-center bg-red-50 p-2 rounded">
                                  <span className="text-gray-600">80C Family*</span>
                                  <div className="font-semibold text-red-600">
                                    - ₹{formatCurrency(eligible80CFamily)}
                                  </div>
                                </div>
                                <div className="text-center bg-red-50 p-2 rounded">
                                  <span className="text-gray-600">80CCD(2)</span>
                                  <div className="font-semibold text-red-600">
                                    - ₹{formatCurrency(total80CCD2Eligible)}
                                  </div>
                                </div>
                                <div className="text-center bg-red-50 p-2 rounded">
                                  <span className="text-gray-600">80TTA**</span>
                                  <div className="font-semibold text-red-600">
                                    - ₹{formatCurrency(eligible80TTA)}
                                  </div>
                                </div>
                                <div className="text-center bg-red-50 p-2 rounded">
                                  <span className="text-gray-600">80TTB***</span>
                                  <div className="font-semibold text-red-600">
                                    - ₹{formatCurrency(eligible80TTB)}
                                  </div>
                                </div>
                              </div>
                            </div>

                            {/* Medical & Other Deductions */}
                            <div>
                              <h5 className="text-xs font-semibold text-gray-700 mb-2">Medical & Other Deductions</h5>
                              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-2 text-xs">
                                <div className="text-center bg-red-50 p-2 rounded">
                                  <span className="text-gray-600">80GG****</span>
                                  <div className="font-semibold text-red-600">
                                    - ₹{formatCurrency(eligible80GG)}
                                  </div>
                                </div>
                                <div className="text-center bg-red-50 p-2 rounded">
                                  <span className="text-gray-600">80D*****</span>
                                  <div className="font-semibold text-red-600">
                                    - ₹{formatCurrency(eligible80D)}
                                  </div>
                                </div>
                                <div className="text-center bg-red-50 p-2 rounded">
                                  <span className="text-gray-600">80DDB******</span>
                                  <div className="font-semibold text-red-600">
                                    - ₹{formatCurrency(eligible80DDB)}
                                  </div>
                                </div>
                                <div className="text-center bg-purple-50 p-2 rounded">
                                  <span className="text-gray-600">80DD***********</span>
                                  <div className="font-semibold text-purple-600">
                                    - ₹{formatCurrency(eligible80DD)}
                                  </div>
                                </div>
                                <div className="text-center bg-purple-50 p-2 rounded">
                                  <span className="text-gray-600">80U************</span>
                                  <div className="font-semibold text-purple-600">
                                    - ₹{formatCurrency(eligible80U)}
                                  </div>
                                </div>
                              </div>
                            </div>

                            {/* Loan Interest Deductions */}
                            <div>
                              <h5 className="text-xs font-semibold text-gray-700 mb-2">Loan Interest</h5>
                              <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
                                <div className="text-center bg-orange-50 p-2 rounded">
                                  <span className="text-gray-600">80E*******</span>
                                  <div className="font-semibold text-orange-600">
                                    - ₹{formatCurrency(eligible80E)}
                                  </div>
                                </div>
                                <div className="text-center bg-orange-50 p-2 rounded">
                                  <span className="text-gray-600">80EE********</span>
                                  <div className="font-semibold text-orange-600">
                                    - ₹{formatCurrency(eligible80EE)}
                                  </div>
                                </div>
                                <div className="text-center bg-orange-50 p-2 rounded">
                                  <span className="text-gray-600">80EEA*********</span>
                                  <div className="font-semibold text-orange-600">
                                    - ₹{formatCurrency(eligible80EEA)}
                                  </div>
                                </div>
                                <div className="text-center bg-orange-50 p-2 rounded">
                                  <span className="text-gray-600">80EEB**********</span>
                                  <div className="font-semibold text-orange-600">
                                    - ₹{formatCurrency(eligible80EEB)}
                                  </div>
                                </div>
                              </div>
                            </div>

                            {/* Donation Deductions */}
                            <div>
                              <h5 className="text-xs font-semibold text-gray-700 mb-2">Donations</h5>
                              <div className="grid grid-cols-2 md:grid-cols-3 gap-2 text-xs">
                                <div className="text-center bg-green-50 p-2 rounded">
                                  <span className="text-gray-600">80GGA</span>
                                  <div className="font-semibold text-green-600">
                                    - ₹{formatCurrency(total80GGAEligible)}
                                  </div>
                                </div>
                                <div className="text-center bg-green-50 p-2 rounded">
                                  <span className="text-gray-600">80GGC</span>
                                  <div className="font-semibold text-green-600">
                                    - ₹{formatCurrency(total80GGCEligible)}
                                  </div>
                                </div>
                                <div className="text-center bg-blue-50 p-2 rounded border-2 border-blue-200">
                                  <span className="text-gray-600 font-semibold">Total Deductions</span>
                                  <div className="font-semibold text-blue-600">
                                    ₹{formatCurrency(totalAllDeductions)}
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                          
                          {/* Final Result */}
                          <div className="border-t border-gray-300 pt-3">
                            <div className="text-center">
                              <span className="text-gray-600 text-lg font-semibold">Adjusted Gross Total Income</span>
                              <div className="font-bold text-blue-600 text-2xl">
                                ₹{formatCurrency(calculateAdjustedGrossTotalIncome())}
                              </div>
                              <div className="text-xs text-gray-500 mt-1">
                                = ₹{formatCurrency(grossTotalIncome)} - ₹{formatCurrency(totalAllDeductions)}
                              </div>
                            </div>
                          </div>
                          
                          <div className="mt-3 text-xs text-gray-500 space-y-1">
                            <div>*80C Family (80C+80CCC+80CCD1+80CCD1B): Max ₹1,50,000</div>
                            <div>**80TTA (Interest on savings): Max ₹10,000</div>
                            <div>***80TTB (Senior citizen deposits): Max ₹50,000</div>
                            <div>****80GG (Rent paid): Max ₹60,000</div>
                            <div>*****80D (Health insurance): Self/Family (₹25K/₹50K) + Parents (₹25K/₹50K)</div>
                            <div>******80DDB (Medical treatment): Max ₹1,00,000</div>
                            <div>*******80E (Education loan interest): No limit</div>
                            <div>********80EE (Home loan interest): Max ₹50,000</div>
                            <div>*********80EEA (Home loan interest): Max ₹1,50,000</div>
                            <div>**********80EEB (Electric vehicle loan): Max ₹1,50,000</div>
                            <div>***********80DD (Disability maintenance): Max ₹1,25,000</div>
                            <div>************80U (Person with disability): Max ₹1,25,000</div>
                          </div>
                        </div>
                      );
                    })()
                  )}

                  {/* Qualifying Limit Warning */}
                  {entry.donationType?.includes("ApprReqd") && (
                    (() => {
                      const cashDonation = Number.parseFloat(entry.donationInCash || 0);
                      const otherModeDonation = Number.parseFloat(entry.donationInOtherMode || 0);
                      const eligibleCashDonation = cashDonation > 2000 ? 0 : cashDonation;
                      const totalEligibleDonation = eligibleCashDonation + otherModeDonation;
                      const calculatedDeduction = (entry.donationType?.includes("100Percent")) ? 
                        totalEligibleDonation : totalEligibleDonation * 0.5;
                      const qualifyingLimit = calculate80GQualifyingLimit(entry.donationType) || 0;
                      const isExceedingLimit = calculatedDeduction > qualifyingLimit;
                      const grossTotalIncome = oldRegimeData ? 
                        Number(oldRegimeData.incomeFromSalary || 0) + 
                        Number(oldRegimeData.incomeFromHP || 0) + 
                        Number(oldRegimeData.incomeFromOtherSources || 0) : 0;
                      // Calculate all deduction amounts (same as in breakdown)
                      const section80CFamily = [
                        ...(deductionData.deduction80Cs || []),
                        ...(deductionData.deduction80CCFamilies || []).filter(familyEntry =>
                          ['80CCC', '80CCD1', '80CCD1B'].includes(familyEntry.section)
                        )
                      ];
                      const total80CFamilyAmount = section80CFamily.reduce((sum, familyEntry) => sum + (familyEntry.amount || 0), 0);
                      const eligible80CFamily = Math.min(total80CFamilyAmount, 150000);
                      
                      const total80CCD2Eligible = (deductionData.deduction80CCFamilies || [])
                        .filter(familyEntry => familyEntry.section === '80CCD2')
                        .reduce((sum, familyEntry) => sum + (familyEntry.amount || 0), 0);
                      
                      const total80TTAAmount = (deductionData.deductionDepositInterests || [])
                        .filter(interestEntry => interestEntry.sectionOfInterest === '80TTA')
                        .reduce((sum, interestEntry) => sum + (interestEntry.interestPaid || 0), 0);
                      const eligible80TTA = Math.min(total80TTAAmount, 10000);
                      
                      const total80TTBAmount = (deductionData.deductionDepositInterests || [])
                        .filter(interestEntry => interestEntry.sectionOfInterest === '80TTB')
                        .reduce((sum, interestEntry) => sum + (interestEntry.interestPaid || 0), 0);
                      const eligible80TTB = Math.min(total80TTBAmount, 50000);
                      
                      const total80GGAmount = (deductionData.deduction80GGs || [])
                        .reduce((sum, ggEntry) => sum + (ggEntry.amountOfRentPaid || 0), 0);
                      const eligible80GG = Math.min(total80GGAmount, 60000);
                      
                      // Calculate 80D eligible deduction (same logic as in main function)
                      let eligible80D = 0;
                      if (deductionData.deduction80D) {
                        const selfLimit = seniorCitizenFlagSelfFamily === 'Y' ? 50000 : 25000;
                        const parentsLimit = parentsSeniorCitizenFlag === 'Y' ? 50000 : 25000;

                        const selfTotal = Math.min(preventiveHealthCheckUpSelfFamily || 0, 5000) + 
                          (medicalExpSelfFamily > 0 ? medicalExpSelfFamily : 
                           (forSelfEntries || []).reduce((sum, entry) => sum + (entry.amount || 0), 0));

                        const parentsTotal = Math.min(preventiveHealthCheckUpParents || 0, 5000) + 
                          (medicalExpenditureParents > 0 ? medicalExpenditureParents : 
                           (forParentEntries || []).reduce((sum, entry) => sum + (entry.amount || 0), 0));

                        eligible80D = Math.min(selfTotal, selfLimit) + Math.min(parentsTotal, parentsLimit);
                      }
                      
                      // Calculate 80DDB eligible deduction
                      const total80DDBAmount = (deductionData.deduction80DDBs || [])
                        .reduce((sum, ddbEntry) => sum + (ddbEntry.expenditureIncurred || 0), 0);
                      const eligible80DDB = Math.min(total80DDBAmount, 100000);
                      
                      // Calculate loan deductions (same logic as in main function)
                      const total80EAmount = (deductionData.deductionLoans || [])
                        .filter(loanEntry => loanEntry.sectionOfLoan === '80E')
                        .reduce((sum, loanEntry) => sum + (loanEntry.interestPayableForTheYear || 0), 0);
                      const eligible80E = total80EAmount; // No limit for 80E
                      
                      const total80EEAmount = (deductionData.deductionLoans || [])
                        .filter(loanEntry => loanEntry.sectionOfLoan === '80EE')
                        .reduce((sum, loanEntry) => sum + (loanEntry.interestPayableForTheYear || 0), 0);
                      const eligible80EE = Math.min(total80EEAmount, 50000); // ₹50,000 limit
                      
                      const total80EEAAmount = (deductionData.deductionLoans || [])
                        .filter(loanEntry => loanEntry.sectionOfLoan === '80EEA')
                        .reduce((sum, loanEntry) => sum + (loanEntry.interestPayableForTheYear || 0), 0);
                      const eligible80EEA = Math.min(total80EEAAmount, 150000); // ₹1,50,000 limit
                      
                      const total80EEBAmount = (deductionData.deductionLoans || [])
                        .filter(loanEntry => loanEntry.sectionOfLoan === '80EEB')
                        .reduce((sum, loanEntry) => sum + (loanEntry.interestPayableForTheYear || 0), 0);
                      const eligible80EEB = Math.min(total80EEBAmount, 150000); // ₹1,50,000 limit
                      
                      // Calculate disability deductions (same logic as in main function)
                      const total80DDAmount = (deductionData.disablementDeductions || [])
                        .filter(disabilityEntry => disabilityEntry.section === '80DD')
                        .reduce((sum, disabilityEntry) => sum + (disabilityEntry.deductionAmount || 0), 0);
                      const eligible80DD = Math.min(total80DDAmount, 125000); // ₹1,25,000 limit
                      
                      const total80UAmount = (deductionData.disablementDeductions || [])
                        .filter(disabilityEntry => disabilityEntry.section === '80U')
                        .reduce((sum, disabilityEntry) => sum + (disabilityEntry.deductionAmount || 0), 0);
                      const eligible80U = Math.min(total80UAmount, 125000); // ₹1,25,000 limit
                      
                      const total80GGAEligible = (deductionData.deductionDonations || [])
                        .filter(donationEntry => donationEntry.sectionOfDonation === "80GGA")
                        .reduce((sum, donationEntry) => sum + (donationEntry.eligibleDeduction || 0), 0);
                      const total80GGCEligible = (deductionData.deduction80GGCs || [])
                        .reduce((sum, gccEntry) => sum + calculate80GGCEligibleDeduction(gccEntry), 0);
                      
                      const totalAllDeductions = eligible80CFamily + total80CCD2Eligible + eligible80TTA + eligible80TTB + eligible80GG + eligible80D + eligible80DDB + eligible80E + eligible80EE + eligible80EEA + eligible80EEB + eligible80DD + eligible80U + total80GGAEligible + total80GGCEligible;
                      const adjustedGTI = Math.max(0, grossTotalIncome - totalAllDeductions);
                      
                      return (
                        <div className={`mt-4 p-3 rounded-md border ${isExceedingLimit ? 'bg-yellow-50 border-yellow-300' : 'bg-blue-50 border-blue-300'}`}>
                          <div className="flex items-start">
                            <div className="flex-shrink-0">
                              <svg className={`h-5 w-5 ${isExceedingLimit ? 'text-yellow-400' : 'text-blue-400'}`} viewBox="0 0 20 20" fill="currentColor">
                                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                              </svg>
                            </div>
                            <div className="ml-3">
                              <p className={`text-sm ${isExceedingLimit ? 'text-yellow-700' : 'text-blue-700'}`}>
                                {isExceedingLimit ? (
                                  <>
                                    <strong>Qualifying Limit Exceeded:</strong> Your donation deduction of ₹{formatCurrency(calculatedDeduction)} 
                                    exceeds the qualifying limit of ₹{formatCurrency(qualifyingLimit)} ({entry.donationType === "Don50PercentApprReqd" ? "5%" : "10%"} of Adjusted GTI). 
                                    <br /><small>Adjusted GTI: ₹{formatCurrency(grossTotalIncome)} (GTI) - ₹{formatCurrency(totalAllDeductions)} (All Chapter VIA deductions) = ₹{formatCurrency(adjustedGTI)}</small>
                                    <br />The deduction will be capped at ₹{formatCurrency(qualifyingLimit)}.
                                  </>
                                ) : (
                                  <>
                                    <strong>Qualifying Limit Info:</strong> Your donation is subject to a qualifying limit of 
                                    ₹{formatCurrency(qualifyingLimit)} ({entry.donationType === "Don50PercentApprReqd" ? "5%" : "10%"} of Adjusted GTI). 
                                    <br /><small>Adjusted GTI: ₹{formatCurrency(grossTotalIncome)} (GTI) - ₹{formatCurrency(totalAllDeductions)} (All Chapter VIA deductions) = ₹{formatCurrency(adjustedGTI)}</small>
                                    <br />Current deduction: ₹{formatCurrency(calculatedDeduction)} is within the limit.
                                  </>
                                )}
                              </p>
                            </div>
                          </div>
                        </div>
                      );
                    })()
                  )}
                </motion.div>
              );
            })}

            <div className="flex space-x-2">
              <button
                onClick={addEntry}
                className="flex-1 py-3 bg-cyan-500 text-white rounded-lg hover:bg-cyan-600 transition-colors flex items-center justify-center space-x-2"
              >
                <Plus className="h-5 w-5" />
                <span>Add Donation</span>
              </button>
            </div>
          </div>
        );
      case "80GGA":
        return (
          <div className="space-y-6">
            {currentEntries.map((entry, index) => {
              const validationStatus = get80GGAValidationStatus(entry);
              return (
                <motion.div
                  key={entry.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className={`border rounded-lg p-4 space-y-4 relative ${
                    activeTab === "80GGA" && !validationStatus.isValid 
                      ? 'border-red-300 bg-red-50' 
                      : 'border-gray-200'
                  }`}
                >
                <h3 className="absolute top-2 left-4 text-sm font-semibold text-gray-600">Donation {index + 1}</h3>
                {currentEntries.length > 1 && (
                  <button
                    onClick={() => removeEntry(entry.id, entry)}
                    className="absolute top-2 right-2 w-8 h-8 bg-red-500 text-white rounded-full flex items-center justify-center hover:bg-red-600 transition-colors"
                  >
                    <Minus className="h-4 w-4" />
                  </button>
                )}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Donation Type</label>
                    <div className="relative">
                      <select
                        className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 appearance-none bg-white ${
                          activeTab === "80GGA" && !validationStatus.isValid 
                            ? 'border-red-500 focus:ring-red-500' 
                            : 'border-gray-300 focus:ring-cyan-500'
                        }`}
                        value={entry.donationType || ""}
                        onChange={(e) => updateEntry(entry.id, "donationType", e.target.value)}
                      >
                        <option value="">Select Donation Type</option>
                        {
                          donationTypeList.map((type) => (
                            <option key={type.codeValue} value={type.codeValue}>
                              {type.description}
                            </option>
                          ))
                        }
                        <option value="scientific_research">
                          Sum paid to Research Association or University, college or other institution for Scientific
                          Research
                        </option>
                        <option value="social_statistical_research">
                          Sum paid to Research Association or University, college or other institution for Social
                          science or Statistical Research
                        </option>
                      </select>
                      <ChevronDown className="absolute right-3 top-3 h-4 w-4 text-gray-400" />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">PAN of Donee</label>
                    <input
                      type="text"
                      value={entry.panOfDonee || ""}
                      onChange={(e) => updateEntry(entry.id, "panOfDonee", e.target.value)}
                      className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 ${
                        activeTab === "80GGA" && !validationStatus.isValid 
                          ? 'border-red-500 focus:ring-red-500' 
                          : 'border-gray-300 focus:ring-cyan-500'
                      }`}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Name of Donee</label>
                    <input
                      type="text"
                      value={entry.nameOfDonee || ""}
                      onChange={(e) => updateEntry(entry.id, "nameOfDonee", e.target.value)}
                      className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 ${
                        activeTab === "80GGA" && !validationStatus.isValid 
                          ? 'border-red-500 focus:ring-red-500' 
                          : 'border-gray-300 focus:ring-cyan-500'
                      }`}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Address</label>
                    <input
                      type="text"
                      placeholder="Enter Details"
                      value={entry.addressOfDonee || ""}
                      onChange={(e) => updateEntry(entry.id, "addressOfDonee", e.target.value)}
                      className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 ${
                        activeTab === "80GGA" && !validationStatus.isValid 
                          ? 'border-red-500 focus:ring-red-500' 
                          : 'border-gray-300 focus:ring-cyan-500'
                      }`}
                    />
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">City / Town / District</label>
                    <input
                      type="text"
                      value={entry.cityTownDistrict || ""}
                      onChange={(e) => updateEntry(entry.id, "cityTownDistrict", e.target.value)}
                      className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 ${
                        activeTab === "80GGA" && !validationStatus.isValid 
                          ? 'border-red-500 focus:ring-red-500' 
                          : 'border-gray-300 focus:ring-cyan-500'
                      }`}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">State</label>
                    <select
                      value={entry.stateCode || ""}
                      onChange={(e) => updateEntry(entry.id, "stateCode", e.target.value)}
                      className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 ${
                        activeTab === "80GGA" && !validationStatus.isValid 
                          ? 'border-red-500 focus:ring-red-500' 
                          : 'border-gray-300 focus:ring-cyan-500'
                      }`}
                    >
                      <option value="">Select State</option>
                      {statesList?.map((state) => (
                        <option key={state.codeValue} value={state.codeValue}>
                          {state.description}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Pin</label>
                    <input
                      type="text"
                      value={entry.pinCode || ""}
                      onChange={(e) => updateEntry(entry.id, "pinCode", e.target.value)}
                      className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 ${
                        getFieldError(entry.id, "pinCode") 
                          ? 'border-red-500 focus:ring-red-500' 
                          : activeTab === "80GGA" && !validationStatus.isValid 
                            ? 'border-red-500 focus:ring-red-500' 
                            : 'border-gray-300 focus:ring-cyan-500'
                      }`}
                    />
                    {getFieldError(entry.id, "pinCode") && (
                      <p className="text-xs text-red-600 mt-1">{getFieldError(entry.id, "pinCode")}</p>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Donation in Cash</label>
                    <div className="relative">
                      <span className="absolute left-3 top-3 text-gray-500">₹</span>
                      <input
                        type="text"
                        placeholder="0.00"
                        value={entry.donationInCash}
                        onChange={(e) => {
                          // Only allow numbers (0-9) and decimal point
                          const value = e.target.value.replace(/[^0-9.]/g, '');
                          // Prevent multiple decimal points
                          const parts = value.split('.');
                          const filteredValue = parts.length > 2 ? parts[0] + '.' + parts.slice(1).join('') : value;
                          const numericValue = Math.max(0, Number.parseFloat(filteredValue || 0));                           if (activeTab === "80GGA") {
                            update80GGATotals(entry.id, "donationInCash", numericValue);
                          } else {
                            updateEntry(entry.id, "donationInCash", numericValue);
                          }
                        }}
                        className={`w-full pl-8 pr-3 py-2 border rounded-md focus:outline-none focus:ring-2 ${
                          activeTab === "80GGA" && !validationStatus.isValid 
                            ? 'border-red-500 focus:ring-red-500' 
                            : 'border-gray-300 focus:ring-cyan-500'
                        }`}
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Donation in Other Mode</label>
                    <div className="relative">
                      <span className="absolute left-3 top-3 text-gray-500">₹</span>
                      <input
                        type="text"
                        placeholder="0.00"
                        value={entry.donationInOtherMode}
                        onChange={(e) => {
                          // Only allow numbers (0-9) and decimal point
                          const value = e.target.value.replace(/[^0-9.]/g, '');
                          // Prevent multiple decimal points
                          const parts = value.split('.');
                          const filteredValue = parts.length > 2 ? parts[0] + '.' + parts.slice(1).join('') : value;
                          const numericValue = Math.max(0, Number.parseFloat(filteredValue || 0));                           if (activeTab === "80GGA") {
                            update80GGATotals(entry.id, "donationInOtherMode", numericValue);
                          } else {
                            updateEntry(entry.id, "donationInOtherMode", numericValue);
                          }
                        }}
                        className={`w-full pl-8 pr-3 py-2 border rounded-md focus:outline-none focus:ring-2 ${
                          activeTab === "80GGA" && !validationStatus.isValid 
                            ? 'border-red-500 focus:ring-red-500' 
                            : 'border-gray-300 focus:ring-cyan-500'
                        }`}
                      />
                    </div>
                  </div>
                </div>

                {/* Cash donation warning for 80GGA */}
                {activeTab === "80GGA" && entry.donationInCash > 2000 && (
                  <div className="mt-4 p-3 bg-yellow-50 border border-yellow-300 rounded-md">
                    <div className="flex items-start">
                      <div className="flex-shrink-0">
                        <svg className="h-5 w-5 text-yellow-400" viewBox="0 0 20 20" fill="currentColor">
                          <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                        </svg>
                      </div>
                      <div className="ml-3">
                        <p className="text-sm text-yellow-700">
                          Cash donations exceeding ₹2,000 are not eligible for deduction under 80GGA.
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Eligible Deduction Summary for 80GGA */}
                {activeTab === "80GGA" && (
                  <div className="mt-4 bg-green-50 border border-green-200 rounded-md p-4">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div className="text-center">
                        <span className="text-sm font-medium text-gray-700">Total Donation</span>
                        <div className="flex items-center justify-center mt-1">
                          <span className="text-gray-500 mr-1">₹</span>
                          <span className="text-lg font-semibold text-gray-900">
                            {formatCurrency(entry.totalDonation || 0)}
                          </span>
                        </div>
                      </div>
                      <div className="text-center">
                        <span className="text-sm font-medium text-gray-700">Eligible Cash</span>
                        <div className="flex items-center justify-center mt-1">
                          <span className="text-gray-500 mr-1">₹</span>
                          <span className="text-lg font-semibold text-blue-600">
                            {formatCurrency((entry.donationInCash || 0) > 2000 ? 0 : (entry.donationInCash || 0))}
                          </span>
                        </div>
                      </div>
                      <div className="text-center">
                        <span className="text-sm font-medium text-gray-700">Eligible Deduction</span>
                        <div className="flex items-center justify-center mt-1">
                          <span className="text-gray-500 mr-1">₹</span>
                          <span className="text-lg font-semibold text-green-600">
                            {formatCurrency(entry.eligibleDeduction || 0)}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {activeTab === "80GGA" && !validationStatus.isValid && (
                  <div className="mt-4 p-3 bg-red-100 border border-red-300 rounded-md">
                    <p className="text-sm text-red-700">{validationStatus.errorMessage}</p>
                  </div>
                )}
              </motion.div>
            );
            })}
            <div className="flex space-x-2">
              <button
                onClick={addEntry}
                className="flex-1 py-3 bg-cyan-500 text-white rounded-lg hover:bg-cyan-600 transition-colors flex items-center justify-center space-x-2"
              >
                <Plus className="h-5 w-5" />
                <span>Add</span>
              </button>
            </div>
          </div>
        )
      case "80GGC":
        return (
          <div className="space-y-6">
            {currentEntries.map((entry, index) => {
              const validationStatus = get80GGCValidationStatus(entry);
              const hasCashDonation = Number.parseFloat(entry.DonationAmtCash || 0) > 0;
              const hasOtherModeDonation = Number.parseFloat(entry.DonationAmtOtherMode || 0) > 0;
              
              return (
                <motion.div
                  key={entry.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className={`border rounded-lg p-4 space-y-4 relative ${
                    activeTab === "80GGC" && !validationStatus.isValid 
                      ? 'border-red-300 bg-red-50' 
                      : 'border-gray-200'
                  }`}
                >
                <h3 className="absolute top-2 left-4 text-sm font-semibold text-gray-600">Donation {index + 1}</h3>
                {currentEntries.length > 1 && (
                  <button
                    onClick={() => removeEntry(entry.id, entry)}
                    className="absolute top-2 right-2 w-8 h-8 bg-red-500 text-white rounded-full flex items-center justify-center hover:bg-red-600 transition-colors"
                  >
                    <Minus className="h-4 w-4" />
                  </button>
                )}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Date of Donation</label>
                    <input
                      type="date"
                      value={entry.DonationDate || ""}
                      onChange={(e) => updateEntry(entry.id, "DonationDate", e.target.value)}
                      className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 ${
                        activeTab === "80GGC" && !validationStatus.isValid 
                          ? 'border-red-500 focus:ring-red-500' 
                          : 'border-gray-300 focus:ring-cyan-500'
                      }`}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Donation in Cash</label>
                    <div className="relative">
                      <span className="absolute left-3 top-3 text-gray-500">₹</span>
                      <input
                        type="text"
                        placeholder="0.00"
                        value={entry.DonationAmtCash}
                        disabled={hasOtherModeDonation}
                        onChange={(e) => {
                          // Only allow numbers (0-9) and decimal point
                          const value = e.target.value.replace(/[^0-9.]/g, '');
                          // Prevent multiple decimal points
                          const parts = value.split('.');
                          const filteredValue = parts.length > 2 ? parts[0] + '.' + parts.slice(1).join('') : value;
                          const numericValue = Math.max(0, Number.parseFloat(filteredValue || 0));                           updateEntry(entry.id, "DonationAmtCash", numericValue);
                        }}
                        className={`w-full pl-8 pr-3 py-2 border rounded-md focus:outline-none focus:ring-2 ${
                          hasOtherModeDonation 
                            ? 'bg-gray-100 text-gray-500 cursor-not-allowed border-gray-200' 
                            : activeTab === "80GGC" && !validationStatus.isValid 
                              ? 'border-red-500 focus:ring-red-500' 
                              : 'border-gray-300 focus:ring-cyan-500'
                        }`}
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Donation in Other Mode</label>
                    <div className="relative">
                      <span className="absolute left-3 top-3 text-gray-500">₹</span>
                      <input
                        type="text"
                        placeholder="0.00"
                        value={entry.DonationAmtOtherMode}
                        disabled={hasCashDonation}
                        onChange={(e) => {
                          // Only allow numbers (0-9) and decimal point
                          const value = e.target.value.replace(/[^0-9.]/g, '');
                          // Prevent multiple decimal points
                          const parts = value.split('.');
                          const filteredValue = parts.length > 2 ? parts[0] + '.' + parts.slice(1).join('') : value;
                          const numericValue = Math.max(0, Number.parseFloat(filteredValue || 0));                           updateEntry(entry.id, "DonationAmtOtherMode", numericValue);
                        }}
                        className={`w-full pl-8 pr-3 py-2 border rounded-md focus:outline-none focus:ring-2 ${
                          hasCashDonation 
                            ? 'bg-gray-100 text-gray-500 cursor-not-allowed border-gray-200' 
                            : activeTab === "80GGC" && !validationStatus.isValid 
                              ? 'border-red-500 focus:ring-red-500' 
                              : 'border-gray-300 focus:ring-cyan-500'
                        }`}
                      />
                    </div>
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Transaction Reference No.</label>
                    <input
                      type="text"
                      value={entry.TransactionRefNum || ""}
                      disabled={hasCashDonation}
                      onChange={(e) => updateEntry(entry.id, "TransactionRefNum", e.target.value)}
                      className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 ${
                        hasCashDonation 
                          ? 'bg-gray-100 text-gray-500 cursor-not-allowed border-gray-200' 
                          : activeTab === "80GGC" && !validationStatus.isValid 
                            ? 'border-red-500 focus:ring-red-500' 
                            : 'border-gray-300 focus:ring-cyan-500'
                      }`}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">IFSC code</label>
                    <input
                      type="text"
                      value={entry.IfscCode || ""}
                      disabled={hasCashDonation}
                      onChange={(e) => updateEntry(entry.id, "IfscCode", e.target.value)}
                      className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 ${
                        hasCashDonation 
                          ? 'bg-gray-100 text-gray-500 cursor-not-allowed border-gray-200' 
                          : activeTab === "80GGC" && !validationStatus.isValid 
                            ? 'border-red-500 focus:ring-red-500' 
                            : 'border-gray-300 focus:ring-cyan-500'
                      }`}
                    />
                  </div>
                </div>

                {/* Cash donation warning for 80GGC */}
                {activeTab === "80GGC" && hasCashDonation && (
                  <div className="mt-4 p-3 bg-yellow-50 border border-yellow-300 rounded-md">
                    <div className="flex items-start">
                      <div className="flex-shrink-0">
                        <svg className="h-5 w-5 text-yellow-400" viewBox="0 0 20 20" fill="currentColor">
                          <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                        </svg>
                      </div>
                      <div className="ml-3">
                        <p className="text-sm text-yellow-700">
                          Cash donations to political parties are not eligible for deduction under 80GGC.
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Eligible Deduction Summary for 80GGC */}
                {activeTab === "80GGC" && (hasCashDonation || hasOtherModeDonation) && (
                  <div className="mt-4 bg-green-50 border border-green-200 rounded-md p-4">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div className="text-center">
                        <span className="text-sm font-medium text-gray-700">Total Donation</span>
                        <div className="flex items-center justify-center mt-1">
                          <span className="text-gray-500 mr-1">₹</span>
                          <span className="text-lg font-semibold text-gray-900">
                            {formatCurrency((entry.DonationAmtCash || 0) + (entry.DonationAmtOtherMode || 0))}
                          </span>
                        </div>
                      </div>
                      <div className="text-center">
                        <span className="text-sm font-medium text-gray-700">Cash (Not Eligible)</span>
                        <div className="flex items-center justify-center mt-1">
                          <span className="text-gray-500 mr-1">₹</span>
                          <span className="text-lg font-semibold text-red-600">
                            {formatCurrency(entry.DonationAmtCash || 0)}
                          </span>
                        </div>
                      </div>
                      <div className="text-center">
                        <span className="text-sm font-medium text-gray-700">Eligible Deduction</span>
                        <div className="flex items-center justify-center mt-1">
                          <span className="text-gray-500 mr-1">₹</span>
                          <span className="text-lg font-semibold text-green-600">
                            {formatCurrency(calculate80GGCEligibleDeduction(entry))}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {activeTab === "80GGC" && !validationStatus.isValid && (
                  <div className="mt-4 p-3 bg-red-100 border border-red-300 rounded-md">
                    <p className="text-sm text-red-700">{validationStatus.errorMessage}</p>
                  </div>
                )}
              </motion.div>
            );
            })}
            <div className="flex space-x-2">
              <button
                onClick={addEntry}
                className="flex-1 py-3 bg-cyan-500 text-white rounded-lg hover:bg-cyan-600 transition-colors flex items-center justify-center space-x-2"
              >
                <Plus className="h-5 w-5" />
                <span>Add</span>
              </button>
            </div>
          </div>
        )
      case "80EE":
        return (
          <div className="space-y-6">
            {currentEntries.map((entry, index) => (
              <motion.div
                key={entry.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="border border-gray-200 rounded-lg p-4 space-y-4 relative"
              >
                <h3 className="absolute top-2 left-4 text-sm font-semibold text-gray-600">Loan {index + 1}</h3>
                {currentEntries.length > 1 && (
                  <button
                    onClick={() => removeEntry(entry.id, entry)}
                    className="absolute top-2 right-2 w-8 h-8 bg-red-500 text-white rounded-full flex items-center justify-center hover:bg-red-600 transition-colors"
                  >
                    <Minus className="h-4 w-4" />
                  </button>
                )}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Loan Taken From</label>
                    <div className="relative">
                      <select
                        className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 appearance-none bg-white ${
                          getFieldError(entry.id, "loanTakenFrom") 
                            ? 'border-red-500 focus:ring-red-500' 
                            : 'border-gray-300 focus:ring-cyan-500'
                        }`}
                        value={entry.loanTakenFrom || ""}
                        onChange={(e) => updateEntry(entry.id, "loanTakenFrom", e.target.value)}
                      >
                        <option value="">Select Nature</option>
                        {
                          loanTakenList ? loanTakenList.map((option) => (
                            <option key={option.codeValue} value={option.codeValue}>{option.description}</option>
                          )) : null
                        }

                      </select>
                      <ChevronDown className="absolute right-3 top-3 h-4 w-4 text-gray-400" />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Name of Loan Provider</label>
                    <input
                      type="text"
                      value={entry.bankInstitutionName || ""}
                      onChange={(e) => updateEntry(entry.id, "bankInstitutionName", e.target.value)}
                      className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 ${
                        getFieldError(entry.id, "bankInstitutionName") 
                          ? 'border-red-500 focus:ring-red-500' 
                          : 'border-gray-300 focus:ring-cyan-500'
                      }`}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Account No.</label>
                    <input
                      type="text"
                      value={entry.accountNo || ""}
                      onChange={(e) => updateEntry(entry.id, "accountNo", e.target.value)}
                      className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 ${
                        getFieldError(entry.id, "accountNo") 
                          ? 'border-red-500 focus:ring-red-500' 
                          : 'border-gray-300 focus:ring-cyan-500'
                      }`}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Date of Loan sanctioned</label>
                    <input
                      type="date"
                      value={entry.dateOfLoan || ""}
                      onChange={(e) => updateEntry(entry.id, "dateOfLoan", e.target.value)}
                      className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 ${
                        getFieldError(entry.id, "dateOfLoan") 
                          ? 'border-red-500 focus:ring-red-500' 
                          : 'border-gray-300 focus:ring-cyan-500'
                      }`}
                    />
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Loan Amount</label>
                    <div className="relative">
                      <span className="absolute left-3 top-3 text-gray-500">₹</span>
                      <input
                        type="text"
                        placeholder="0.00"
                        value={entry.totalLoan}
                        onChange={(e) => {
                          // Only allow numbers (0-9) and decimal point
                          const value = e.target.value.replace(/[^0-9.]/g, '');
                          // Prevent multiple decimal points
                          const parts = value.split('.');
                          const filteredValue = parts.length > 2 ? parts[0] + '.' + parts.slice(1).join('') : value;
                          const numericValue = Math.max(0, Number.parseFloat(filteredValue || 0));                           updateEntry(entry.id, "totalLoan", numericValue);
                        }}
                        className={`w-full pl-8 pr-3 py-2 border rounded-md focus:outline-none focus:ring-2 ${
                          getFieldError(entry.id, "totalLoan") 
                            ? 'border-red-500 focus:ring-red-500' 
                            : 'border-gray-300 focus:ring-cyan-500'
                        }`}
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Loan outstanding</label>
                    <div className="relative">
                      <span className="absolute left-3 top-3 text-gray-500">₹</span>
                      <input
                        type="text"
                        placeholder="0.00"
                        value={entry.loanOutStanding}
                        onChange={(e) => {
                          // Only allow numbers (0-9) and decimal point
                          const value = e.target.value.replace(/[^0-9.]/g, '');
                          // Prevent multiple decimal points
                          const parts = value.split('.');
                          const filteredValue = parts.length > 2 ? parts[0] + '.' + parts.slice(1).join('') : value;
                          const numericValue = Math.max(0, Number.parseFloat(filteredValue || 0));                           updateEntry(entry.id, "loanOutStanding", numericValue);
                        }}
                        className={`w-full pl-8 pr-3 py-2 border rounded-md focus:outline-none focus:ring-2 ${
                          getFieldError(entry.id, "loanOutStanding") 
                            ? 'border-red-500 focus:ring-red-500' 
                            : 'border-gray-300 focus:ring-cyan-500'
                        }`}
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Interest u/s 80EE *</label>
                    <div className="relative">
                      <span className="absolute left-3 top-3 text-gray-500">₹</span>
                      <input
                        type="text"
                        placeholder="0.00"
                        value={entry.interestPayableForTheYear}
                        onChange={(e) => {
                          // Only allow numbers (0-9) and decimal point
                          const value = e.target.value.replace(/[^0-9.]/g, '');
                          // Prevent multiple decimal points
                          const parts = value.split('.');
                          const filteredValue = parts.length > 2 ? parts[0] + '.' + parts.slice(1).join('') : value;
                          const numericValue = Math.max(0, Number.parseFloat(filteredValue || 0));                           updateEntry(entry.id, "interestPayableForTheYear", numericValue);
                        }}
                        className={`w-full pl-8 pr-3 py-2 border rounded-md focus:outline-none focus:ring-2 ${
                          getFieldError(entry.id, "interestPayableForTheYear") 
                            ? 'border-red-500 focus:ring-red-500' 
                            : 'border-gray-300 focus:ring-cyan-500'
                        }`}
                      />
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
            <div className="flex space-x-2">
              <button
                onClick={addEntry}
                className="flex-1 py-3 bg-cyan-500 text-white rounded-lg hover:bg-cyan-600 transition-colors flex items-center justify-center space-x-2"
              >
                <Plus className="h-5 w-5" />
                <span>Add</span>
              </button>
            </div>
          </div>
        )
      case "80EEA":
        return (
          <div className="space-y-6">
            {currentEntries.map((entry, index) => (
              <motion.div
                key={entry.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="border border-gray-200 rounded-lg p-4 space-y-4 relative"
              >
                <h3 className="absolute top-2 left-4 text-sm font-semibold text-gray-600">Loan {index + 1}</h3>
                {currentEntries.length > 1 && (
                  <button
                    onClick={() => removeEntry(entry.id, entry)}
                    className="absolute top-2 right-2 w-8 h-8 bg-red-500 text-white rounded-full flex items-center justify-center hover:bg-red-600 transition-colors"
                  >
                    <Minus className="h-4 w-4" />
                  </button>
                )}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Loan Taken From</label>
                    <div className="relative">
                      <select
                        className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 appearance-none bg-white ${
                          getFieldError(entry.id, "loanTakenFrom") 
                            ? 'border-red-500 focus:ring-red-500' 
                            : 'border-gray-300 focus:ring-cyan-500'
                        }`}
                        value={entry.loanTakenFrom || ""}
                        onChange={(e) => updateEntry(entry.id, "loanTakenFrom", e.target.value)}
                      >
                        <option value="">Select Nature</option>
                        {
                          loanTakenList ? loanTakenList.map((option) => (
                            <option key={option.codeValue} value={option.codeValue}>{option.description}</option>
                          )) : null
                        }
                      </select>
                      <ChevronDown className="absolute right-3 top-3 h-4 w-4 text-gray-400" />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Name of Loan Provider</label>
                    <input
                      type="text"
                      value={entry.bankInstitutionName || ""}
                      onChange={(e) => updateEntry(entry.id, "bankInstitutionName", e.target.value)}
                      className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 ${
                        getFieldError(entry.id, "bankInstitutionName") 
                          ? 'border-red-500 focus:ring-red-500' 
                          : 'border-gray-300 focus:ring-cyan-500'
                      }`}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Account No.</label>
                    <input
                      type="text"
                      value={entry.accountNo || ""}
                      onChange={(e) => updateEntry(entry.id, "accountNo", e.target.value)}
                      className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 ${
                        getFieldError(entry.id, "accountNo") 
                          ? 'border-red-500 focus:ring-red-500' 
                          : 'border-gray-300 focus:ring-cyan-500'
                      }`}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Date of Loan sanctioned</label>
                    <input
                      type="date"
                      value={entry.dateOfLoan || ""}
                      onChange={(e) => updateEntry(entry.id, "dateOfLoan", e.target.value)}
                      className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 ${
                        getFieldError(entry.id, "dateOfLoan") 
                          ? 'border-red-500 focus:ring-red-500' 
                          : 'border-gray-300 focus:ring-cyan-500'
                      }`}
                    />
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Loan Amount</label>
                    <div className="relative">
                      <span className="absolute left-3 top-3 text-gray-500">₹</span>
                      <input
                        type="text"
                        placeholder="0.00"
                        value={entry.totalLoan}
                        onChange={(e) => {
                          // Only allow numbers (0-9) and decimal point
                          const value = e.target.value.replace(/[^0-9.]/g, '');
                          // Prevent multiple decimal points
                          const parts = value.split('.');
                          const filteredValue = parts.length > 2 ? parts[0] + '.' + parts.slice(1).join('') : value;
                          const numericValue = Math.max(0, Number.parseFloat(filteredValue || 0));                           updateEntry(entry.id, "totalLoan", numericValue);
                        }}
                        className={`w-full pl-8 pr-3 py-2 border rounded-md focus:outline-none focus:ring-2 ${
                          getFieldError(entry.id, "totalLoan") 
                            ? 'border-red-500 focus:ring-red-500' 
                            : 'border-gray-300 focus:ring-cyan-500'
                        }`}
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Loan outstanding</label>
                    <div className="relative">
                      <span className="absolute left-3 top-3 text-gray-500">₹</span>
                      <input
                        type="text"
                        placeholder="0.00"
                        value={entry.loanOutStanding}
                        onChange={(e) => {
                          // Only allow numbers (0-9) and decimal point
                          const value = e.target.value.replace(/[^0-9.]/g, '');
                          // Prevent multiple decimal points
                          const parts = value.split('.');
                          const filteredValue = parts.length > 2 ? parts[0] + '.' + parts.slice(1).join('') : value;
                          const numericValue = Math.max(0, Number.parseFloat(filteredValue || 0));                           updateEntry(entry.id, "loanOutStanding", numericValue);
                        }}
                        className={`w-full pl-8 pr-3 py-2 border rounded-md focus:outline-none focus:ring-2 ${
                          getFieldError(entry.id, "loanOutStanding") 
                            ? 'border-red-500 focus:ring-red-500' 
                            : 'border-gray-300 focus:ring-cyan-500'
                        }`}
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Interest u/s 80EEA *</label>
                    <div className="relative">
                      <span className="absolute left-3 top-3 text-gray-500">₹</span>
                      <input
                        type="text"
                        placeholder="0.00"
                        value={entry.interestPayableForTheYear}
                        onChange={(e) => {
                          // Only allow numbers (0-9) and decimal point
                          const value = e.target.value.replace(/[^0-9.]/g, '');
                          // Prevent multiple decimal points
                          const parts = value.split('.');
                          const filteredValue = parts.length > 2 ? parts[0] + '.' + parts.slice(1).join('') : value;
                          const numericValue = Math.max(0, Number.parseFloat(filteredValue || 0));                           updateEntry(entry.id, "interestPayableForTheYear", numericValue);
                        }}
                        className={`w-full pl-8 pr-3 py-2 border rounded-md focus:outline-none focus:ring-2 ${
                          getFieldError(entry.id, "interestPayableForTheYear") 
                            ? 'border-red-500 focus:ring-red-500' 
                            : 'border-gray-300 focus:ring-cyan-500'
                        }`}
                      />
                    </div>
                  </div>
                </div>
                <div className="flex items-start flex-row-reverse">
                  <div></div>
                </div>
              </motion.div>
            ))}
            <button
              onClick={addEntry}
              className="w-full py-3 border-2 border-dashed border-gray-300 rounded-lg text-gray-600 hover:border-cyan-500 hover:text-cyan-600 transition-colors flex items-center justify-center space-x-2"
            >
              <Plus className="h-5 w-5" />
              <span>Add Another Loan</span>
            </button>
            <div className="flex flex-col md:flex-row items-center gap-4 justify-between">
              <label className="text-md font-semibold text-gray-700 w-full md:w-1/3">Stamp Value</label>
              <input
                type="text"
                placeholder="0.00"
                value={currentEntries[0]?.stampValue}
                onChange={(e) => {
                  // Only allow numbers (0-9) and decimal point
                  const value = e.target.value.replace(/[^0-9.]/g, '');
                  // Prevent multiple decimal points
                  const parts = value.split('.');
                  const filteredValue = parts.length > 2 ? parts[0] + '.' + parts.slice(1).join('') : value;
                  const numericValue = Math.max(0, Number.parseFloat(filteredValue || 0));                   updateEntry(currentEntries[0]?.id, "stampValue", numericValue);
                }}
                className="w-32 px-2 py-3 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-cyan-500"
              />
            </div>
          </div>
        )
      case "80EEB":
        return (
          <div className="space-y-6">
            {currentEntries.map((entry, index) => (
              <motion.div
                key={entry.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="border border-gray-200 rounded-lg p-4 space-y-4 relative"
              >
                <h3 className="absolute top-2 left-4 text-sm font-semibold text-gray-600">Loan {index + 1}</h3>
                {currentEntries.length > 1 && (
                  <button
                    onClick={() => removeEntry(entry.id, entry)}
                    className="absolute top-2 right-2 w-8 h-8 bg-red-500 text-white rounded-full flex items-center justify-center hover:bg-red-600 transition-colors"
                  >
                    <Minus className="h-4 w-4" />
                  </button>
                )}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Loan Taken From</label>
                    <div className="relative">
                      <select
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-cyan-500 appearance-none bg-white"
                        value={entry.loanTakenFrom || ""}
                        onChange={(e) => updateEntry(entry.id, "loanTakenFrom", e.target.value)}
                      >
                        <option value="">Select Nature</option>
                        {
                          loanTakenList ? loanTakenList.map((option) => (
                            <option key={option.codeValue} value={option.codeValue}>{option.description}</option>
                          )) : null
                        }
                      </select>
                      <ChevronDown className="absolute right-3 top-3 h-4 w-4 text-gray-400" />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Name of Loan Provider</label>
                    <input
                      type="text"
                      value={entry.bankInstitutionName || ""}
                      onChange={(e) => updateEntry(entry.id, "bankInstitutionName", e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-cyan-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Account No.</label>
                    <input
                      type="text"
                      value={entry.accountNo || ""}
                      onChange={(e) => updateEntry(entry.id, "accountNo", e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-cyan-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Date of Loan sanctioned</label>
                    <input
                      type="date"
                      value={entry.dateOfLoan || ""}
                      onChange={(e) => updateEntry(entry.id, "dateOfLoan", e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-cyan-500"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Loan Amount</label>
                    <div className="relative">
                      <span className="absolute left-3 top-3 text-gray-500">₹</span>
                      <input
                        type="text"
                        placeholder="0.00"
                        value={entry.totalLoan}
                        onChange={(e) => {
                          // Only allow numbers (0-9) and decimal point
                          const value = e.target.value.replace(/[^0-9.]/g, '');
                          // Prevent multiple decimal points
                          const parts = value.split('.');
                          const filteredValue = parts.length > 2 ? parts[0] + '.' + parts.slice(1).join('') : value;
                          const numericValue = Math.max(0, Number.parseFloat(filteredValue || 0));                           updateEntry(entry.id, "totalLoan", numericValue);
                        }}
                        className="w-full pl-8 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-cyan-500"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Loan outstanding</label>
                    <div className="relative">
                      <span className="absolute left-3 top-3 text-gray-500">₹</span>
                      <input
                        type="text"
                        placeholder="0.00"
                        value={entry.loanOutStanding}
                        onChange={(e) => {
                          // Only allow numbers (0-9) and decimal point
                          const value = e.target.value.replace(/[^0-9.]/g, '');
                          // Prevent multiple decimal points
                          const parts = value.split('.');
                          const filteredValue = parts.length > 2 ? parts[0] + '.' + parts.slice(1).join('') : value;
                          const numericValue = Math.max(0, Number.parseFloat(filteredValue || 0));                           updateEntry(entry.id, "loanOutStanding", numericValue);
                        }}
                        className="w-full pl-8 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-cyan-500"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Vehicle Registration no.</label>
                    <input
                      type="text"
                      value={entry.vehicleRegistrationNo || ""}
                      onChange={(e) => updateEntry(entry.id, "vehicleRegistrationNo", e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-cyan-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Interest u/s 80EEB *</label>
                    <div className="relative">
                      <span className="absolute left-3 top-3 text-gray-500">₹</span>
                      <input
                        type="text"
                        placeholder="0.00"
                        value={entry.interestPayableForTheYear}
                        onChange={(e) => {
                          // Only allow numbers (0-9) and decimal point
                          const value = e.target.value.replace(/[^0-9.]/g, '');
                          // Prevent multiple decimal points
                          const parts = value.split('.');
                          const filteredValue = parts.length > 2 ? parts[0] + '.' + parts.slice(1).join('') : value;
                          const numericValue = Math.max(0, Number.parseFloat(filteredValue || 0));                           updateEntry(entry.id, "interestPayableForTheYear", numericValue);
                        }}
                        className="w-full pl-8 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-cyan-500"
                      />
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
            <button
              onClick={addEntry}
              className="w-full py-3 border-2 border-dashed border-gray-300 rounded-lg text-gray-600 hover:border-cyan-500 hover:text-cyan-600 transition-colors flex items-center justify-center space-x-2"
            >
              <Plus className="h-5 w-5" />
              <span>Add Another Loan</span>
            </button>
          </div>
        )
      case "80DD":
        return (
          <div className="space-y-6">
            {currentEntries.map((entry, index) => {
              const validationStatus = get80DDValidationStatus(entry);
              const currentLimit = getDisabilityAmountLimit(entry.typeOfDisability);
              const isForm10IANeeded = isForm10IARequired(entry.typeOfDisability, typeOfDisabilityList);
              
              return (
                <motion.div
                  key={entry.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className={`border rounded-lg p-4 space-y-4 relative ${
                    activeTab === "80DD" && !validationStatus.isValid 
                      ? 'border-red-300 bg-red-50' 
                      : 'border-gray-200'
                  }`}
                >
                <h3 className="absolute top-2 left-4 text-sm font-semibold text-gray-600">Dependent {index + 1}</h3>
                {currentEntries.length > 1 && (
                  <button
                    onClick={() => removeEntry(entry.id, entry)}
                    className="absolute top-2 right-2 w-8 h-8 bg-red-500 text-white rounded-full flex items-center justify-center hover:bg-red-600 transition-colors"
                  >
                    <Minus className="h-4 w-4" />
                  </button>
                )}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Nature of Disability</label>
                    <div className="relative">
                      <select
                        className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 appearance-none bg-white ${
                          activeTab === "80DD" && !validationStatus.isValid 
                            ? 'border-red-500 focus:ring-red-500' 
                            : 'border-gray-300 focus:ring-cyan-500'
                        }`}
                        value={entry.natureOfDisability || ""}
                        onChange={(e) => updateEntry(entry.id, "natureOfDisability", e.target.value)}
                      >
                        <option value="">Select Nature</option>
                        {
                          disabilityNatureList?.map((option) => (
                            <option key={option.codeValue} value={option.codeValue}>
                              {option.description}
                            </option>
                          ))
                        }
                      </select>
                      <ChevronDown className="absolute right-3 top-3 h-4 w-4 text-gray-400" />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Type of Disability</label>
                    <div className="relative">
                      <select
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-cyan-500 appearance-none bg-white"
                        value={entry.typeOfDisability || ""}
                        onChange={(e) => updateEntry(entry.id, "typeOfDisability", e.target.value)}
                      >
                        <option value="">Select disability type</option>
                        {
                          typeOfDisabilityList?.map((option) => (
                            <option key={option.codeValue} value={option.codeValue}>
                              {option.description}
                            </option>
                          ))
                        }
                      </select>
                      <ChevronDown className="absolute right-3 top-3 h-4 w-4 text-gray-400" />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Type of Dependent</label>
                    <div className="relative">
                      <select
                        className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 appearance-none bg-white ${
                          activeTab === "80DD" && !validationStatus.isValid 
                            ? 'border-red-500 focus:ring-red-500' 
                            : 'border-gray-300 focus:ring-cyan-500'
                        }`}
                        value={entry.dependentType || ""}
                        onChange={(e) => updateEntry(entry.id, "dependentType", e.target.value)}
                      >
                        <option value="">Select Dependent</option>
                        {
                          typeOfDependentList?.map((option) => (
                            <option key={option.codeValue} value={option.codeValue}>
                              {option.description}
                            </option>
                          ))
                        }
                      </select>
                      <ChevronDown className="absolute right-3 top-3 h-4 w-4 text-gray-400" />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Amount</label>
                    <div className="relative">
                      <span className="absolute left-3 top-3 text-gray-500">₹</span>
                      <input
                        type="text"
                        placeholder="0.00"
                        value={entry.deductionAmount}
                        onChange={(e) => {
                          // Only allow numbers (0-9) and decimal point
                          const value = e.target.value.replace(/[^0-9.]/g, '');
                          // Prevent multiple decimal points
                          const parts = value.split('.');
                          const filteredValue = parts.length > 2 ? parts[0] + '.' + parts.slice(1).join('') : value;
                          const numericValue = Math.max(0, Number.parseFloat(filteredValue || 0));                           updateEntry(entry.id, "deductionAmount", numericValue);
                        }}
                        className="w-full pl-8 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-cyan-500"
                      />
                    </div>
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Dependent PAN</label>
                    <input
                      type="text"
                      placeholder="PAN"
                      value={entry.dependentPan || ""}
                      onChange={(e) => updateEntry(entry.id, "dependentPan", e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-cyan-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Dependent Aadhar</label>
                    <input
                      type="text"
                      placeholder="Aadhar"
                      value={entry.dependentAadhaar || ""}
                      onChange={(e) => updateEntry(entry.id, "dependentAadhaar", e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-cyan-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Ack. No. of Form 10IA filed
                    </label>
                    <input
                      type="text"
                      placeholder="Enter Details"
                      value={entry.form10AAckNum || ""}
                      onChange={(e) => updateEntry(entry.id, "form10AAckNum", e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-cyan-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">UDID Number (If available)</label>
                    <input
                      type="text"
                      placeholder="Enter Details"
                      value={entry.udidNo || ""}
                      onChange={(e) => updateEntry(entry.id, "udidNo", e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-cyan-500"
                    />
                  </div>
                </div>
                
                {/* Amount limit information for 80DD */}
                {activeTab === "80DD" && entry.typeOfDisability && (
                  <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-md">
                    <div className="flex items-start">
                      <div className="flex-shrink-0">
                        <svg className="h-5 w-5 text-blue-400" viewBox="0 0 20 20" fill="currentColor">
                          <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                        </svg>
                      </div>
                      <div className="ml-3">
                        <p className="text-sm text-blue-700">
                          Maximum deduction limit: ₹{currentLimit.toLocaleString()} 
                          ({isSevereDisability(entry.typeOfDisability) ? 'Severe' : 'Regular'} disability)
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Form 10IA requirement warning for 80DD */}
                {activeTab === "80DD" && isForm10IANeeded && !entry.form10AAckNum && (
                  <div className="mt-4 p-3 bg-yellow-50 border border-yellow-300 rounded-md">
                    <div className="flex items-start">
                      <div className="flex-shrink-0">
                        <svg className="h-5 w-5 text-yellow-400" viewBox="0 0 20 20" fill="currentColor">
                          <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                        </svg>
                      </div>
                      <div className="ml-3">
                        <p className="text-sm text-yellow-700">
                          Form 10IA acknowledgement number is required for Autism/Cerebral Palsy/Multiple Disability cases.
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                {activeTab === "80DD" && !validationStatus.isValid && (
                  <div className="mt-4 p-3 bg-red-100 border border-red-300 rounded-md">
                    <p className="text-sm text-red-700">{validationStatus.errorMessage}</p>
                  </div>
                )}
              </motion.div>
            );
            })}

          </div>
        )
      case "80GG":
        return (
          <div className="space-y-6">
            {currentEntries.map((entry, index) => {
              const validationStatus = get80GGValidationStatus(entry);
              
              return (
                <motion.div
                  key={entry.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className={`grid grid-cols-1 md:grid-cols-2 gap-8 border rounded-lg p-4 relative ${
                    activeTab === "80GG" && !validationStatus.isValid 
                      ? 'border-red-300 bg-red-50' 
                      : 'border-gray-200'
                  }`}
                >
                <h3 className="absolute top-2 left-4 text-sm font-semibold text-gray-600 hidden">Entry {index + 1}</h3>
                {currentEntries.length > 1 && (
                  <button
                    onClick={() => removeEntry(entry.id, entry)}
                    className="absolute top-2 right-2 w-8 h-8 bg-red-500 text-white rounded-full flex items-center justify-center hover:bg-red-600 transition-colors"
                  >
                    <Minus className="h-4 w-4" />
                  </button>
                )}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2 text-start">
                    Form 10BA Acknowledgement Number
                  </label>
                  <div className="relative">

                    <input
                      type="text"
                      placeholder=""
                      value={entry.acknowledgement10BA || ""}
                      onChange={(e) => updateEntry(entry.id, "acknowledgement10BA", e.target.value)}
                      className={`w-full pl-8 pr-3 py-2 border rounded-md focus:outline-none focus:ring-2 ${
                        activeTab === "80GG" && !validationStatus.isValid 
                          ? 'border-red-500 focus:ring-red-500' 
                          : 'border-gray-300 focus:ring-cyan-500'
                      }`}
                    />
                  </div>
                </div>
                <div className="">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Annual Rent Amount</label>
                    <div className="relative">
                      <span className="absolute left-3 top-3 text-gray-500">₹</span>
                      <input
                        type="text"
                        placeholder="0.00"
                        value={entry.amountOfRentPaid}
                        onChange={(e) => {
                          // Only allow numbers (0-9) and decimal point
                          const value = e.target.value.replace(/[^0-9.]/g, '');
                          // Prevent multiple decimal points
                          const parts = value.split('.');
                          const filteredValue = parts.length > 2 ? parts[0] + '.' + parts.slice(1).join('') : value;
                          const numericValue = Math.max(0, Number.parseFloat(filteredValue || 0));                           updateEntry(entry.id, "amountOfRentPaid", numericValue);
                        }}
                        className={`w-full pl-8 pr-3 py-2 border rounded-md focus:outline-none focus:ring-2 ${
                          activeTab === "80GG" && !validationStatus.isValid 
                            ? 'border-red-500 focus:ring-red-500' 
                            : 'border-gray-300 focus:ring-cyan-500'
                        }`}
                      />
                    </div>
                  </div>
                </div>
                
                {activeTab === "80GG" && !validationStatus.isValid && (
                  <div className="mt-4 p-3 bg-red-100 border border-red-300 rounded-md col-span-2">
                    <p className="text-sm text-red-700">{validationStatus.errorMessage}</p>
                  </div>
                )}
              </motion.div>
            );
            })}
          </div>
        )
      case "80E":
        return (
          <div className="space-y-6">
            {currentEntries.map((entry, index) => (
              <motion.div
                key={entry.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="border border-gray-200 rounded-lg p-4 space-y-4 relative"
              >
                <h3 className="absolute top-2 left-4 text-sm font-semibold text-gray-700">Loan {index + 1}</h3>
                {currentEntries.length > 1 && (
                  <button
                    onClick={() => removeEntry(entry.id, entry)}
                    className="absolute top-2 right-2 w-8 h-8 bg-red-500 text-white rounded-full flex items-center justify-center hover:bg-red-600 transition-colors"
                  >
                    <Minus className="h-5 w-5" />
                  </button>
                )}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Loan Taken From</label>
                    <div className="relative">
                      <select
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-cyan-500 appearance-none bg-white"
                        value={entry.loanTakenFrom || ""}
                        onChange={(e) => updateEntry(entry.id, "loanTakenFrom", e.target.value)}
                      >
                        <option value="">Select Nature</option>
                        {
                          loanTakenList ? loanTakenList.map((option) => (
                            <option key={option.codeValue} value={option.codeValue}>{option.description}</option>
                          )) : null
                        }
                      </select>
                      <ChevronDown className="absolute right-3 top-3 h-4 w-4 text-gray-400" />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Name of Loan Provider</label>
                    <input
                      type="text"
                      placeholder="Enter name"
                      value={entry.bankInstitutionName || ""}
                      onChange={(e) => updateEntry(entry.id, "bankInstitutionName", e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-cyan-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Account No.</label>
                    <input
                      type="text"
                      placeholder="Account Number"
                      value={entry.accountNo || ""}
                      onChange={(e) => updateEntry(entry.id, "accountNo", e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-cyan-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Date of Loan sanctioned</label>
                    <input
                      type="date"
                      value={entry.dateOfLoan || ""}
                      onChange={(e) => updateEntry(entry.id, "dateOfLoan", e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-cyan-500"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Loan Amount</label>
                    <div className="relative">
                      <span className="absolute left-3 top-3 text-gray-500">₹</span>
                      <input
                        type="text"
                        placeholder="0.00"
                        value={entry.totalLoan}
                        onChange={(e) => {
                          // Only allow numbers (0-9) and decimal point
                          const value = e.target.value.replace(/[^0-9.]/g, '');
                          // Prevent multiple decimal points
                          const parts = value.split('.');
                          const filteredValue = parts.length > 2 ? parts[0] + '.' + parts.slice(1).join('') : value;
                          const numericValue = Math.max(0, Number.parseFloat(filteredValue || 0));                           updateEntry(entry.id, "totalLoan", numericValue);
                        }}
                        className="w-full pl-8 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-cyan-500"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Loan outstanding</label>
                    <div className="relative">
                      <span className="absolute left-3 top-3 text-gray-500">₹</span>
                      <input
                        type="text"
                        placeholder="0.00"
                        value={entry.loanOutStanding}
                        onChange={(e) => {
                          // Only allow numbers (0-9) and decimal point
                          const value = e.target.value.replace(/[^0-9.]/g, '');
                          // Prevent multiple decimal points
                          const parts = value.split('.');
                          const filteredValue = parts.length > 2 ? parts[0] + '.' + parts.slice(1).join('') : value;
                          const numericValue = Math.max(0, Number.parseFloat(filteredValue || 0));                           updateEntry(entry.id, "loanOutStanding", numericValue);
                        }}
                        className="w-full pl-8 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-cyan-500"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Interest u/s 80E *</label>
                    <div className="relative">
                      <span className="absolute left-3 top-3 text-gray-500">₹</span>
                      <input
                        type="text"
                        placeholder="0.00"
                        value={entry.interestPayableForTheYear}
                        onChange={(e) => {
                          // Only allow numbers (0-9) and decimal point
                          const value = e.target.value.replace(/[^0-9.]/g, '');
                          // Prevent multiple decimal points
                          const parts = value.split('.');
                          const filteredValue = parts.length > 2 ? parts[0] + '.' + parts.slice(1).join('') : value;
                          const numericValue = Math.max(0, Number.parseFloat(filteredValue || 0));                           updateEntry(entry.id, "interestPayableForTheYear", numericValue);
                        }}
                        className="w-full pl-8 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-cyan-500"
                      />
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
            <button
              onClick={addEntry}
              className="w-full py-3 border-2 border-dashed border-gray-300 rounded-lg text-gray-600 hover:border-cyan-500 hover:text-cyan-600 transition-colors flex items-center justify-center space-x-2"
            >
              <Plus className="h-5 w-5" />
              <span>Add Another Loan</span>
            </button>
          </div>
        )
      case "80CCD1":
      case "80CCC":
      case "80CCD1B":
      case "80CCD2":
        return (
          <div className="space-y-6">
            {currentEntries.map((entry, index) => {
              const validationStatus = get80CCD1BValidationStatus(entry);
              return (
                <motion.div
                  key={entry.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className={`grid grid-cols-1 md:grid-cols-2 gap-8 items-end border rounded-lg p-4 relative ${
                    activeTab === "80CCD1B" && !validationStatus.isValid 
                      ? 'border-red-300 bg-red-50' 
                      : 'border-gray-200'
                  }`}
                >
                  <h3 className="absolute top-2 left-4 text-sm font-semibold text-gray-600 hidden">Entry {index + 1}</h3>
                  {currentEntries.length > 1 && (
                    <button
                      onClick={() => removeEntry(entry.id, entry)}
                      className="absolute top-2 right-2 w-8 h-8 bg-red-500 text-white rounded-full flex items-center justify-center hover:bg-red-600 transition-colors"
                    >
                      <Minus className="h-4 w-4" />
                    </button>
                  )}
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2 text-center">
                      Amount {activeTab === "80CCD1B" && <span className="text-red-500">*</span>}
                    </label>
                    <div className="relative">
                      <span className="absolute left-3 top-3 text-gray-500">₹</span>
                      <input
                        type="text"
                        placeholder="0.00"
                        value={entry.amount}
                        onChange={(e) => {
                          // Only allow numbers (0-9) and decimal point
                          const value = e.target.value.replace(/[^0-9.]/g, '');
                          // Prevent multiple decimal points
                          const parts = value.split('.');
                          const filteredValue = parts.length > 2 ? parts[0] + '.' + parts.slice(1).join('') : value;
                          const numericValue = Math.max(0, Number.parseFloat(filteredValue || 0));                           updateEntry(entry.id, "amount", numericValue);
                        }}
                        className={`w-full pl-8 pr-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-cyan-500 ${
                          activeTab === "80CCD1B" && !validationStatus.isValid 
                            ? 'border-red-500 focus:ring-red-500' 
                            : 'border-gray-300 focus:ring-cyan-500'
                        }`}
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2 text-center">
                      PRAN No. {activeTab === "80CCD1B" && <span className="text-red-500">*</span>}
                    </label>
                    <input
                      type="text"
                      placeholder="Enter PRAN number"
                      value={entry.pranNumber || ""}
                      onChange={(e) => updateEntry(entry.id, "pranNumber", e.target.value)}
                      className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-cyan-500 ${
                        getFieldError(entry.id, "pranNumber")
                          ? 'border-red-500 focus:ring-red-500' 
                          : activeTab === "80CCD1B" && !validationStatus.isValid 
                            ? 'border-red-500 focus:ring-red-500' 
                            : 'border-gray-300 focus:ring-cyan-500'
                      }`}
                    />
                    {getFieldError(entry.id, "pranNumber") && (
                      <p className="text-xs text-red-600 mt-1">{getFieldError(entry.id, "pranNumber")}</p>
                    )}
                  </div>
                  {activeTab === "80CCD1B" && !validationStatus.isValid && (
                    <div className="col-span-2 mt-2 p-2 bg-red-100 border border-red-300 rounded-md">
                      <p className="text-sm text-red-700">{validationStatus.errorMessage}</p>
                    </div>
                  )}
                </motion.div>
              );
            })}
            <button
              onClick={addEntry}
              className="w-full py-3 border-2 border-dashed border-gray-300 rounded-lg text-gray-600 hover:border-cyan-500 hover:text-cyan-600 transition-colors flex items-center justify-center space-x-2"
            >
              <Plus className="h-5 w-5" />
              <span>Add Another Entry</span>
            </button>
          </div>
        )
      // In your renderForm function, modify the 80TTA and 80TTB cases
      case "80TTA":
        return (
          <div className="space-y-6">
            {isSeniorCitizen ? (
              <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4">
                <div className="flex">
                  <div className="flex-shrink-0">
                    <svg className="h-5 w-5 text-yellow-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div className="ml-3">
                    <p className="text-sm text-yellow-700">
                      Section 80TTA is not available for senior citizens. You may be eligible for 80TTB instead.
                    </p>
                  </div>
                </div>
              </div>
            ) : (
              // 80TTA form for non-senior citizens
              currentEntries.map((entry, index) => (
                <motion.div
                  key={entry.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="grid grid-cols-1 gap-4 items-end border border-gray-200 rounded-lg p-4 relative"
                >
                  <h3 className="absolute top-2 left-4 text-sm font-semibold text-gray-600 hidden">Entry {index + 1}</h3>
                  {currentEntries.length > 1 && (
                    <button
                      onClick={() => removeEntry(entry.id, entry)}
                      className="absolute top-2 right-2 w-8 h-8 bg-red-500 text-white rounded-full flex items-center justify-center hover:bg-red-600 transition-colors"
                    >
                      <Minus className="h-4 w-4" />
                    </button>
                  )}
                  <div className="flex justify-between items-center p-4 border rounded-md">
                    <span className="text-sm font-semibold text-gray-700">Interest Amount</span>
                    <div className="relative">
                      <span className="absolute left-3 top-3 text-gray-500">₹</span>
                      <input
                        type="text"
                        placeholder="0.00"
                        value={entry.interestPaid}
                        onChange={(e) => {
                          // Only allow numbers (0-9) and decimal point
                          const value = e.target.value.replace(/[^0-9.]/g, '');
                          // Prevent multiple decimal points
                          const parts = value.split('.');
                          const filteredValue = parts.length > 2 ? parts[0] + '.' + parts.slice(1).join('') : value;
                          const numericValue = Math.max(0, Number.parseFloat(filteredValue || 0));                           updateEntry(entry.id, "interestPaid", numericValue);
                        }}
                        className="pl-8 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-cyan-500 w-48 text-right"
                      />
                    </div>
                  </div>
                </motion.div>
              ))
            )}
            {/* Add entry removed for 80TTA as per requirement */}
          </div>
        );

      case "80TTB":
        return (
          <div className="space-y-6">
            {!isSeniorCitizen ? (
              <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4">
                <div className="flex">
                  <div className="flex-shrink-0">
                    <svg className="h-5 w-5 text-yellow-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div className="ml-3">
                    <p className="text-sm text-yellow-700">
                      Section 80TTB is only available for senior citizens (age 60+).
                    </p>
                  </div>
                </div>
              </div>
            ) : (
              // 80TTB form for senior citizens
              currentEntries.map((entry, index) => (
                <motion.div
                  key={entry.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="grid grid-cols-1 md:grid-cols-3 gap-4 items-end border border-gray-200 rounded-lg p-4 relative"
                >
                  <h3 className="absolute top-2 left-4 text-sm font-semibold text-gray-600 hidden">Entry {index + 1}</h3>
                  {currentEntries.length > 1 && (
                    <button
                      onClick={() => removeEntry(entry.id, entry)}
                      className="absolute top-2 right-2 w-8 h-8 bg-red-500 text-white rounded-full flex items-center justify-center hover:bg-red-600 transition-colors"
                    >
                      <Minus className="h-4 w-4" />
                    </button>
                  )}
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Bank Name</label>
                    <input
                      type="text"
                      placeholder="Enter bank name"
                      value={entry.bankName || ""}
                      onChange={(e) => updateEntry(entry.id, "bankName", e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-cyan-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Account Number</label>
                    <input
                      type="text"
                      placeholder="Enter account number"
                      value={entry.accountNumber || ""}
                      onChange={(e) => updateEntry(entry.id, "accountNumber", e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-cyan-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Interest Amount</label>
                    <div className="relative">
                      <span className="absolute left-3 top-3 text-gray-500">₹</span>
                      <input
                        type="text"
                        placeholder="0.00"
                        value={entry.interestPaid}
                        onChange={(e) => {
                          // Only allow numbers (0-9) and decimal point
                          const value = e.target.value.replace(/[^0-9.]/g, '');
                          // Prevent multiple decimal points
                          const parts = value.split('.');
                          const filteredValue = parts.length > 2 ? parts[0] + '.' + parts.slice(1).join('') : value;
                          const numericValue = Math.max(0, Number.parseFloat(filteredValue || 0));                           updateEntry(entry.id, "interestPaid", numericValue);
                        }}
                        className="w-full pl-8 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-cyan-500"
                      />
                    </div>
                  </div>
                </motion.div>
              ))
            )}
            {isSeniorCitizen && (
              <button
                onClick={addEntry}
                className="w-full py-3 border-2 border-dashed border-gray-300 rounded-lg text-gray-600 hover:border-cyan-500 hover:text-cyan-600 transition-colors flex items-center justify-center space-x-2"
              >
                <Plus className="h-5 w-5" />
                <span>Add Another 80TTB Entry</span>
              </button>
            )}
          </div>
        );

      case "80QQB":
      case "80RRB":
      case "80CCH":
        return (
          <div className="space-y-6">
            {currentEntries.map((entry, index) => (
              <motion.div
                key={entry.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="grid grid-cols-1 md:grid-cols-3 gap-4 items-end border border-gray-200 rounded-lg p-4 relative"
              >
                <h3 className="absolute top-2 left-4 text-sm font-semibold text-gray-600 hidden">Entry {index + 1}</h3>
                {currentEntries.length > 1 && (
                  <button
                    onClick={() => removeEntry(entry.id, entry)}
                    className="absolute top-2 right-2 w-8 h-8 bg-red-500 text-white rounded-full flex items-center justify-center hover:bg-red-600 transition-colors"
                  >
                    <Minus className="h-4 w-4" />
                  </button>
                )}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Amount</label>
                  <div className="relative">
                    <span className="absolute left-3 top-3 text-gray-500">₹</span>
                    <input
                      type="number"
                      placeholder="0.00"
                      value={entry.interestPaid}
                      onChange={(e) => {
                        // Only allow numbers (0-9) and decimal point
                        const value = e.target.value.replace(/[^0-9.]/g, '');
                        // Prevent multiple decimal points
                        const parts = value.split('.');
                        const filteredValue = parts.length > 2 ? parts[0] + '.' + parts.slice(1).join('') : value;
                        const numericValue = Math.max(0, Number.parseFloat(filteredValue || 0));                         updateEntry(entry.id, "interestPaid", numericValue);
                      }}
                      className="w-full pl-8 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-cyan-500"
                    />
                  </div>
                </div>
              </motion.div>
            ))}
            <button
              onClick={addEntry}
              className="w-full py-3 border-2 border-dashed border-gray-300 rounded-lg text-gray-600 hover:border-cyan-500 hover:text-cyan-600 transition-colors flex items-center justify-center space-x-2"
            >
              <Plus className="h-5 w-5" />
              <span>Add Another Entry</span>
            </button>
            
            {/* 80D Validation Error Message */}
            {activeTab === "80D" && !eightyDValidationStatus.isValid && (
              <div className="mt-6 p-4 bg-red-100 border border-red-300 rounded-md">
                <div className="flex items-start">
                  <div className="flex-shrink-0">
                    <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div className="ml-3">
                    <h3 className="text-sm font-medium text-red-800">
                      Please fix the following validation errors:
                    </h3>
                    <div className="mt-2 text-sm text-red-700">
                      {eightyDValidationStatus.errorMessage.split(';').map((error, index) => (
                        <div key={index} className="mb-1">• {error.trim()}</div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            )}
            
            <div className="flex space-x-2">
              <button
                onClick={addEntry}
                className="flex-1 py-3 bg-cyan-500 text-white rounded-lg hover:bg-cyan-600 transition-colors flex items-center justify-center space-x-2"
              >
                <Plus className="h-5 w-5" />
                <span>Add</span>
              </button>
            </div>
          </div>
        )
      case "80DDB":
        return (
          <div className="space-y-6">
            {currentEntries.map((entry, index) => {
              const validationStatus = get80DDBValidationStatus(entry);
              const currentLimit = get80DDBAmountLimit(entry.userType);
              
              return (
                <motion.div
                  key={entry.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className={`grid grid-cols-1 md:grid-cols-3 gap-4 items-end border rounded-lg p-4 relative ${
                    activeTab === "80DDB" && !validationStatus.isValid 
                      ? 'border-red-300 bg-red-50' 
                      : 'border-gray-200'
                  }`}
                >
                <h3 className="absolute top-2 left-4 text-sm font-semibold text-gray-600 hidden">Entry {index + 1}</h3>
                {currentEntries.length > 1 && (
                  <button
                    onClick={() => removeEntry(entry.id, entry)}
                    className="absolute top-2 right-2 w-8 h-8 bg-red-500 text-white rounded-full flex items-center justify-center hover:bg-red-600 transition-colors"
                  >
                    <Minus className="h-4 w-4" />
                  </button>
                )}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Type Of User</label>
                  <div className="relative">
                    <select
                      className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 appearance-none bg-white ${
                        activeTab === "80DDB" && !validationStatus.isValid 
                          ? 'border-red-500 focus:ring-red-500' 
                          : 'border-gray-300 focus:ring-cyan-500'
                      }`}
                      value={entry.userType || ""}
                      onChange={(e) => updateEntry(entry.id, "userType", e.target.value)}
                    >
                      <option value="">Select User</option>
                      <option value="1">Self or Dependent</option>
                      <option value="2">Self or Dependent - Senior Citizen</option>
                    </select>
                    <ChevronDown className="absolute right-3 top-3 h-4 w-4 text-gray-400" />
                  </div>
                </div>
                <div className="flex-1">
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Name Of Specified Disease</label>
                  <div className="relative">
                    <select
                      className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 appearance-none bg-white ${
                        activeTab === "80DDB" && !validationStatus.isValid 
                          ? 'border-red-500 focus:ring-red-500' 
                          : 'border-gray-300 focus:ring-cyan-500'
                      }`}
                      value={entry.nameOfSpecifiedDisease || ""}
                      onChange={(e) => updateEntry(entry.id, "nameOfSpecifiedDisease", e.target.value)}
                    >
                      <option value="">Select Disease</option>
                      {
                        specifiedDiseasesList?.map((disease) => (
                          <option key={disease.codeValue} value={disease.codeValue}>
                            {disease.description}
                          </option>
                        ))
                      }


                    </select>
                    <ChevronDown className="absolute right-3 top-3 h-4 w-4 text-gray-400" />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Amount</label>
                  <div className="relative">
                    <span className="absolute left-3 top-3 text-gray-500">₹</span>
                    <input
                      type="number"
                      placeholder="0.00"
                      value={entry.expenditureIncurred}
                      onChange={(e) => {
                        // Only allow numbers (0-9) and decimal point
                        const value = e.target.value.replace(/[^0-9.]/g, '');
                        // Prevent multiple decimal points
                        const parts = value.split('.');
                        const filteredValue = parts.length > 2 ? parts[0] + '.' + parts.slice(1).join('') : value;
                        const numericValue = Math.max(0, Number.parseFloat(filteredValue || 0));                         updateEntry(entry.id, "expenditureIncurred", numericValue);
                      }}
                      className={`w-full pl-8 pr-3 py-2 border rounded-md focus:outline-none focus:ring-2 ${
                        activeTab === "80DDB" && !validationStatus.isValid 
                          ? 'border-red-500 focus:ring-red-500' 
                          : 'border-gray-300 focus:ring-cyan-500'
                      }`}
                    />
                  </div>
                </div>
                
                {/* Amount limit information for 80DDB */}
                {activeTab === "80DDB" && entry.userType && (
                  <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-md col-span-3">
                    <div className="flex items-start">
                      <div className="flex-shrink-0">
                        <svg className="h-5 w-5 text-blue-400" viewBox="0 0 20 20" fill="currentColor">
                          <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                        </svg>
                      </div>
                      <div className="ml-3">
                        <p className="text-sm text-blue-700">
                          Maximum deduction limit: ₹{currentLimit.toLocaleString()} 
                          ({entry.userType === "2" ? 'Senior Citizen' : 'Self/Dependent'})
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                {activeTab === "80DDB" && !validationStatus.isValid && (
                  <div className="mt-4 p-3 bg-red-100 border border-red-300 rounded-md col-span-3">
                    <p className="text-sm text-red-700">{validationStatus.errorMessage}</p>
                  </div>
                )}
              </motion.div>
            );
            })}

          </div>
        )
      case "80U":
        return (
          <div className="space-y-6">
            {currentEntries.map((entry, index) => {
              const validationStatus = get80UValidationStatus(entry);
              const currentLimit = getDisabilityAmountLimit(entry.typeOfDisability);
              const isForm10IANeeded = isForm10IARequired(entry.typeOfDisability, typeOfDisabilityList);
              
              return (
                <motion.div
                  key={entry.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className={`border rounded-lg p-4 space-y-4 relative ${
                    activeTab === "80U" && !validationStatus.isValid 
                      ? 'border-red-300 bg-red-50' 
                      : 'border-gray-200'
                  }`}
                >
                <h3 className="absolute top-2 left-4 text-sm font-semibold text-gray-600 hidden">Entry {index + 1}</h3>
                {currentEntries.length > 1 && (
                  <button
                    onClick={() => removeEntry(entry.id, entry)}
                    className="absolute top-2 right-2 w-8 h-8 bg-red-500 text-white rounded-full flex items-center justify-center hover:bg-red-600 transition-colors"
                  >
                    <Minus className="h-4 w-4" />
                  </button>
                )}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Nature of Disability</label>
                    <div className="relative">
                      <select
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-cyan-500 appearance-none bg-white"
                        value={entry.natureOfDisability || ""}
                        onChange={(e) => updateEntry(entry.id, "natureOfDisability", e.target.value)}
                      >
                        <option value="">Select Disability</option>
                        {
                          disabilityNatureList?.map((option) => (
                            <option key={option.codeValue} value={option.codeValue}>
                              {option.description}
                            </option>
                          ))
                        }
                      </select>
                      <ChevronDown className="absolute right-3 top-3 h-4 w-4 text-gray-400" />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Type of Disability</label>
                    <div className="relative">
                      <select
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-cyan-500 appearance-none bg-white"
                        value={entry.typeOfDisability || ""}
                        onChange={(e) => updateEntry(entry.id, "typeOfDisability", e.target.value)}
                      >
                        <option value="">Select disability type</option>
                        {
                          typeOfDisabilityList?.map((option) => (
                            <option key={option.codeValue} value={option.codeValue}>
                              {option.description}
                            </option>
                          ))
                        }
                      </select>
                      <ChevronDown className="absolute right-3 top-3 h-4 w-4 text-gray-400" />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Amount</label>
                    <div className="relative">
                      <span className="absolute left-3 top-3 text-gray-500">₹</span>
                      <input
                        type="text"
                        placeholder="0.00"
                        value={entry.deductionAmount}
                        onChange={(e) => {
                          // Only allow numbers (0-9) and decimal point
                          const value = e.target.value.replace(/[^0-9.]/g, '');
                          // Prevent multiple decimal points
                          const parts = value.split('.');
                          const filteredValue = parts.length > 2 ? parts[0] + '.' + parts.slice(1).join('') : value;
                          const numericValue = Math.max(0, Number.parseFloat(filteredValue || 0));                           updateEntry(entry.id, "deductionAmount", numericValue);
                        }}
                        className="w-full pl-8 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-cyan-500"
                      />
                    </div>
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Ack. No. of Form 10IA filed
                    </label>
                    <input
                      type="text"
                      placeholder="Enter Details"
                      value={entry.form10AAckNum || ""}
                      onChange={(e) =>
                        updateEntry(entry.id, "form10AAckNum", e.target.value)
                      }
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-cyan-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">UDID Number (If available)</label>
                    <input
                      type="text"
                      placeholder="Enter Details"
                      value={entry.udidNo || ""}
                      onChange={(e) =>
                        updateEntry(entry.id, "udidNo", e.target.value)
                      }
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-cyan-500"
                    />
                  </div>
                </div>
                
                {/* Amount limit information for 80U */}
                {activeTab === "80U" && entry.typeOfDisability && (
                  <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-md">
                    <div className="flex items-start">
                      <div className="flex-shrink-0">
                        <svg className="h-5 w-5 text-blue-400" viewBox="0 0 20 20" fill="currentColor">
                          <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                        </svg>
                      </div>
                      <div className="ml-3">
                        <p className="text-sm text-blue-700">
                          Maximum deduction limit: ₹{currentLimit.toLocaleString()} 
                          ({isSevereDisability(entry.typeOfDisability) ? 'Severe' : 'Regular'} disability)
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Form 10IA requirement warning for 80U */}
                {activeTab === "80U" && isForm10IANeeded && !entry.form10AAckNum && (
                  <div className="mt-4 p-3 bg-yellow-50 border border-yellow-300 rounded-md">
                    <div className="flex items-start">
                      <div className="flex-shrink-0">
                        <svg className="h-5 w-5 text-yellow-400" viewBox="0 0 20 20" fill="currentColor">
                          <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                        </svg>
                      </div>
                      <div className="ml-3">
                        <p className="text-sm text-yellow-700">
                          Form 10IA acknowledgement number is required for Autism/Cerebral Palsy/Multiple Disability cases.
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                {activeTab === "80U" && !validationStatus.isValid && (
                  <div className="mt-4 p-3 bg-red-100 border border-red-300 rounded-md">
                    <p className="text-sm text-red-700">{validationStatus.errorMessage}</p>
                  </div>
                )}
              </motion.div>
            );
            })}

          </div>
        )
      case "80C":
      default:
        return (
          <div className="space-y-6">
            {currentEntries.map((entry, index) => {
              const validationStatus = get80CValidationStatus(entry);
              
              return (
                <motion.div
                  key={entry.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className={`grid grid-cols-1 md:grid-cols-3 gap-4 items-end border rounded-lg p-4 relative ${
                    activeTab === "80C" && !validationStatus.isValid 
                      ? 'border-red-300 bg-red-50' 
                      : 'border-gray-200'
                  }`}
                >
                <h3 className="absolute top-2 left-4 text-sm font-semibold text-gray-600 hidden">Entry {index + 1}</h3>{" "}
                {/* Added entry label */}
                {currentEntries.length > 1 && ( // Show remove button if more than one entry
                  <button
                    onClick={() => removeEntry(entry.id, entry)}
                    className="absolute top-2 right-2 w-8 h-8 bg-red-500 text-white rounded-full flex items-center justify-center hover:bg-red-600 transition-colors"
                  >
                    <Minus className="h-4 w-4" />
                  </button>
                )}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Nature</label>
                  <div className="relative">
                    <select
                      className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 appearance-none bg-white ${
                        activeTab === "80C" && !validationStatus.isValid 
                          ? 'border-red-500 focus:ring-red-500' 
                          : 'border-gray-300 focus:ring-cyan-500'
                      }`}
                      value={entry.nature || ""}
                      onChange={(e) => updateEntry(entry.id, "nature", e.target.value)}
                    >
                      <option value="">Select Nature</option>
                      <option value="Life Insurance Premium">Life Insurance Premium</option>
                      <option value="PPF">PPF</option>
                      <option value="ELSS">ELSS</option>
                      <option value="NSC">NSC</option>
                      <option value="Tax Saver FD">Tax Saver FD</option>
                      <option value="ULIP">ULIP</option>
                      <option value="Home Loan Principal">Home Loan Principal</option>
                      <option value="Tuition Fee">Tuition Fee</option>
                      <option value="Others">Others</option>
                    </select>
                    <ChevronDown className="absolute right-3 top-3 h-4 w-4 text-gray-400" />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Amount</label>
                  <div className="relative">
                    <span className="absolute left-3 top-3 text-gray-500">₹</span>
                    <input
                      type="text"
                      placeholder="0.00"
                      value={entry.amount || ""}
                      onChange={(e) => {
                        // Only allow numbers (0-9) and decimal point
                        const value = e.target.value.replace(/[^0-9.]/g, '');
                        // Prevent multiple decimal points
                        const parts = value.split('.');
                        const filteredValue = parts.length > 2 ? parts[0] + '.' + parts.slice(1).join('') : value;
                        const numericValue = Math.max(0, Number.parseFloat(filteredValue || 0));                         updateEntry(entry.id, "amount", numericValue);
                      }}
                      className={`w-full pl-8 pr-3 py-2 text-right border rounded-md focus:outline-none focus:ring-2 ${
                        activeTab === "80C" && !validationStatus.isValid 
                          ? 'border-red-500 focus:ring-red-500' 
                          : 'border-gray-300 focus:ring-cyan-500'
                      }`}
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Identification No.</label>
                  <input
                    type="text"
                    placeholder="Enter details"
                    value={entry.identificationNo || ""}
                    onChange={(e) => updateEntry(entry.id, "identificationNo", e.target.value)}
                    className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 ${
                      activeTab === "80C" && !validationStatus.isValid 
                        ? 'border-red-500 focus:ring-red-500' 
                        : 'border-gray-300 focus:ring-cyan-500'
                    }`}
                  />
                </div>
                
                {activeTab === "80C" && !validationStatus.isValid && (
                  <div className="mt-4 p-3 bg-red-100 border border-red-300 rounded-md col-span-3">
                    <p className="text-sm text-red-700">{validationStatus.errorMessage}</p>
                  </div>
                )}
              </motion.div>
            );
            })}
            <div className="flex space-x-2">
              <button
                onClick={addEntry}
                className="flex-1 py-3 bg-cyan-500 text-white rounded-lg hover:bg-cyan-600 transition-colors flex items-center justify-center space-x-2"
              >
                <Plus className="h-5 w-5" />
                <span>Add</span>
              </button>
            </div>
          </div>
        )
    }
  }

  const currentType = deductionTypes.find((type) => type.id === activeTab)
  const total = calculateTotal()
  const eligibleDeduction = getEligibleDeduction()


  // Compute capped eligible value using fetched cap stored on deductionData (avoid extra hooks order)
  const cappedEligibleDeduction = deductionData?.totalTaxableIncomeCap > 0
    ? Math.min(eligibleDeduction, deductionData.totalTaxableIncomeCap)
    : eligibleDeduction

  return (
    <div className="w-full">
      <ToastContainer />
      <div className="w-full">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-lg shadow-lg overflow-hidden"
        >
          {/* Header */}
          <div className="bg-cyan-500 text-white p-4 flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="bg-white bg-opacity-20 p-2 rounded">
                <FileText className="h-6 w-6" />
              </div>
              <div>
                <h1 className="text-xl font-semibold">{activeTab} Details</h1>
                <p className="text-white text-md">{activeTabDescription ? activeTabDescription : "Update Information"}</p>
              </div>
            </div>
            {/* Real-time Total Eligible Deductions */}
            <div className="flex items-center space-x-4">
              <div className="text-right">
                <div className="text-sm text-white/80">Total Eligible Deductions</div>
                <div className="text-lg font-bold text-white">₹{formatCurrency(totalEligibleDeduction)}</div>
              </div>
              {/* Calculate Deduction Button */}
              <button
                onClick={performAutoCalculation}
                disabled={isCalculating || calculationCompleted}
                className={`px-6 py-2 rounded-lg transition-colors flex items-center justify-center space-x-2 shadow-md font-medium ${
                  isCalculating 
                    ? 'bg-blue-100 text-blue-600 cursor-not-allowed' 
                    : calculationCompleted
                    ? 'bg-green-100 text-green-600 cursor-not-allowed'
                    : 'bg-blue-300 text-white hover:bg-blue-400'
                }`}
              >
                {isCalculating ? (
                  <motion.div
                    animate={{ rotate: 360 }}
                    transition={{ duration: 1, repeat: Number.POSITIVE_INFINITY, ease: "linear" }}
                    className="w-4 h-4 border-2 border-blue-500 border-t-transparent rounded-full"
                  />
                ) : calculationCompleted ? (
                  <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                ) : (
                  <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                  </svg>
                )}
                <span>
                  {isCalculating 
                    ? `Calculating... (${currentCalculationTab + 1}/16)` 
                    : calculationCompleted 
                    ? 'Calculated ✓' 
                    : 'Calculate Deduction'
                  }
                </span>
              </button>

              {/* Save Button */}
              <button
                onClick={handleSubmit}
                disabled={isSubmitting || !calculationCompleted}
                className={`px-6 py-2 rounded-lg transition-colors flex items-center justify-center space-x-2 shadow-md font-medium ${
                  !calculationCompleted
                    ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                    : isSubmitting
                    ? 'bg-cyan-200 text-cyan-600 cursor-not-allowed'
                    : 'bg-white text-cyan-500 hover:bg-gray-100'
                }`}
              >
                {isSubmitting ? (
                  <motion.div
                    animate={{ rotate: 360 }}
                    transition={{ duration: 1, repeat: Number.POSITIVE_INFINITY, ease: "linear" }}
                    className="w-4 h-4 border-2 border-cyan-500 border-t-transparent rounded-full"
                  />
                ) : (
                  <FileText className="h-4 w-4" />
                )}
                <span>{isSubmitting ? "Saving..." : "Save All"}</span>
              </button>
            </div>
            
            {/* Validation Error Messages */}
            {hasAnyValidationErrors() && (
              <div className="mt-2 text-center">
                <p className="text-xs text-red-600 font-medium">
                  Cannot calculate: Please fix validation errors in deduction forms above
                </p>
              </div>
            )}
            {hasAnyValidationErrors() && !has80GValidationErrors() && (
              <div className="mt-2 text-center">
                <p className="text-xs text-red-600 font-medium">
                  Cannot save: Please fix validation errors in deduction forms above
                </p>
              </div>
            )}
          </div>
          
          {/* Calculation Progress Indicator */}
          {isCalculating && (
            <div className="bg-blue-50 border-l-4 border-blue-300 p-4 mb-4">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <motion.div
                    animate={{ rotate: 360 }}
                    transition={{ duration: 1, repeat: Number.POSITIVE_INFINITY, ease: "linear" }}
                    className="w-5 h-5 border-2 border-blue-500 border-t-transparent rounded-full"
                  />
                </div>
                <div className="ml-3">
                  <p className="text-sm text-blue-600">
                    <strong>Auto-calculating deductions...</strong> Processing tab {currentCalculationTab + 1} of 16
                  </p>
                  <div className="mt-2 w-full bg-blue-100 rounded-full h-2">
                    <div 
                      className="bg-blue-400 h-2 rounded-full transition-all duration-300"
                      style={{ width: `${((currentCalculationTab + 1) / 16) * 100}%` }}
                    ></div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Category Tabs */}
          <div className="border-b border-gray-200">
            <div className="flex overflow-x-auto">
              {categories.map((category) => (
                <button
                  key={category}
                  onClick={() => switchCategory(category)}
                  className={`px-4 sm:px-6 py-3 text-sm font-medium whitespace-nowrap border-b-2 transition-colors ${activeCategory === category
                    ? "border-cyan-500 text-white mr-1 mt-1 mb-1 bg-cyan-700"
                    : "border-transparent text-gray-800 mr-1 mt-1 mb-1 hover:text-gray-700 hover:border-gray-300 bg-gray-300 "
                    }`}
                >
                  {category}
                </button>
              ))}
            </div>
          </div>
          {/* Deduction Type Tabs */}
          <div className="border-b border-gray-200">
            <div className="flex overflow-x-auto scrollbar-hide">
              {deductionTypes
                .filter((type) => type.category === activeCategory)
                .map((type) => (
                  <button
                    key={type.id}
                    onClick={() => switchTab(type.id, type.description)}
                    className={`px-3 sm:px-4 py-2 text-sm font-medium whitespace-nowrap transition-colors ${activeTab === type.id
                      ? "bg-cyan-500 text-white mr-1 mb-1"
                      : "mr-1 mb-1 bg-gray-200 text-gray-700 hover:bg-gray-200"
                      }`}
                  >
                    {type.name}
                  </button>
                ))}
            </div>
          </div>
          {/* Form Content */}
          <div className="p-4 sm:p-6">
            <motion.div
              key={activeTab}
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.3 }}
            >
              {renderForm()}
            </motion.div>
            {/* Total and Eligible Amount - Show for all tabs */}
            <div className="mt-8 space-y-4">
              <div className="flex justify-between items-center p-4 border rounded-md">
                <span className="font-bold">Total Amount</span>
                <span>₹{formatCurrency(total)}</span>
              </div>

              <div className="flex justify-between items-center p-4 bg-cyan-50 border border-cyan-200 rounded-md">
                <span className="font-bold">Eligible Deduction</span>
                <span>₹{formatCurrency(eligibleDeduction)}</span>
              </div>
            </div>

          </div>
        </motion.div>
      </div>

      <ValidationErrorSidebar
        validationErrors={validationErrors}
        isVisible={true}
        title="Deduction Validation Errors"
        onFieldClick={(fieldKey) => {
          // Handle field click - scroll to the field or show focus
          console.log('Field clicked:', fieldKey);
          // You can implement field navigation logic here
        }}
      />
      
    </div>
  )
}

export default TaxDeductionForms  